import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Shield,
  Users,
  FileText,
  Clock,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Download,
  Eye,
  BarChart3,
  Loader,
  Settings,
  Upload,
  Bell,
  LogOut,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Trash2,
  Edit,
  Edit2,
  Save,
  X,
  Check,
  Award,
  Database,
  Type,
  Globe,
  Package,
  Copy,
  Archive,
  ArchiveRestore,
  RotateCcw,
  GitBranch,
  Building2,
  ArrowLeft,
  DollarSign,
  Brain,
  Info,
  Wrench,
  CreditCard,
  UserCircle,
  Key,
  Zap,
  Menu,
  Plug,
  Sparkles,
  BookOpen,
  Activity,
  Cookie,
  AlertCircle,
  Lock,
  Bot,
  TrendingUp
} from 'lucide-react'
import AdminLogin from '../components/AdminLogin'
import type { AccessRequest } from '../lib/storage'
import PageContentEditor from '../components/PageContentEditor'
import AllowlistManager from '../components/AllowlistManager'
import { featureFlags } from '../lib/featureFlags'
import ProductManager from '../components/ProductManager'
import ContentVersioning from '../components/ContentVersioning'
import SectionHeading from '../components/SectionHeading'
import TenantManager from '../components/TenantManager'
import BillingSettings from '../components/BillingSettings'
import SlackNotificationSettings from '../components/SlackNotificationSettings'
import PasskeySettings from '../components/PasskeySettings'
import NdaSettingsManager from '../components/NdaSettingsManager'
import NdaAcceptanceManager from '../components/NdaAcceptanceManager'
import CustomDomainSettings from '../components/CustomDomainSettings'
import NotificationComposer from '../components/NotificationComposer'
import EngagementDashboard from './EngagementDashboard'
import AuditTrailPage from './AuditTrailPage'
import RevenueAttributionPage from './RevenueAttributionPage'
import PlatformAnalytics from '../components/PlatformAnalytics'
import KnowledgeBasePage from './KnowledgeBasePage'
import VendorRiskAssessment from '../components/VendorRiskAssessment'
import ContractsManagement from './ContractsManagement'
import AdminPrivacyPortal from '../components/AdminPrivacyPortal'
import PrivacyRequestsManager from '../components/PrivacyRequestsManager'
import ChangelogManager from '../components/ChangelogManager'
import PolicyReviewSettings from '../components/PolicyReviewSettings'
import { ApiKeysSettings } from '../components/ApiKeysSettings'
import { useTenant } from '../contexts/TenantContext'
import { useContentRefresh } from '../contexts/ContentRefreshContext'
import ProductTour from '../components/ProductTour'
import { useProductTour } from '../hooks/useProductTour'
import TourStarter from '../components/TourStarter'
import AIContentGenerator from '../components/AIContentGenerator'
import AIAgentModal from '../components/AIAgentModal'
import AgentRegistrationForm from '../components/AgentRegistrationForm'
import SkillsRankTab from '../components/SkillsRankTab'
import AssociatesTab from '../components/AssociatesTab'

interface AdminUser {
  email: string
  role: 'admin' | 'security' | 'legal'
  name: string
}

const AdminFullFunctional = () => {
  // Get tenantId from URL params (when accessed from platform admin)
  const { tenantId: urlTenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()

  // Check if we're in platform admin mode (accessed via /platformadmin/tenant/:id)
  const isPlatformAdminMode = window.location.pathname.startsWith('/platformadmin')

  // Tenant context (fallback when not using URL param)
  const { currentTenant } = useTenant()

  // Content refresh context for real-time updates
  const { lastContentUpdate, triggerContentRefresh } = useContentRefresh()

  // Determine which tenant we're managing
  const [managedTenant, setManagedTenant] = useState<any>(null)
  const effectiveTenantId = urlTenantId || currentTenant?.id

  // Load tenant info when URL param changes
  useEffect(() => {
    if (urlTenantId) {
      fetch(`/api/admin/tenants?id=${urlTenantId}`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(tenant => setManagedTenant(tenant))
        .catch(err => console.error('Failed to load tenant:', err))
    }
  }, [urlTenantId])

  // Use managedTenant when accessed from platform admin, otherwise use currentTenant
  const displayTenant = managedTenant || currentTenant

  // Helper function to add tenant override header when in platform admin mode
  const getHeaders = (additionalHeaders: Record<string, string> = {}): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...additionalHeaders
    }

    // Add Authorization header if token exists in localStorage
    const token = localStorage.getItem('auth-token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Add tenant override header when managing a specific tenant from platform admin
    if (urlTenantId) {
      headers['X-Tenant-Override'] = urlTenantId
    }

    return headers
  }

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null)
  const [loginLoading, setLoginLoading] = useState(true)

  // Data state
  const [requests, setRequests] = useState<AccessRequest[]>([])
  const [documents, setDocuments] = useState<any[]>([])
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set())
  const [showBulkEditModal, setShowBulkEditModal] = useState(false)
  const [rescanningAll, setRescanningAll] = useState(false)
  const [rescanningDocId, setRescanningDocId] = useState<string | null>(null)
  const [rescanResults, setRescanResults] = useState<any | null>(null)
  const [rescanBatchInfo, setRescanBatchInfo] = useState<any | null>(null)
  const [showRescanResults, setShowRescanResults] = useState(false)
  const [scanProgress, setScanProgress] = useState<{
    total: number
    processed: number
    remaining: number
    percentage: number
    lastProcessed?: any
  } | null>(null)
  const [bulkEditForm, setBulkEditForm] = useState({
    ndaRequired: false,
    watermarked: false,
    visibility: 'gated' as 'public' | 'gated' | 'admin',
    category: '',
    type: '',
    product: ''
  })
  const [badges, setBadges] = useState<any[]>([])
  const [analytics, setAnalytics] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [renderKey, setRenderKey] = useState(0) // Force re-render counter
  const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [bulkFiles, setBulkFiles] = useState<File[]>([])
  const [bulkUploading, setBulkUploading] = useState(false)
  const [bulkOptions, setBulkOptions] = useState({
    visibility: 'gated' as 'public' | 'gated' | 'admin',
    ndaRequired: true,
    watermarked: true,
    redacted: false,
    allowedPages: '5' // Can be "5" or "1,3,5" or "1-5,10"
  })

  // OAuth2 client state
  const [oauthClients, setOauthClients] = useState<any[]>([])
  const [showCreateOAuthClient, setShowCreateOAuthClient] = useState(false)
  const [newOAuthClient, setNewOAuthClient] = useState({
    name: '',
    redirect_uris: [''],
    scopes: ['read', 'write']
  })
  const [createdClientCredentials, setCreatedClientCredentials] = useState<any>(null)

  // Site scraping state
  const [scrapeUrl, setScrapeUrl] = useState('')
  const [scraping, setScraping] = useState(false)
  const [scrapeResult, setScrapeResult] = useState<any>(null)
  const [scrapeError, setScrapeError] = useState<string | null>(null)

  // Allowlist state
  const [allowedDomains, setAllowedDomains] = useState<any[]>([])
  const [allowedEmails, setAllowedEmails] = useState<any[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [bulkDomainsText, setBulkDomainsText] = useState('')
  const [bulkEmailsText, setBulkEmailsText] = useState('')
  const [allowlistLoading, setAllowlistLoading] = useState(false)

  // User management state
  const [userRoles, setUserRoles] = useState<any[]>([])
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserRole, setNewUserRole] = useState<'admin' | 'editor' | 'reviewer' | 'viewer'>('editor')

  // UI state
  const [activeTab, setActiveTab] = useState<'dashboard' | 'trust-center' | 'analytics' | 'settings' | 'knowledge-base' | 'tprm' | 'changelog' | 'platform' | 'agents' | 'skills' | 'associates'>('dashboard')
  const [trustCenterSubTab, setTrustCenterSubTab] = useState<'visitors' | 'deployments' | 'documents' | 'features' | 'settings'>('visitors')
  const [changelogSubTab, setChangelogSubTab] = useState<'entries' | 'settings'>('entries')
  const [contentSubTab, setContentSubTab] = useState<'manage'>('manage')
  const [privacySubTab, setPrivacySubTab] = useState<'requests' | 'portal' | 'cookies' | 'settings' | 'retention'>('requests')
  const [settingsSubTab, setSettingsSubTab] = useState<'general' | 'billing' | 'team' | 'integrations' | 'security'>('general')
  const [analyticsSubTab, setAnalyticsSubTab] = useState<'overview' | 'engagement' | 'audit' | 'revenue' | 'feature-usage'>('overview')
  const [knowledgeBaseTab, setKnowledgeBaseTab] = useState<'query' | 'questionnaire' | 'caiq' | 'tprm' | 'vendor-risk' | undefined>(undefined)
  const [caiqSubTab, setCAIQSubTab] = useState<'configure' | 'scan' | 'review' | 'browse' | 'analytics' | undefined>(undefined)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [showNotificationsModal, setShowNotificationsModal] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [notifications, setNotifications] = useState<Array<any>>([])
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [tenantList, setTenantList] = useState<Array<{ id: string; name: string }>>([])
  const [selectedCrm, setSelectedCrm] = useState<'salesforce' | 'hubspot' | 'pipedrive' | 'close' | ''>('')
  const [crmApiKey, setCrmApiKey] = useState('')
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: 'certifications',
    documentType: '' as string,
    gated: true,
    ndaRequired: true,
    watermarked: true,
    redacted: false,
    allowedPages: '5',
    visibility: 'gated' as 'public' | 'gated' | 'admin',
    hidden: false,
    productIds: [] as string[],
    file: null as File | null
  })
  const [editingDoc, setEditingDoc] = useState<any | null>(null)
  const [editForm, setEditForm] = useState({
    title: '',
    category: 'certifications',
    documentType: undefined as 'policy' | 'procedure' | 'attestation' | 'report' | 'certificate' | 'agreement' | 'other' | undefined,
    gated: true,
    ndaRequired: true,
    watermarked: true,
    redacted: false,
    allowedPages: '5',
    visibility: 'gated' as 'public' | 'gated' | 'admin',
    hidden: false,
    productIds: [] as string[]
  })
  const [editingMarkdown, setEditingMarkdown] = useState<{ doc: any; markdown: string } | null>(null)
  const [markdownContent, setMarkdownContent] = useState('')
  const [savingMarkdown, setSavingMarkdown] = useState(false)
  const [badgeForm, setBadgeForm] = useState({
    name: '',
    icon: 'Shield',
    status: '',
    year: new Date().getFullYear().toString(),
    description: '',
    reportAvailable: false,
    reportUrl: '',
    isActive: true,
    productIds: [] as string[]
  })
  const [editingBadge, setEditingBadge] = useState<any | null>(null)

  // Content management state
  const [contentData, setContentData] = useState<any>({
    subprocessors: [],
    features: [],
    hero: null,
    heroTerms: null,
    heroPrivacy: null,
    heroDmcaSubmit: null,
    heroDmcaCounter: null,
    settings: null,
    contracts: [],
    legalPages: {}
  })
  const [documentTab, setDocumentTab] = useState<'active' | 'archived' | 'recyclebin'>('active')
  const [subprocessorTab, setSubprocessorTab] = useState<'active' | 'archived' | 'recyclebin'>('active')
  const [subprocessorProductFilter, setSubprocessorProductFilter] = useState<string>('all')
  const [badgeTab, setBadgeTab] = useState<'active' | 'archived' | 'recyclebin'>('active')
  const [badgeProductFilter, setBadgeProductFilter] = useState<string>('all')
  const [featureTab, setFeatureTab] = useState<'active' | 'archived' | 'recyclebin'>('active')
  const [featureProductFilter, setFeatureProductFilter] = useState<string>('all')
  const [selectedHeroType, setSelectedHeroType] = useState<'hero' | 'hero-terms' | 'hero-privacy' | 'hero-dmca-submit' | 'hero-dmca-counter'>('hero')
  const [editingContent, setEditingContent] = useState<any | null>(null)
  const [contentForm, setContentForm] = useState<any>({})
  const [showContentForm, setShowContentForm] = useState(false)
  const [contentFormType, setContentFormType] = useState<'subprocessors' | 'features' | 'badges' | 'pages' | 'settings' | null>(null)

  // Document filter and sort state
  const [docFilterCategory, setDocFilterCategory] = useState<string>('all')
  const [docFilterVisibility, setDocFilterVisibility] = useState<string>('all')
  const [docFilterType, setDocFilterType] = useState<string>('all')
  const [docFilterAttestation, setDocFilterAttestation] = useState<string>('all')
  const [docFilterProduct, setDocFilterProduct] = useState<string>('all')
  const [docSortBy, setDocSortBy] = useState<'name' | 'date' | 'size' | 'category' | 'type'>('date')
  const [docSortOrder, setDocSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedAdminDocs, setSelectedAdminDocs] = useState<Set<string>>(new Set())
  const [adminBulkDownloading, setAdminBulkDownloading] = useState(false)

  // Subprocessor reviews state
  const [reviews, setReviews] = useState<any[]>([])
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewForm, setReviewForm] = useState({
    reviewType: 'quarterly' as 'annual' | 'quarterly' | 'ad-hoc' | 'audit',
    notes: '',
    postToJira: false,
    postToLinear: false
  })
  const [selectedReview, setSelectedReview] = useState<any | null>(null)

  // Subprocessor bulk import state
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPreview, setCsvPreview] = useState<any[]>([])
  const [showUrlFetchModal, setShowUrlFetchModal] = useState(false)
  const [showAIGenerator, setShowAIGenerator] = useState(false)
  const [aiGeneratorType, setAIGeneratorType] = useState<'features' | 'subprocessors' | 'privacy-policy' | 'terms' | 'dmca-submit' | 'dmca-counter' | 'badges' | 'legal'>('features')
  const [showAIAgentModal, setShowAIAgentModal] = useState(false)
  const [urlFetchInput, setUrlFetchInput] = useState('')
  const [urlFetchProduct, setUrlFetchProduct] = useState<string>('')
  const [isFetchingUrl, setIsFetchingUrl] = useState(false)

  // Product tour state
  const [activeTourType, setActiveTourType] = useState<'admin' | 'publish' | null>(null)
  const [tourInitialStep, setTourInitialStep] = useState<number>(0)

  // Admin tour
  const {
    shouldShowTour: shouldShowAdminTour,
    completeTour: completeAdminTour,
    skipTour: skipAdminTour,
    resetTour: resetAdminTour,
    startTour: startAdminTour
  } = useProductTour('admin', true)

  // Publish tour
  const {
    shouldShowTour: shouldShowPublishTour,
    completeTour: completePublishTour,
    skipTour: skipPublishTour,
    startTour: startPublishTour,
    resetTour: resetPublishTour
  } = useProductTour('publish', false)

  // Determine which tour should be active
  useEffect(() => {
    if (shouldShowAdminTour && !activeTourType) {
      setActiveTourType('admin')
    }
  }, [shouldShowAdminTour, activeTourType])

  // Handle admin tour completion - transition to publish tour
  const handleAdminTourComplete = async () => {
    // Complete the admin tour
    await completeAdminTour()

    // Switch to trust-center tab
    setActiveTab('trust-center')
    setTrustCenterSubTab('content')
    window.location.hash = 'trust-center'

    // Small delay to let the tab switch complete, then start publish tour
    setTimeout(() => {
      setTourInitialStep(0) // Start publish tour from beginning
      setActiveTourType('publish')
      startPublishTour()
    }, 300)
  }

  // Handle publish tour completion
  const handlePublishTourComplete = async () => {
    await completePublishTour()
    setActiveTourType(null)
    setTourInitialStep(0)
  }

  // Handle tour skip
  const handleTourSkip = async () => {
    if (activeTourType === 'admin') {
      await skipAdminTour()
    } else if (activeTourType === 'publish') {
      await skipPublishTour()
    }
    setActiveTourType(null)
    setTourInitialStep(0)
  }

  // Handle contextual tour start from TourStarter button
  const handleStartContextualTour = (stepIndex: number) => {
    setTourInitialStep(stepIndex)
    // All contextual tours use the publish tour type (which covers all features)
    setActiveTourType('publish')
    startPublishTour()
  }

  // Handle tour step changes for automatic tab switching
  const handleTourStepChange = (stepIndex: number, tourType: string) => {
    if (tourType === 'publish') {
      // Main tab switching for comprehensive tour
      if (stepIndex >= 0 && stepIndex <= 9) {
        // Steps 0-9: Trust Center tab
        setActiveTab('trust-center')

        // Sub-tab switching within Trust Center
        if (stepIndex >= 4 && stepIndex <= 7) {
          setTrustCenterSubTab('deployments')
        } else if (stepIndex === 9) {
          setTrustCenterSubTab('privacy')
          setPrivacySubTab('portal')
        } else if (stepIndex === 2 || stepIndex === 3) {
          setTrustCenterSubTab('content')
        }
      } else if (stepIndex >= 10 && stepIndex <= 12) {
        // Steps 10-12: Requests tab (now under Trust Center)
        setActiveTab('trust-center')
        setTrustCenterSubTab('requests')
        window.location.hash = 'trust-center'
        // Sub-tab switching within Requests
        setRequestsSubTab('access')
      } else if (stepIndex >= 13 && stepIndex <= 20) {
        // Steps 13-20: Knowledge Base tab (with CAIQ subtabs)
        setActiveTab('knowledge-base')
        window.location.hash = 'knowledge-base'

        // Control Knowledge Base internal tabs
        if (stepIndex === 13) {
          // Step 13: Knowledge Base main tab
          setKnowledgeBaseTab('query')
          setCAIQSubTab(undefined)
        } else if (stepIndex === 14) {
          // Step 14: Query Knowledge Base
          setKnowledgeBaseTab('query')
          setCAIQSubTab(undefined)
        } else if (stepIndex === 15) {
          // Step 15: Questionnaire Upload
          setKnowledgeBaseTab('questionnaire')
          setCAIQSubTab(undefined)
        } else if (stepIndex === 16) {
          // Step 16: CAIQ Configuration
          setKnowledgeBaseTab('caiq')
          setCAIQSubTab('configure')
        } else if (stepIndex === 17) {
          // Step 17: CAIQ Scan & Extract
          setKnowledgeBaseTab('caiq')
          setCAIQSubTab('scan')
        } else if (stepIndex === 18) {
          // Step 18: Review Extracted Answers
          setKnowledgeBaseTab('caiq')
          setCAIQSubTab('review')
        } else if (stepIndex === 19) {
          // Step 19: Browse CAIQ Questions
          setKnowledgeBaseTab('caiq')
          setCAIQSubTab('browse')
        } else if (stepIndex === 20) {
          // Step 20: Knowledge Base Analytics
          setKnowledgeBaseTab('caiq')
          setCAIQSubTab('analytics')
        }
      } else if (stepIndex >= 21 && stepIndex <= 24) {
        // Steps 21-24: TPRM tab (Dashboard, Assessments, Settings)
        setActiveTab('tprm')

        // Control TPRM internal tabs via hash
        if (stepIndex === 21) {
          // Step 21: TPRM main tab
          window.location.hash = 'tprm'
        } else if (stepIndex === 22) {
          // Step 22: TPRM Dashboard
          window.location.hash = 'tprm-dashboard'
        } else if (stepIndex === 23) {
          // Step 23: Vendor Assessments
          window.location.hash = 'tprm-assessments'
        } else if (stepIndex === 24) {
          // Step 24: TPRM Configuration/Settings
          window.location.hash = 'tprm-settings'
        }
      } else if (stepIndex >= 25 && stepIndex <= 29) {
        // Steps 25-29: Analytics tab (Overview, Engagement, Audit, Revenue)
        setActiveTab('analytics')
        window.location.hash = 'analytics'
        // Sub-tab switching within Analytics
        if (stepIndex === 27) {
          setAnalyticsSubTab('engagement')
        } else if (stepIndex === 28) {
          setAnalyticsSubTab('audit')
        } else if (stepIndex === 29) {
          setAnalyticsSubTab('revenue')
        } else {
          setAnalyticsSubTab('overview')
        }
      } else if (stepIndex >= 30 && stepIndex <= 35) {
        // Steps 30-35: Settings tab (Products, Domain, Billing, Team, Integrations, Security)
        setActiveTab('settings')
        window.location.hash = 'settings'
        // Sub-tab switching within Settings
        if (stepIndex === 32) {
          setSettingsSubTab('billing')
        } else if (stepIndex === 33) {
          setSettingsSubTab('team')
        } else if (stepIndex === 34) {
          setSettingsSubTab('integrations')
        } else if (stepIndex === 35) {
          setSettingsSubTab('security')
        } else {
          setSettingsSubTab('general')
        }
      }
    }
  }

  // Redirect live tenant sites to public trust center
  useEffect(() => {
    // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
    if (typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__.version === 'live') {
      console.log('🔒 Live site detected - redirecting /admin to public trust center')
      navigate('/', { replace: true })
      return
    }
  }, [navigate])

  // Check authentication on mount
  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Clean up OAuth callback parameters from URL after authentication
  useEffect(() => {
    if (isAuthenticated) {
      const url = new URL(window.location.href)
      const hasOAuthParams = url.searchParams.has('code') || url.searchParams.has('state')

      if (hasOAuthParams) {
        // Remove OAuth parameters
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        url.searchParams.delete('scope')
        url.searchParams.delete('authuser')
        url.searchParams.delete('hd')
        url.searchParams.delete('prompt')

        // Update URL without reloading the page
        window.history.replaceState({}, document.title, url.pathname + url.search)
      }
    }
  }, [isAuthenticated])

  // Read URL hash on mount to support direct links to tabs (e.g., /admin#tprm or /admin#tprm-assessments)
  useEffect(() => {
    // Only process hash after authentication to avoid race conditions
    if (!isAuthenticated) return

    const hash = window.location.hash.replace('#', '')
    console.log('[AdminFullFunctional] Processing hash navigation:', hash)
    const validTabs = ['dashboard', 'trust-center', 'analytics', 'settings', 'knowledge-base', 'tprm', 'agents']

    // Handle redirects for moved tabs
    if (hash === 'requests' || hash === 'visitors') {
      console.log('[AdminFullFunctional] Redirecting to trust-center/visitors')
      setActiveTab('trust-center')
      setTrustCenterSubTab('visitors')
      return
    }
    if (hash === 'privacy') {
      console.log('[AdminFullFunctional] Redirecting to trust-center/privacy')
      setActiveTab('trust-center')
      setTrustCenterSubTab('privacy')
      return
    }
    if (hash === 'trust-center-deployments' || hash === 'trust-center-deploy') {
      console.log('[AdminFullFunctional] Redirecting to trust-center/deployments')
      setActiveTab('trust-center')
      setTrustCenterSubTab('deployments')
      return
    }

    // Handle direct tab names
    if (hash && validTabs.includes(hash)) {
      console.log('[AdminFullFunctional] Setting active tab to:', hash)
      setActiveTab(hash as any)
    }
    // Handle sub-tab format (e.g., tprm-assessments)
    else if (hash && hash.includes('-')) {
      const mainTab = hash.split('-')[0]
      console.log('[AdminFullFunctional] Checking sub-tab format, mainTab:', mainTab)
      if (validTabs.includes(mainTab)) {
        console.log('[AdminFullFunctional] Setting active tab to:', mainTab)
        setActiveTab(mainTab as any)
      }
    }
  }, [isAuthenticated])

  // Listen for navigation events from embedded components
  useEffect(() => {
    const handleNavigateToSettings = (event?: any) => {
      setActiveTab('settings')
      // Check if event has subTab detail
      if (event?.detail?.subTab) {
        setSettingsSubTab(event.detail.subTab)
      }
    }

    window.addEventListener('navigate-to-settings', handleNavigateToSettings)
    return () => {
      window.removeEventListener('navigate-to-settings', handleNavigateToSettings)
    }
  }, [])

  // Load user roles when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings' && currentTenant) {
      loadUserRoles()
    }
  }, [activeTab, currentTenant])

  useEffect(() => {
    if (showNotificationsModal && effectiveTenantId) {
      fetchNotifications()
    }
  }, [showNotificationsModal, effectiveTenantId])

  useEffect(() => {
    if (isPlatformAdminMode && activeTab === 'settings') {
      fetchTenantList()
    }
  }, [isPlatformAdminMode, activeTab])

  // First-login auto-scraping
  useEffect(() => {
    if (isAuthenticated && currentUser && effectiveTenantId) {
      const hasScraped = localStorage.getItem(`site-scraped-${effectiveTenantId}`)

      if (!hasScraped && currentUser.email) {
        const emailDomain = currentUser.email.split('@')[1]
        const url = `https://${emailDomain}`

        console.log(`[First Login] Auto-scraping ${url} for tenant ${effectiveTenantId}`)

        setScrapeUrl(url)

        // Get auth headers
        const token = localStorage.getItem('auth-token')
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        }
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        fetch('/api/scrape-site-config', {
          method: 'POST',
          headers,
          body: JSON.stringify({ url, applySettings: true })
        })
          .then(res => res.json())
          .then(data => {
            console.log('[First Login] Auto-scrape complete:', data)
            localStorage.setItem(`site-scraped-${effectiveTenantId}`, 'true')
            fetchAllData()
            triggerContentRefresh()
          })
          .catch(err => {
            console.error('[First Login] Auto-scrape error:', err)
            localStorage.setItem(`site-scraped-${effectiveTenantId}`, 'true')
          })
      }
    }
  }, [isAuthenticated, currentUser, effectiveTenantId])

  // Listen for content refresh events and re-fetch all data
  useEffect(() => {
    let active = true

    const loadData = async () => {
      if (isAuthenticated && effectiveTenantId) {
        console.log('[ContentRefresh] Detected content update, refreshing admin data...')

        // Don't block refreshes - allow concurrent fetches to ensure UI stays fresh
        setLoading(true)
        setError(null)

        try {
          // Add cache-busting headers to prevent stale data
          const headers = getHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          })

          // Fetch all data in parallel
          const [requestsRes, documentsRes, badgesRes, analyticsRes, subprocessorsRes, featuresRes, heroRes, heroTermsRes, heroPrivacyRes, heroDmcaSubmitRes, heroDmcaCounterRes, settingsRes, productsRes, reviewsRes, contractsRes, legalPagesRes] = await Promise.allSettled([
            fetch('/api/admin/requests', { headers }),
            fetch('/api/documents?version=preview&includeDeleted=true&includeArchived=true', { headers }),
            fetch('/api/badges?includeArchived=true', { headers }),
            fetch('/api/admin/analytics', { headers }),
            fetch('/api/subprocessors', { headers }),
            fetch('/api/security-features', { headers }),
            fetch('/api/hero', { headers }),
            fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
            fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
            fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
            fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
            fetch('/api/settings-db', { headers }),
            fetch('/api/products?includeDeleted=true', { headers }),
            fetch('/api/subprocessor-reviews', { headers }),
            fetch('/api/tprm/contracts-db', { headers }), // Use database endpoint for 10-100x faster loading
            fetch('/api/legal-page-status', { headers })
          ])

          if (!active) return

          // Handle requests
          if (requestsRes.status === 'fulfilled' && requestsRes.value.ok) {
            const requestsData = await requestsRes.value.json()
            // Force new array reference to trigger React re-render
            if (active) setRequests(Array.isArray(requestsData) ? [...requestsData] : [])
          } else {
            if (requestsRes.status === 'fulfilled') {
              const errorBody = await requestsRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch requests: ${requestsRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch requests:', requestsRes.reason)
            }
          }

          // Handle documents
          if (documentsRes.status === 'fulfilled' && documentsRes.value.ok) {
            const documentsData = await documentsRes.value.json()
            // API returns {success: true, result: {documents: [...], total: N}}
            const docs = documentsData.success ? documentsData.result.documents : documentsData
            console.log('📄 Fetched documents:', docs.length, 'docs')
            console.log('📄 Deleted docs:', docs.filter((d: any) => d.deleted).length)
            console.log('📄 Archived docs:', docs.filter((d: any) => d.archived).length)
            // Force new array reference to trigger React re-render
            if (active) setDocuments(Array.isArray(docs) ? [...docs] : [])
          } else {
            if (documentsRes.status === 'fulfilled') {
              const errorBody = await documentsRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch documents: ${documentsRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch documents:', documentsRes.reason)
            }
          }

          // Handle badges
          if (badgesRes.status === 'fulfilled' && badgesRes.value.ok) {
            const badgesData = await badgesRes.value.json()
            // API returns {success: true, result: {badges: [...], total: N}}
            const badges = badgesData.success ? badgesData.result.badges : badgesData
            // Force new array reference to trigger React re-render
            if (active) setBadges(Array.isArray(badges) ? [...badges] : [])
          } else {
            if (badgesRes.status === 'fulfilled') {
              const errorBody = await badgesRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch badges: ${badgesRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch badges:', badgesRes.reason)
            }
          }

          // Handle analytics
          if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
            const analyticsData = await analyticsRes.value.json()
            console.log('📊 Analytics data received:', analyticsData)
            console.log('📊 Overview metrics:', analyticsData?.overview)
            console.log('📊 Top documents:', analyticsData?.topRequestedDocuments)
            console.log('📊 Requests by role:', analyticsData?.requestsByRole)
            if (active) setAnalytics(analyticsData)
          } else {
            if (analyticsRes.status === 'fulfilled') {
              const errorBody = await analyticsRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch analytics: ${analyticsRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch analytics:', analyticsRes.reason)
            }
          }

          // Handle content - collect all content types into contentData object
          const contentData: any = {
            subprocessors: [],
            features: [],
            hero: null,
            settings: null,
            products: [],
            contracts: [],
            legalPages: {}
          }

          if (subprocessorsRes.status === 'fulfilled' && subprocessorsRes.value.ok) {
            const subprocessorsData = await subprocessorsRes.value.json()
            // API returns {success: true, result: {subprocessors: [...], total: N}}
            const subprocessors = subprocessorsData.success ? subprocessorsData.result.subprocessors : subprocessorsData
            console.log('🏢 Fetched subprocessors:', subprocessors.length, 'total')
            console.log('🏢 Deleted subprocessors:', subprocessors.filter((s: any) => s.deleted).length)
            console.log('🏢 Archived subprocessors:', subprocessors.filter((s: any) => s.archived).length)
            // Force new array reference to trigger React re-render
            contentData.subprocessors = Array.isArray(subprocessors) ? [...subprocessors] : []
          } else {
            if (subprocessorsRes.status === 'fulfilled') {
              const errorBody = await subprocessorsRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch subprocessors: ${subprocessorsRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch subprocessors:', subprocessorsRes.reason)
            }
          }

          if (featuresRes.status === 'fulfilled' && featuresRes.value.ok) {
            const featuresData = await featuresRes.value.json()
            // API returns {success: true, result: {features: [...], total: N}}
            const features = featuresData.success ? featuresData.result.features : featuresData
            console.log('🛡️ Fetched security features:', features.length, 'total')
            console.log('🛡️ Features list:', features.map((f: any) => f.title).join(', '))
            // Force new array reference to trigger React re-render
            contentData.features = Array.isArray(features) ? [...features] : []
          } else {
            if (featuresRes.status === 'fulfilled') {
              const errorBody = await featuresRes.value.text().catch(() => 'Unable to read response')
              console.error(`Failed to fetch features: ${featuresRes.value.status} - ${errorBody}`)
            } else {
              console.error('Failed to fetch features:', featuresRes.reason)
            }
          }

          // Fetch hero content once and extract sections
          if (heroRes.status === 'fulfilled' && heroRes.value.ok) {
            const heroData = await heroRes.value.json()
            // API returns {success: true, result: {general: {...}, terms: {...}, privacy: {...}}}
            const heroContent = heroData.success ? heroData.result : heroData
            contentData.hero = heroContent?.general || null
            contentData.heroTerms = heroContent?.terms || null
            contentData.heroPrivacy = heroContent?.privacy || null
            contentData.heroDmcaSubmit = heroContent?.dmcaSubmit || null
            contentData.heroDmcaCounter = heroContent?.dmcaCounter || null
          } else {
            console.error('Failed to fetch hero content')
            contentData.hero = null
            contentData.heroTerms = null
            contentData.heroPrivacy = null
            contentData.heroDmcaSubmit = null
            contentData.heroDmcaCounter = null
          }

          if (settingsRes.status === 'fulfilled' && settingsRes.value.ok) {
            const settingsData = await settingsRes.value.json()
            // API returns {success: true, result: {...}}
            const settings = settingsData.success ? settingsData.result : settingsData
            contentData.settings = settings && settings.id ? settings : null
          } else {
            console.error('Failed to fetch settings')
          }

          if (productsRes.status === 'fulfilled' && productsRes.value.ok) {
            const productsData = await productsRes.value.json()
            // API returns {success: true, result: {products: [...], total: N}}
            const products = productsData.success ? productsData.result.products : productsData
            // Force new array reference to trigger React re-render
            contentData.products = Array.isArray(products) ? [...products] : []
          } else {
            console.error('Failed to fetch products')
          }

          // Handle reviews
          if (reviewsRes.status === 'fulfilled' && reviewsRes.value.ok) {
            const reviewsData = await reviewsRes.value.json()
            // Force new array reference to trigger React re-render
            if (active) setReviews(Array.isArray(reviewsData) ? [...reviewsData] : [])
          } else {
            console.error('Failed to fetch reviews')
          }

          // Handle contracts
          if (contractsRes.status === 'fulfilled' && contractsRes.value.ok) {
            const contractsData = await contractsRes.value.json()
            const contracts = contractsData.contracts || []
            console.log('📄 Fetched contracts:', contracts.length, 'total')
            contentData.contracts = Array.isArray(contracts) ? [...contracts] : []
          } else {
            console.error('Failed to fetch contracts')
          }

          if (legalPagesRes.status === 'fulfilled' && legalPagesRes.value.ok) {
            const legalPagesData = await legalPagesRes.value.json()
            contentData.legalPages = legalPagesData || {}
            console.log('📋 Fetched legal page statuses:', Object.keys(legalPagesData).length, 'pages')
          } else {
            console.error('Failed to fetch legal page statuses')
          }

          // Note: contentData object was built but individual state setters
          // (setDocuments, setRequests, etc.) are already called above
          // No need for a single setContent() call
        } catch (error) {
          if (active) {
            console.error('Failed to load admin data:', error)
            setError(error instanceof Error ? error.message : 'Failed to load data')
          }
        } finally {
          if (active) {
            setLoading(false)
          }
        }
      }
    }

    loadData()
    return () => { active = false }
  }, [lastContentUpdate])

  const fetchTenantList = async () => {
    try {
      const response = await fetch('/api/admin/tenants', { credentials: 'include' })
      if (!response.ok) throw new Error('Failed to fetch tenants')

      const data = await response.json()
      if (Array.isArray(data)) {
        setTenantList(data.map((t: any) => ({ id: t.id, name: t.name || t.id })))
      }
    } catch (error) {
      console.error('Error fetching tenant list:', error)
      setTenantList([])
    }
  }

  const fetchNotifications = async () => {
    if (!effectiveTenantId) return

    setLoadingNotifications(true)
    try {
      const response = await fetch(`/api/notifications?tenantId=${effectiveTenantId}`)
      if (!response.ok) throw new Error('Failed to fetch notifications')

      const data = await response.json()
      setNotifications(data.notifications || [])
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setNotifications([])
    } finally {
      setLoadingNotifications(false)
    }
  }

  const checkAuthStatus = async () => {
    try {
      // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
      const token = localStorage.getItem('auth-token')
      console.log('Token in localStorage:', token ? 'Found' : 'Not found - checking cookie')

      // Check for valid JWT session - will check cookie if no Authorization header
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
        console.log('✅ Authorization header will be sent')
      } else {
        console.log('⚠️ No token - Authorization header will NOT be sent')
      }
      console.log('📤 Sending request with headers:', Object.keys(headers))

      const response = await fetch('/api/auth/validate', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      console.log('📥 Response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        const user = data.user

        // Check if user has admin role (admin, platform-admin, or super-admin)
        if (user.role === 'admin' || user.role === 'platform-admin' || user.role === 'super-admin') {
          // Determine specific admin role type
          const userRole = user.role === 'platform-admin' || user.role === 'super-admin' ? user.role :
                          user.email.includes('security') ? 'security' :
                          user.email.includes('legal') ? 'legal' : 'admin'

          setCurrentUser({
            email: user.email,
            role: userRole,
            name: user.name || user.email.split('@')[0].replace('.', ' ')
          })
          setIsAuthenticated(true)

          setTimeout(() => fetchAllData(), 100)
        } else {
          console.log('Access denied: User does not have admin privileges')
          setError('Access denied: Admin privileges required')
        }
      } else {
        console.log('No valid admin session found - redirecting to login')
        // Store return URL so user comes back here after login
        sessionStorage.setItem('returnUrl', window.location.pathname)
        navigate('/login', { replace: true })
      }
    } catch (e) {
      console.error('Auth check failed:', e)
      // On auth error, redirect to login
      sessionStorage.setItem('returnUrl', window.location.pathname)
      navigate('/login', { replace: true })
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogin = (email: string) => {
    // This will be handled by the Google SSO callback
    // After successful SSO, user will be redirected back here
    // and checkAuthStatus will verify their session
    window.location.reload()
  }

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear session
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (e) {
      console.error('Logout error:', e)
    } finally {
      // SECURITY: Clear ALL cached data including sensitive information

      // Clear localStorage (auth tokens, cached API keys, etc.)
      localStorage.clear()

      // Clear sessionStorage
      sessionStorage.clear()

      // Clear all cookies by setting them to expire
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim()
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      })

      // Clear component state
      setIsAuthenticated(false)
      setCurrentUser(null)
      setRequests([])
      setDocuments([])
      setAnalytics(null)

      // Force redirect to clear any cached page state
      window.location.href = '/'
    }
  }

  // OAuth2 Client Management
  const fetchOAuthClients = async () => {
    try {
      const response = await fetch('/oauth/clients', {
        headers: getHeaders()
      })
      if (response.ok) {
        const data = await response.json()
        setOauthClients(data.result?.clients || [])
      }
    } catch (error) {
      console.error('Error fetching OAuth clients:', error)
    }
  }

  const createOAuthClient = async () => {
    try {
      const response = await fetch('/oauth/clients', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(newOAuthClient)
      })
      if (response.ok) {
        const data = await response.json()
        setCreatedClientCredentials(data.result)
        setShowCreateOAuthClient(false)
        setNewOAuthClient({ name: '', redirect_uris: [''], scopes: ['read', 'write'] })
        await fetchOAuthClients()
      } else {
        const error = await response.json()
        alert(`Error creating OAuth client: ${error.error}`)
      }
    } catch (error) {
      console.error('Error creating OAuth client:', error)
      alert('Failed to create OAuth client')
    }
  }

  const deleteOAuthClient = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this OAuth client? This will revoke all associated tokens.')) {
      return
    }
    try {
      const response = await fetch(`/oauth/clients/${clientId}`, {
        method: 'DELETE',
        headers: getHeaders()
      })
      if (response.ok) {
        await fetchOAuthClients()
      } else {
        const error = await response.json()
        alert(`Error deleting OAuth client: ${error.error}`)
      }
    } catch (error) {
      console.error('Error deleting OAuth client:', error)
      alert('Failed to delete OAuth client')
    }
  }

  // Fetch OAuth clients when integrations tab is viewed
  useEffect(() => {
    if (activeTab === 'settings' && settingsSubTab === 'integrations' && isAuthenticated) {
      fetchOAuthClients()
    }
  }, [activeTab, settingsSubTab, isAuthenticated])

  const fetchAllData = async () => {
    // Don't block refreshes - allow concurrent fetches to ensure UI stays fresh
    setLoading(true)
    setError(null)

    try {
      // Add cache-busting headers to prevent stale data
      const headers = getHeaders({
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      })

      // Fetch all data in parallel
      const [requestsRes, documentsRes, badgesRes, analyticsRes, subprocessorsRes, featuresRes, heroRes, heroTermsRes, heroPrivacyRes, heroDmcaSubmitRes, heroDmcaCounterRes, settingsRes, productsRes, reviewsRes, contractsRes, legalPagesRes] = await Promise.allSettled([
        fetch('/api/admin/requests', { headers }),
        fetch('/api/documents?version=all&includeDeleted=true&includeArchived=true', { headers }),
        fetch('/api/badges?includeArchived=true', { headers }),
        fetch('/api/admin/analytics', { headers }),
        fetch('/api/subprocessors', { headers }),
        fetch('/api/security-features', { headers }),
        fetch('/api/hero', { headers }),
        fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
        fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
        fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
        fetch('/api/hero', { headers }), // All hero content in one call - will dedupe below
        fetch('/api/settings-db', { headers }),
        fetch('/api/products?includeDeleted=true', { headers }),
        fetch('/api/subprocessor-reviews', { headers }),
        fetch('/api/tprm/contracts', { headers }),
        fetch('/api/legal-page-status', { headers })
      ])

      // Handle requests
      if (requestsRes.status === 'fulfilled' && requestsRes.value.ok) {
        const requestsData = await requestsRes.value.json()
        // Force new array reference to trigger React re-render
        setRequests(Array.isArray(requestsData) ? [...requestsData] : [])
      } else {
        if (requestsRes.status === 'fulfilled') {
          const errorBody = await requestsRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch requests: ${requestsRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch requests:', requestsRes.reason)
        }
      }

      // Handle documents
      if (documentsRes.status === 'fulfilled' && documentsRes.value.ok) {
        const documentsData = await documentsRes.value.json()
        // API returns {success: true, result: {documents: [...], total: N}}
        const docs = documentsData.success ? documentsData.result.documents : documentsData
        console.log('📄 Fetched documents:', docs.length, 'docs')
        console.log('📄 Deleted docs:', docs.filter((d: any) => d.deleted).length)
        console.log('📄 Archived docs:', docs.filter((d: any) => d.archived).length)

        // DEBUG: Check for redacted documents
        const redactedDocs = docs.filter((d: any) => d.redacted)
        console.log('🔍 DEBUG: Found', redactedDocs.length, 'redacted documents')
        if (redactedDocs.length > 0) {
          console.log('🔍 DEBUG: First redacted doc:', redactedDocs[0].id, redactedDocs[0].title)
          console.log('🔍 DEBUG: Has redacted field:', redactedDocs[0].redacted)
          console.log('🔍 DEBUG: Has allowedPages field:', redactedDocs[0].allowedPages)
        }

        // Force new array reference to trigger React re-render
        setDocuments(Array.isArray(docs) ? [...docs] : [])
      } else {
        if (documentsRes.status === 'fulfilled') {
          const errorBody = await documentsRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch documents: ${documentsRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch documents:', documentsRes.reason)
        }
      }

      // Handle badges
      if (badgesRes.status === 'fulfilled' && badgesRes.value.ok) {
        const badgesData = await badgesRes.value.json()
        // API returns {success: true, result: {badges: [...], total: N}}
        const badges = badgesData.success ? badgesData.result.badges : badgesData
        console.log(`🏅 Fetched compliance badges: ${Array.isArray(badges) ? badges.length : 0} total`)
        console.log(`🏅 Active badges: ${Array.isArray(badges) ? badges.filter(b => !b.archived && !b.deleted).length : 0}`)
        console.log(`🏅 Archived badges: ${Array.isArray(badges) ? badges.filter(b => b.archived && !b.deleted).length : 0}`)
        console.log(`🏅 Deleted badges: ${Array.isArray(badges) ? badges.filter(b => b.deleted).length : 0}`)
        // Force new array reference to trigger React re-render
        setBadges(Array.isArray(badges) ? [...badges] : [])
      } else {
        if (badgesRes.status === 'fulfilled') {
          const errorBody = await badgesRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch badges: ${badgesRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch badges:', badgesRes.reason)
        }
      }

      // Handle analytics
      if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
        const analyticsData = await analyticsRes.value.json()
        console.log('📊 Analytics data received:', analyticsData)
        console.log('📊 Overview metrics:', analyticsData?.overview)
        console.log('📊 Top documents:', analyticsData?.topRequestedDocuments)
        console.log('📊 Requests by role:', analyticsData?.requestsByRole)
        setAnalytics(analyticsData)
      } else {
        if (analyticsRes.status === 'fulfilled') {
          const errorBody = await analyticsRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch analytics: ${analyticsRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch analytics:', analyticsRes.reason)
        }
      }

      // Handle content - collect all content types into contentData object
      const contentData: any = {
        subprocessors: [],
        features: [],
        hero: null,
        settings: null,
        products: [],
        contracts: []
      }

      if (subprocessorsRes.status === 'fulfilled' && subprocessorsRes.value.ok) {
        const subprocessorsData = await subprocessorsRes.value.json()
        // API returns {success: true, result: {subprocessors: [...], total: N}}
        const subprocessors = subprocessorsData.success ? subprocessorsData.result.subprocessors : subprocessorsData
        console.log('🏢 Fetched subprocessors:', subprocessors.length, 'total')
        console.log('🏢 Deleted subprocessors:', subprocessors.filter((s: any) => s.deleted).length)
        console.log('🏢 Archived subprocessors:', subprocessors.filter((s: any) => s.archived).length)
        // Force new array reference to trigger React re-render
        contentData.subprocessors = Array.isArray(subprocessors) ? [...subprocessors] : []
      } else {
        if (subprocessorsRes.status === 'fulfilled') {
          const errorBody = await subprocessorsRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch subprocessors: ${subprocessorsRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch subprocessors:', subprocessorsRes.reason)
        }
      }

      if (featuresRes.status === 'fulfilled' && featuresRes.value.ok) {
        const featuresData = await featuresRes.value.json()
        // API returns {success: true, result: {features: [...], total: N}}
        const features = featuresData.success ? featuresData.result.features : featuresData
        console.log('🛡️ Fetched security features:', features.length, 'total')
        console.log('🛡️ Features list:', features.map((f: any) => f.title).join(', '))
        // Force new array reference to trigger React re-render
        contentData.features = Array.isArray(features) ? [...features] : []
      } else {
        if (featuresRes.status === 'fulfilled') {
          const errorBody = await featuresRes.value.text().catch(() => 'Unable to read response')
          console.error(`Failed to fetch features: ${featuresRes.value.status} - ${errorBody}`)
        } else {
          console.error('Failed to fetch features:', featuresRes.reason)
        }
      }

      // Fetch hero content once and extract sections
      if (heroRes.status === 'fulfilled' && heroRes.value.ok) {
        const heroData = await heroRes.value.json()
        // API returns {success: true, result: {general: {...}, terms: {...}, privacy: {...}}}
        const heroContent = heroData.success ? heroData.result : heroData
        contentData.hero = heroContent?.general || null
        contentData.heroTerms = heroContent?.terms || null
        contentData.heroPrivacy = heroContent?.privacy || null
        contentData.heroDmcaSubmit = heroContent?.dmcaSubmit || null
        contentData.heroDmcaCounter = heroContent?.dmcaCounter || null
      } else {
        console.error('Failed to fetch hero content')
        contentData.hero = null
        contentData.heroTerms = null
        contentData.heroPrivacy = null
        contentData.heroDmcaSubmit = null
        contentData.heroDmcaCounter = null
      }

      if (settingsRes.status === 'fulfilled' && settingsRes.value.ok) {
        const settingsData = await settingsRes.value.json()
        // API returns {success: true, result: {...}}
        const settings = settingsData.success ? settingsData.result : settingsData
        // Force new object reference to trigger React re-render
        contentData.settings = settings && settings.id ? { ...settings } : null
      } else {
        console.error('Failed to fetch settings')
      }

      if (productsRes.status === 'fulfilled' && productsRes.value.ok) {
        const productsData = await productsRes.value.json()
        // API returns {success: true, result: {products: [...], total: N}}
        const products = productsData.success ? productsData.result.products : productsData
        // Force new array reference to trigger React re-render
        contentData.products = Array.isArray(products) ? [...products] : []
      } else {
        console.error('Failed to fetch products')
      }

      // Handle reviews
      if (reviewsRes.status === 'fulfilled' && reviewsRes.value.ok) {
        const reviewsData = await reviewsRes.value.json()
        // Force new array reference to trigger React re-render
        setReviews(Array.isArray(reviewsData) ? [...reviewsData] : [])
      } else {
        console.error('Failed to fetch reviews')
      }

      // Handle contracts
      if (contractsRes.status === 'fulfilled' && contractsRes.value.ok) {
        const contractsData = await contractsRes.value.json()
        const contracts = contractsData.contracts || []
        console.log('📄 Fetched contracts:', contracts.length, 'total')
        contentData.contracts = Array.isArray(contracts) ? [...contracts] : []
      } else {
        console.error('Failed to fetch contracts')
      }

      if (legalPagesRes.status === 'fulfilled' && legalPagesRes.value.ok) {
        const legalPagesData = await legalPagesRes.value.json()
        contentData.legalPages = legalPagesData || {}
        console.log('📋 Fetched legal page statuses:', Object.keys(legalPagesData).length, 'pages')
      } else {
        console.error('Failed to fetch legal page statuses')
      }

      // Force new object reference to trigger React re-render
      setContentData({ ...contentData })

      // Force component re-render by incrementing key
      setRenderKey(prev => prev + 1)
      console.log('🔄 Render key updated to force re-render')

    } catch (err) {
      console.error('Fetch data error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  // Filter and sort documents
  const getFilteredAndSortedDocuments = () => {
    let filtered = [...documents]

    // Filter by status based on active tab
    if (documentTab === 'active') {
      filtered = filtered.filter(doc => doc && !doc.archived && !doc.deleted)
    } else if (documentTab === 'archived') {
      filtered = filtered.filter(doc => doc && doc.archived && !doc.deleted)
    } else if (documentTab === 'recyclebin') {
      filtered = filtered.filter(doc => doc && doc.deleted)
    }

    // Apply category filter
    if (docFilterCategory !== 'all') {
      filtered = filtered.filter(doc => doc && doc.category === docFilterCategory)
    }

    // Apply visibility filter
    if (docFilterVisibility !== 'all') {
      filtered = filtered.filter(doc => doc && doc.visibility === docFilterVisibility)
    }

    // Apply document type filter
    if (docFilterType !== 'all') {
      filtered = filtered.filter(doc => doc && doc.documentType === docFilterType)
    }

    // Apply attestation tag filter
    if (docFilterAttestation !== 'all') {
      filtered = filtered.filter(doc =>
        doc && doc.attestationTags && doc.attestationTags.includes(docFilterAttestation)
      )
    }

    // Apply product filter
    if (docFilterProduct !== 'all') {
      if (docFilterProduct === 'unassigned') {
        filtered = filtered.filter(doc => doc && (!doc.productIds || doc.productIds.length === 0))
      } else {
        filtered = filtered.filter(doc =>
          doc && doc.productIds && doc.productIds.includes(docFilterProduct)
        )
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0

      switch (docSortBy) {
        case 'name':
          comparison = a.title.localeCompare(b.title)
          break
        case 'date':
          comparison = new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
          break
        case 'size':
          comparison = b.size - a.size
          break
        case 'category':
          comparison = a.category.localeCompare(b.category)
          break
        case 'type':
          comparison = (a.documentType || 'other').localeCompare(b.documentType || 'other')
          break
      }

      return docSortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  }

  const filteredDocuments = getFilteredAndSortedDocuments()

  // Get unique categories and types for filter dropdowns
  const uniqueCategories = Array.from(new Set(documents.map(doc => doc.category)))
  const uniqueTypes = Array.from(new Set(documents.map(doc => doc.documentType).filter(Boolean)))
  const uniqueAttestations = Array.from(new Set(documents.flatMap(doc => doc.attestationTags || []))).sort()

  const fetchRequests = async () => {
    try {
      const response = await fetch('/api/admin/requests', {
        headers: getHeaders()
      })

      if (response.ok) {
        const data = await response.json()
        setRequests(Array.isArray(data) ? data : [])
      } else {
        console.error('Failed to fetch requests:', response.status)
      }
    } catch (err) {
      console.error('Fetch requests error:', err)
    }
  }

  const handleRequestAction = async (requestId: string, action: 'approve' | 'reject') => {
    if (!currentUser) return

    try {
      const response = await fetch('/api/admin/requests', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          requestId,
          action,
          adminEmail: currentUser.email
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('❌ Request action failed:', errorData)
        console.error('❌ Status:', response.status)
        console.error('❌ Error details:', errorData.details)
        throw new Error(errorData.details || errorData.error || 'Failed to update request')
      }

      // Refresh requests
      await fetchRequests()
    } catch (err) {
      console.error('Error updating request:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update request'
      alert(`Failed to update request: ${errorMessage}`)
    }
  }

  const handleApproveRequest = async (requestId: string) => {
    await handleRequestAction(requestId, 'approve')
  }

  const handleRejectRequest = async (requestId: string) => {
    await handleRequestAction(requestId, 'reject')
  }

  const handleArchiveRequest = async (requestId: string) => {
    if (!currentUser) return
    if (!confirm('Archive this access request?')) return

    try {
      const response = await fetch('/api/admin/requests', {
        method: 'DELETE',
        headers: getHeaders(),
        body: JSON.stringify({
          requestId,
          archive: true
        })
      })

      if (!response.ok) {
        throw new Error('Failed to archive request')
      }

      // Refresh requests
      await fetchRequests()
      triggerContentRefresh()
      alert('Access request archived!')
    } catch (err) {
      console.error('Error archiving request:', err)
      alert('Failed to archive request')
    }
  }

  const handleDeleteRequest = async (requestId: string) => {
    if (!currentUser) return
    if (!confirm('Move this access request to recycle bin? You can restore it later.')) return

    try {
      const response = await fetch('/api/admin/requests', {
        method: 'DELETE',
        headers: getHeaders(),
        body: JSON.stringify({
          requestId,
          delete: true
        })
      })

      if (!response.ok) {
        throw new Error('Failed to delete request')
      }

      // Refresh requests
      await fetchRequests()
      triggerContentRefresh()
      alert('Access request moved to recycle bin!')
    } catch (err) {
      console.error('Error deleting request:', err)
      alert('Failed to delete request')
    }
  }

  // Helper function to match category based on filename
  const matchCategory = (filename: string): string => {
    const lower = filename.toLowerCase()

    // Check for certifications
    if (lower.includes('soc') || lower.includes('iso') || lower.includes('cert') ||
        lower.includes('certificate') || lower.includes('compliance')) {
      return 'certifications'
    }

    // Check for reports
    if (lower.includes('pentest') || lower.includes('penetration') || lower.includes('audit') ||
        lower.includes('assessment') || lower.includes('report')) {
      return 'reports'
    }

    // Check for policies
    if (lower.includes('policy') || lower.includes('policies') || lower.includes('procedure') ||
        lower.includes('guideline')) {
      return 'policies'
    }

    // Check for architecture
    if (lower.includes('architecture') || lower.includes('diagram') || lower.includes('infrastructure') ||
        lower.includes('design')) {
      return 'architecture'
    }

    // Check for questionnaires
    if (lower.includes('questionnaire') || lower.includes('caiq') || lower.includes('vsaq') ||
        lower.includes('sq')) {
      return 'questionnaires'
    }

    // Check for legal
    if (lower.includes('dpa') || lower.includes('agreement') || lower.includes('contract') ||
        lower.includes('legal') || lower.includes('terms')) {
      return 'legal'
    }

    // Default to certifications
    return 'certifications'
  }

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0 || !currentUser) return

    setBulkUploading(true)
    let successCount = 0
    let failCount = 0
    let duplicateCount = 0
    const uploadedFiles: string[] = []
    const failedFiles: string[] = []
    const duplicateFiles: string[] = []

    try {
      for (const file of bulkFiles) {
        try {
          console.log(`[Bulk Upload] Uploading ${file.name} (${successCount + duplicateCount + 1}/${bulkFiles.length})...`)

          const formData = new FormData()
          const title = file.name.replace(/\.[^/.]+$/, '') // Remove extension
          const category = matchCategory(file.name)
          const documentType = detectDocumentType(title, category) // Auto-detect type

          formData.append('file', file)
          formData.append('title', title)
          formData.append('category', category)
          formData.append('documentType', documentType)
          formData.append('visibility', bulkOptions.visibility)
          formData.append('gated', String(bulkOptions.visibility === 'gated')) // Legacy field for backwards compatibility
          formData.append('ndaRequired', String(bulkOptions.ndaRequired))
          formData.append('watermarked', String(bulkOptions.watermarked))
          formData.append('redacted', String(bulkOptions.redacted))
          formData.append('allowedPages', bulkOptions.allowedPages)
          formData.append('uploadedBy', currentUser.email)

          const response = await fetch('/api/admin/upload-document', {
            method: 'POST',
            credentials: 'include',
            body: formData
          })

          if (response.ok) {
            const result = await response.json()
            if (result.duplicate) {
              duplicateCount++
              duplicateFiles.push(`${file.name} → ${result.existingDocument.title}`)
              console.log(`[Bulk Upload] ⚠️ ${file.name} is a duplicate of "${result.existingDocument.title}" (skipped)`)
            } else {
              successCount++
              uploadedFiles.push(file.name)
              console.log(`[Bulk Upload] ✅ ${file.name} uploaded successfully (ID: ${result.documentId || 'unknown'})`)
            }
          } else {
            failCount++
            failedFiles.push(file.name)
            const errorText = await response.text()
            console.error(`[Bulk Upload] ❌ Failed to upload ${file.name}: ${response.status} ${response.statusText}`, errorText)
          }

          // Small delay between uploads to avoid overwhelming the backend
          if (successCount + failCount < bulkFiles.length) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        } catch (err) {
          failCount++
          failedFiles.push(file.name)
          console.error(`[Bulk Upload] ❌ Error uploading ${file.name}:`, err)
        }
      }

      console.log('[Bulk Upload] Upload phase complete:', {
        total: bulkFiles.length,
        successful: successCount,
        duplicates: duplicateCount,
        failed: failCount,
        uploadedFiles,
        duplicateFiles,
        failedFiles
      })

      // Clear bulk files
      setBulkFiles([])

      // Wait for blob store replication with retry logic
      console.log(`[Bulk Upload] Waiting for ${successCount} documents to appear in blob store...`)
      const maxRetries = 5
      const delays = [1000, 2000, 3000, 4000, 5000] // 1s, 2s, 3s, 4s, 5s (total 15s max)

      const initialDocCount = documents.length
      const expectedCount = initialDocCount + successCount

      let allDocsFound = false
      let currentDocCount = initialDocCount
      for (let i = 0; i < maxRetries; i++) {
        console.log(`[Bulk Upload] Retry ${i + 1}/${maxRetries} - waiting ${delays[i]}ms...`)
        await new Promise(resolve => setTimeout(resolve, delays[i]))

        // Fetch documents directly to check count
        try {
          const headers = getHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          })
          const response = await fetch('/api/documents', { headers })
          if (response.ok) {
            const docs = await response.json()
            currentDocCount = Array.isArray(docs) ? docs.length : 0
            console.log(`[Bulk Upload] Attempt ${i + 1}: Expected ${expectedCount} docs, got ${currentDocCount} docs`)

            if (currentDocCount >= expectedCount) {
              console.log('[Bulk Upload] ✅ All documents found in blob store!')
              allDocsFound = true
              // Update state with latest docs
              setDocuments(Array.isArray(docs) ? [...docs] : [])
              break
            } else {
              console.log(`[Bulk Upload] Still waiting for ${expectedCount - currentDocCount} documents...`)
            }
          }
        } catch (err) {
          console.error(`[Bulk Upload] Retry ${i + 1} failed:`, err)
        }
      }

      // Final refresh to ensure UI is up to date
      triggerContentRefresh()

      if (allDocsFound) {
        let message = `Bulk upload complete!\n\nUploaded: ${successCount}\nDuplicates skipped: ${duplicateCount}\nFailed: ${failCount}`

        if (duplicateCount > 0) {
          message += `\n\nDuplicate files (already uploaded):\n${duplicateFiles.join('\n')}`
        }

        if (failCount > 0) {
          message += `\n\nFailed files:\n${failedFiles.join('\n')}\n\nCheck console for error details.`
        }

        alert(message)
      } else {
        const missingCount = expectedCount - currentDocCount
        let message = `Bulk upload complete with delays!\n\nUploaded: ${successCount}\nDuplicates skipped: ${duplicateCount}\nFailed: ${failCount}\nShowing: ${currentDocCount - initialDocCount}/${successCount}\n\nSome documents are still being processed (${missingCount} pending).\nThey should appear shortly. Refresh if needed.`

        if (duplicateCount > 0) {
          message += `\n\nDuplicate files (skipped):\n${duplicateFiles.join('\n')}`
        }

        if (failCount > 0) {
          message += `\n\nFailed files:\n${failedFiles.join('\n')}`
        }

        alert(message)
      }

    } catch (err) {
      console.error('Bulk upload error:', err)
      alert('Bulk upload failed')
    } finally {
      setBulkUploading(false)
    }
  }

  // Auto-detect document type based on title and category
  const detectDocumentType = (title: string, category: string): string => {
    const searchText = `${title} ${category}`.toLowerCase()

    // Attestation-specific patterns (check first for more specific matching)
    const attestationPatterns = [
      'soc 2 type ii', 'soc 2 type i', 'soc 2', 'soc2', 'soc 1', 'soc 3',
      'iso 27001', 'iso27001', 'iso 27017', 'iso 27018', 'iso 9001', 'iso 42001',
      'pci dss', 'pci-dss', 'payment card industry',
      'hipaa', 'health insurance portability',
      'gdpr', 'general data protection',
      'ccpa', 'california consumer privacy',
      'fedramp', 'federal risk',
      'csa star', 'cloud security alliance',
      'tisax', 'trusted information security',
      'nist csf', 'nist 800-53', 'nist cybersecurity',
      'aicpa', 'bsi c5', 'pci 3ds'
    ]

    // Document type detection patterns
    const typePatterns = [
      { type: 'policy', patterns: ['policy', 'policies', 'privacy policy', 'security policy', 'acceptable use'] },
      { type: 'procedure', patterns: ['procedure', 'procedures', 'process', 'runbook', 'guideline', 'guidelines'] },
      { type: 'attestation', patterns: [...attestationPatterns, 'attestation', 'audit report', 'compliance report', 'audit attestation'] },
      { type: 'certificate', patterns: ['certificate', 'certification', 'cert ', 'certified'] },
      { type: 'report', patterns: ['report', 'assessment', 'analysis', 'pentest', 'penetration test', 'security assessment'] },
      { type: 'agreement', patterns: ['agreement', 'contract', 'dpa', 'data processing', 'nda', 'msa', 'sla', 'service level'] }
    ]

    // Find matching type
    for (const typePattern of typePatterns) {
      if (typePattern.patterns.some(pattern => searchText.includes(pattern))) {
        return typePattern.type
      }
    }

    return 'other'
  }

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!uploadForm.file || !uploadForm.title || !currentUser) return

    setUploading(true)

    try {
      // Auto-detect document type if not set
      const documentType = uploadForm.documentType || detectDocumentType(uploadForm.title, uploadForm.category)

      const formData = new FormData()
      formData.append('file', uploadForm.file)
      formData.append('title', uploadForm.title)
      formData.append('category', uploadForm.category)
      formData.append('documentType', documentType)
      formData.append('gated', uploadForm.gated.toString())
      formData.append('ndaRequired', uploadForm.ndaRequired.toString())
      formData.append('watermarked', uploadForm.watermarked.toString())
      formData.append('redacted', uploadForm.redacted.toString())
      formData.append('allowedPages', uploadForm.allowedPages)
      formData.append('visibility', uploadForm.visibility)
      formData.append('hidden', uploadForm.hidden.toString())
      formData.append('uploadedBy', currentUser.email)

      const response = await fetch('/api/admin/upload-document', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = 'Failed to upload document'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.details || errorMessage
          console.error('Upload error details:', errorData)
        } catch (e) {
          console.error('Could not parse error response')
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()
      console.log('Upload result:', result)

      // Reset form
      setUploadForm({
        title: '',
        category: 'certifications',
        documentType: '',
        gated: true,
        ndaRequired: true,
        watermarked: true,
        visibility: 'admin',
        hidden: false,
        productIds: [],
        file: null
      })

      // Refresh documents
      triggerContentRefresh()

      setTimeout(() => alert('Document uploaded successfully!'), 100)

    } catch (err) {
      console.error('Upload error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document'
      alert(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to move this document to the recycle bin?')) return
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      const response = await fetch(`/api/documents/${docId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deleted: true,
          deletedAt: now,
          deletedBy: currentUser.email
        })
      })

      if (!response.ok) {
        throw new Error('Failed to delete document')
      }

      // Mark document as deleted in local state (will show in Recycle Bin tab)
      setDocuments(prevDocs => prevDocs.map(doc =>
        doc.id === docId
          ? { ...doc, deleted: true, deletedAt: now, deletedBy: currentUser.email }
          : doc
      ))

      // Trigger content refresh to update all connected components
      triggerContentRefresh()
      alert('Document moved to recycle bin!')

    } catch (err) {
      console.error('Delete error:', err)
      alert('Failed to delete document')
      // Refresh to ensure UI is in sync with server
      triggerContentRefresh()
    }
  }

  const handleArchiveDocument = async (docId: string) => {
    if (!currentUser) return

    try {
      const response = await fetch(`/api/documents/${docId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to archive document')
      }

      const now = new Date().toISOString()
      // Update local state to mark document as archived
      setDocuments(prevDocs => prevDocs.map(doc =>
        doc.id === docId
          ? { ...doc, archived: true, archivedAt: now, archivedBy: currentUser.email }
          : doc
      ))

      // Trigger content refresh to update all connected components
      triggerContentRefresh()
      alert('Document archived successfully!')

    } catch (err) {
      console.error('Archive error:', err)
      alert('Failed to archive document')
      // Refresh to ensure UI is in sync with server
      triggerContentRefresh()
    }
  }

  // Bulk document actions
  const handleSelectAll = () => {
    if (selectedDocuments.size === documents.length) {
      // Deselect all
      setSelectedDocuments(new Set())
    } else {
      // Select all
      setSelectedDocuments(new Set(documents.map(doc => doc.id)))
    }
  }

  const handleToggleDocument = (docId: string) => {
    const newSelected = new Set(selectedDocuments)
    if (newSelected.has(docId)) {
      newSelected.delete(docId)
    } else {
      newSelected.add(docId)
    }
    setSelectedDocuments(newSelected)
  }

  const handleBulkArchive = async () => {
    if (selectedDocuments.size === 0) {
      alert('No documents selected')
      return
    }

    if (!confirm(`Are you sure you want to archive ${selectedDocuments.size} document(s)?`)) return
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      const selectedIds = Array.from(selectedDocuments)

      // Optimistic update
      setDocuments(prevDocs => prevDocs.map(doc =>
        selectedIds.includes(doc.id)
          ? { ...doc, archived: true, archivedAt: now, archivedBy: currentUser.email }
          : doc
      ))

      // Archive each document
      for (const docId of selectedIds) {
        const response = await fetch(`/api/documents/${docId}/archive`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })

        if (!response.ok) {
          console.error(`Failed to archive document ${docId}`)
        }
      }

      setSelectedDocuments(new Set())
      triggerContentRefresh()
      alert(`Successfully archived ${selectedIds.length} document(s)!`)

    } catch (err) {
      console.error('Bulk archive error:', err)
      alert('Failed to archive documents')
      triggerContentRefresh()
    }
  }

  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) {
      alert('No documents selected')
      return
    }

    if (!confirm(`Are you sure you want to move ${selectedDocuments.size} document(s) to the recycle bin?`)) return
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      const selectedIds = Array.from(selectedDocuments)

      // Optimistic update
      setDocuments(prevDocs => prevDocs.map(doc =>
        selectedIds.includes(doc.id)
          ? { ...doc, deleted: true, deletedAt: now, deletedBy: currentUser.email }
          : doc
      ))

      // Delete each document
      for (const docId of selectedIds) {
        const response = await fetch(`/api/documents/${docId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })

        if (!response.ok) {
          console.error(`Failed to delete document ${docId}`)
        }
      }

      setSelectedDocuments(new Set())
      triggerContentRefresh()
      alert(`Successfully moved ${selectedIds.length} document(s) to recycle bin!`)

    } catch (err) {
      console.error('Bulk delete error:', err)
      alert('Failed to delete documents')
      triggerContentRefresh()
    }
  }

  const openBulkEditModal = () => {
    if (selectedDocuments.size === 0) {
      alert('No documents selected')
      return
    }
    setShowBulkEditModal(true)
  }

  const handleBulkEdit = async () => {
    if (selectedDocuments.size === 0) {
      alert('No documents selected')
      return
    }

    if (!currentUser) return

    try {
      const selectedIds = Array.from(selectedDocuments)

      // Build updates object - only include non-empty fields
      const updates: any = {
        ndaRequired: bulkEditForm.ndaRequired,
        watermarked: bulkEditForm.watermarked,
        visibility: bulkEditForm.visibility
      }

      // Only add category, type, product if they have values
      if (bulkEditForm.category) updates.category = bulkEditForm.category
      if (bulkEditForm.type) updates.type = bulkEditForm.type
      if (bulkEditForm.product) updates.product = bulkEditForm.product

      // Optimistic update
      setDocuments(prevDocs => prevDocs.map(doc =>
        selectedIds.includes(doc.id)
          ? { ...doc, ...updates }
          : doc
      ))

      // Update each document
      for (const docId of selectedIds) {
        const response = await fetch(`/api/documents/${docId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        })

        if (!response.ok) {
          console.error(`Failed to update document ${docId}`)
        }
      }

      setShowBulkEditModal(false)
      setSelectedDocuments(new Set())
      setSelectedAdminDocs(new Set()) // Also clear admin docs selection
      await fetchAllData()
      triggerContentRefresh()
      alert(`Successfully updated ${selectedIds.length} document(s)!`)

    } catch (err) {
      console.error('Bulk edit error:', err)
      alert('Failed to update documents')
      await fetchAllData()
      triggerContentRefresh()
    }
  }

  const handleRescanAll = async () => {
    if (!confirm(`Re-scan compliance for all ${documents.length} documents?\n\nThis will use AI to analyze each document and may incur costs (~$0.075 per document).\n\nEstimated cost: $${(documents.length * 0.075).toFixed(2)}`)) {
      return
    }

    setRescanningAll(true)
    setScanProgress(null)

    try {
      // Start the scan job
      console.log('[Rescan] Starting compliance scan job')
      const response = await fetch('/api/rescan-document-compliance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanAll: true })
      })

      if (!response.ok) {
        throw new Error(`Failed to start scan: ${response.status}`)
      }

      const data = await response.json()
      console.log('[Rescan] Job started:', data)

      const jobId = data.jobId
      setScanProgress(data.progress)

      // Poll for progress
      let consecutiveErrors = 0
      const pollInterval = setInterval(async () => {
        try {
          console.log('[Rescan] Polling job', jobId)
          const pollResponse = await fetch('/api/rescan-document-compliance', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ jobId })
          })

          if (!pollResponse.ok) {
            // 504 timeouts are expected (each doc takes ~35s)
            // Don't fail the whole job, just skip this poll and try again
            if (pollResponse.status === 504) {
              console.log('[Rescan] Poll timeout (504) - document still processing, will retry...')
              consecutiveErrors = 0 // Reset error counter for timeouts
              return // Continue polling
            }

            consecutiveErrors++
            if (consecutiveErrors > 5) {
              clearInterval(pollInterval)
              throw new Error(`Poll failed after 5 attempts: ${pollResponse.status}`)
            }
            return // Continue polling
          }

          consecutiveErrors = 0 // Reset on success
          const pollData = await pollResponse.json()
          console.log('[Rescan] Progress:', pollData.progress)

          setScanProgress(pollData.progress)

          // Check if job is complete
          if (pollData.status === 'completed') {
            clearInterval(pollInterval)
            console.log('[Rescan] Job completed!', pollData.results)

            setRescanResults(pollData.results)
            setShowRescanResults(true)
            setRescanningAll(false)

            // Refresh documents
            await fetchAllData()
            triggerContentRefresh()
          }
        } catch (pollError) {
          // Network errors or JSON parse errors
          consecutiveErrors++
          console.error('[Rescan] Poll error:', pollError)

          if (consecutiveErrors > 5) {
            clearInterval(pollInterval)
            setRescanningAll(false)
            alert('Scan failed after multiple errors. Check console for details.')
          }
          // Otherwise continue polling
        }
      }, 5000) // Poll every 5 seconds (slower to reduce load)

    } catch (err) {
      console.error('[Rescan] Error:', err)
      alert('Failed to start compliance scan. Check console for details.')
      setRescanningAll(false)
    }
  }

  const handleRescanDocument = async (docId: string, docTitle: string) => {
    if (!confirm(`Re-scan compliance for "${docTitle}"?\n\nThis will use AI to analyze the document (~$0.075 cost).`)) {
      return
    }

    setRescanningDocId(docId)
    try {
      const response = await fetch('/api/rescan-document-compliance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: docId })
      })

      if (!response.ok) {
        throw new Error('Failed to re-scan document')
      }

      const data = await response.json()

      // Show brief results
      if (data.results.scanned > 0) {
        const detail = data.results.details[0]
        alert(`✅ Re-scan complete!\n\nFrameworks found: ${detail.frameworksFound || 0}\nCost: $${detail.cost?.toFixed(4) || '0.0000'}`)
      } else if (data.results.skipped > 0) {
        alert(`⚠️ Document skipped: ${data.results.details[0].reason}`)
      } else {
        alert('❌ Re-scan failed. Check console for details.')
      }

      // Refresh documents to show updated compliance data
      await fetchAllData()
      triggerContentRefresh()

    } catch (err) {
      console.error('Re-scan error:', err)
      alert('Failed to re-scan document. Check console for details.')
    } finally {
      setRescanningDocId(null)
    }
  }

  const handleUnarchiveDocument = async (docId: string) => {
    try {
      // Optimistic update
      setDocuments(prevDocs => prevDocs.map(doc =>
        doc.id === docId
          ? { ...doc, archived: false, archivedAt: undefined, archivedBy: undefined }
          : doc
      ))

      const response = await fetch(`/api/documents/${docId}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
      triggerContentRefresh()
        throw new Error('Failed to unarchive document')
      }

      alert('Document unarchived successfully!')

    } catch (err) {
      console.error('Unarchive error:', err)
      alert('Failed to unarchive document')
    }
  }

  const handleRestoreDocument = async (docId: string) => {
    // Store original state before optimistic update
    const originalDoc = documents.find(doc => doc.id === docId)

    try {
      console.log('🔄 Starting restore for document:', docId)

      // Optimistic update - update UI immediately
      setDocuments(prevDocs => prevDocs.map(doc =>
        doc.id === docId
          ? { ...doc, deleted: false, deletedAt: undefined, deletedBy: undefined }
          : doc
      ))
      console.log('⚡ Optimistic update applied')

      const response = await fetch(`/api/documents/${docId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        // Revert specific item's state instead of full refresh
        if (originalDoc) {
          setDocuments(prevDocs => prevDocs.map(doc =>
            doc.id === docId ? originalDoc : doc
          ))
        }
        triggerContentRefresh()
        throw new Error('Failed to restore document')
      }

      console.log('✅ Document restored on server')
      alert('Document restored successfully!')

    } catch (err) {
      console.error('Restore error:', err)
      alert('Failed to restore document')
    }
  }

  const handlePermanentDelete = async (docId: string) => {
    if (!confirm('⚠️ PERMANENT DELETE\n\nThis will permanently delete the document and cannot be undone.\n\nAre you absolutely sure?')) return

    try {
      const response = await fetch(`/api/documents/${docId}/permanent`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to permanently delete document')
      }

      // Refresh documents
      await fetchAllData()
      triggerContentRefresh()
      setTimeout(() => alert('Document permanently deleted!'), 100)

    } catch (err) {
      console.error('Permanent delete error:', err)
      alert('Failed to permanently delete document')
    }
  }

  const handleEmptyRecycleBin = async () => {
    const deletedDocs = documents.filter(d => d && d.deleted)

    if (deletedDocs.length === 0) {
      alert('Recycle bin is already empty')
      return
    }

    if (!confirm(`⚠️ EMPTY RECYCLE BIN\n\nThis will permanently delete ${deletedDocs.length} document(s) and cannot be undone.\n\nAre you absolutely sure?`)) return

    try {
      // Delete all documents in recycle bin
      const deletePromises = deletedDocs.map(doc =>
        fetch(`/api/documents/${doc.id}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      )

      await Promise.all(deletePromises)

      // Refresh documents
      await fetchAllData()
      triggerContentRefresh()
      setTimeout(() => alert(`Recycle bin emptied! ${deletedDocs.length} document(s) permanently deleted.`), 100)

    } catch (err) {
      console.error('Empty recycle bin error:', err)
      alert('Failed to empty recycle bin')
    }
  }

  const handleDownloadDocument = async (docId: string, title: string) => {
    try {
      const response = await fetch(`/api/download-document?id=${docId}`)
      if (!response.ok) {
        throw new Error('Failed to download document')
      }

      // Get file extension from Content-Type header
      const contentType = response.headers.get('Content-Type') || 'application/pdf'
      let extension = '.pdf' // default
      if (contentType.includes('text/markdown')) {
        extension = '.md'
      } else if (contentType.includes('application/pdf')) {
        extension = '.pdf'
      } else if (contentType.includes('application/json')) {
        extension = '.json'
      } else if (contentType.includes('text/plain')) {
        extension = '.txt'
      }

      // Add extension if not already present
      const filename = title.endsWith(extension) ? title : `${title}${extension}`

      // Create blob and download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Download error:', err)
      alert('Failed to download document')
    }
  }

  const handleViewDocument = (docId: string) => {
    // Open document in new tab for viewing
    window.open(`/api/download-document?id=${docId}`, '_blank')
  }

  const toggleAdminDocSelection = (docId: string) => {
    const newSelected = new Set(selectedAdminDocs)
    if (newSelected.has(docId)) {
      newSelected.delete(docId)
    } else {
      newSelected.add(docId)
    }
    setSelectedAdminDocs(newSelected)
  }

  const selectAllAdminDocs = () => {
    const visibleDocs = getFilteredAndSortedDocuments()
    const allDocIds = new Set(visibleDocs.map(doc => doc.id).filter(id => id))
    setSelectedAdminDocs(allDocIds)
  }

  const deselectAllAdminDocs = () => {
    setSelectedAdminDocs(new Set())
  }

  const handleAdminBulkDownload = async () => {
    if (selectedAdminDocs.size === 0) {
      alert('Please select documents to download')
      return
    }

    setAdminBulkDownloading(true)

    try {
      const response = await fetch('/api/admin/bulk-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          documentIds: Array.from(selectedAdminDocs)
        })
      })

      if (!response.ok) {
        throw new Error(`Bulk download failed: ${response.statusText}`)
      }

      // Get the ZIP file
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `admin_documents_${new Date().toISOString().slice(0, 10)}.zip`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Create and trigger download
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()

      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      // Show success message
      const docsCount = response.headers.get('X-Documents-Count') || selectedAdminDocs.size
      alert(`✓ Successfully downloaded ${docsCount} document(s) as ZIP`)

      // Clear selection
      setSelectedAdminDocs(new Set())

    } catch (error) {
      console.error('Admin bulk download error:', error)
      alert(`Failed to download documents: ${error.message}`)
    } finally {
      setAdminBulkDownloading(false)
    }
  }

  const handleEditDocument = (doc: any) => {
    console.log('===========================================')
    console.log('📝 EDIT BUTTON CLICKED - handleEditDocument called')
    console.log('📝 Opening edit for document:', doc.id, doc.title)
    console.log('📝 Document redacted value:', doc.redacted)
    console.log('📝 Document allowedPages value:', doc.allowedPages)
    console.log('📝 Document maxPages value:', doc.maxPages)
    console.log('📝 Full document object:', JSON.stringify(doc, null, 2))
    console.log('===========================================')

    setEditingDoc(doc)
    setEditForm({
      title: doc.title,
      category: doc.category,
      documentType: doc.documentType,
      gated: doc.gated || false,
      ndaRequired: doc.ndaRequired || false,
      watermarked: doc.watermarked || false,
      redacted: doc.redacted || false,
      allowedPages: doc.allowedPages || doc.maxPages?.toString() || '5', // Backwards compatible with maxPages
      visibility: doc.visibility || 'admin',
      hidden: doc.hidden || false,
      productIds: doc.productIds || []
    })
  }

  const handleSaveEdit = async () => {
    if (!editingDoc || !currentUser) return

    try {
      const payload = {
        documentId: editingDoc.id,
        updates: {
          ...editForm,
          updatedBy: currentUser.email
        }
      }
      console.log('📤 Sending document update:', payload)
      console.log('📤 Update includes redacted:', payload.updates.redacted)
      console.log('📤 Update includes allowedPages:', payload.updates.allowedPages)

      const response = await fetch(`/api/documents/${editingDocument.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload.updates)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Update failed:', response.status, errorText)
        throw new Error(`Failed to update document: ${response.status} ${errorText}`)
      }

      const result = await response.json()
      console.log('Update successful:', result)
      console.log('📥 Returned document redacted value:', result.document?.redacted)
      console.log('📥 Returned document allowedPages value:', result.document?.allowedPages)

      // Refresh documents
      await fetchAllData()
      triggerContentRefresh()
      setEditingDoc(null)
      setTimeout(() => alert('Document updated successfully!'), 100)
    } catch (err) {
      console.error('Update error:', err)
      alert(`Failed to update document: ${err.message}`)
    }
  }

  const handleCancelEdit = () => {
    setEditingDoc(null)
    setEditForm({
      title: '',
      category: 'certifications',
      documentType: undefined,
      gated: true,
      ndaRequired: true,
      watermarked: true,
      redacted: false,
      allowedPages: '5',
      visibility: 'gated',
      hidden: false,
      productIds: []
    })
  }

  const handleEditMarkdown = async (doc: any) => {
    try {
      const response = await fetch(`/api/policy-generator/edit-published/${doc.id}`)
      if (!response.ok) {
        throw new Error('Failed to fetch markdown')
      }

      const data = await response.json()
      setMarkdownContent(data.markdown)
      setEditingMarkdown({ doc, markdown: data.markdown })
    } catch (err) {
      console.error('Error fetching markdown:', err)
      alert('Failed to load markdown content')
    }
  }

  const handleSaveMarkdown = async () => {
    if (!editingMarkdown || !currentUser) return

    setSavingMarkdown(true)
    try {
      const response = await fetch(`/api/policy-generator/edit-published/${editingMarkdown.doc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markdown: markdownContent })
      })

      if (!response.ok) {
        throw new Error('Failed to save markdown')
      }

      const result = await response.json()

      // Refresh documents
      await fetchAllData()
      triggerContentRefresh()
      setEditingMarkdown(null)
      setMarkdownContent('')

      setTimeout(() => alert(`Policy updated successfully! New version: ${result.version}`), 100)
    } catch (err) {
      console.error('Save markdown error:', err)
      alert('Failed to save markdown')
    } finally {
      setSavingMarkdown(false)
    }
  }

  const handleCancelMarkdownEdit = () => {
    setEditingMarkdown(null)
    setMarkdownContent('')
  }

  // Badge management functions
  const handleBadgeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentUser) return

    try {
      // Use proper REST endpoints: POST for create, PUT for update
      const isUpdate = !!editingBadge?.id
      const url = isUpdate ? `/api/badges/${editingBadge.id}` : '/api/badges'
      const method = isUpdate ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(badgeForm)
      })

      if (!response.ok) {
        throw new Error('Failed to save badge')
      }

      // Reset form and refresh badges
      setBadgeForm({
        name: '',
        icon: 'Shield',
        status: '',
        year: new Date().getFullYear().toString(),
        description: '',
        reportAvailable: false,
        reportUrl: '',
        isActive: true,
        productIds: []
      })
      setEditingBadge(null)
      await fetchAllData()
      triggerContentRefresh()

      setTimeout(() => alert('Badge saved successfully!'), 100)
    } catch (err) {
      console.error('Badge save error:', err)
      alert('Failed to save badge')
    }
  }

  const handleEditBadge = (badge: any) => {
    setEditingBadge(badge)
    setBadgeForm({
      name: badge.name,
      icon: badge.icon,
      status: badge.status,
      year: badge.year,
      description: badge.description,
      reportAvailable: badge.reportAvailable || false,
      reportUrl: badge.reportUrl || '',
      isActive: badge.isActive !== false,
      productIds: badge.productIds || []
    })
  }

  const handleDeleteBadge = async (badgeId: string) => {
    if (!confirm('Are you sure you want to move this badge to the recycle bin?')) return
    if (!currentUser) return

    try {
      // Optimistic update
      const now = new Date().toISOString()
      setBadges(prevBadges => prevBadges.map(badge =>
        badge.id === badgeId
          ? { ...badge, deleted: true, deletedAt: now, deletedBy: currentUser.email }
          : badge
      ))

      const response = await fetch(`/api/badges/${badgeId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
        triggerContentRefresh()
        throw new Error('Failed to delete badge')
      }

      alert('Badge moved to recycle bin!')
    } catch (err) {
      console.error('Badge delete error:', err)
      alert('Failed to delete badge')
    }
  }

  const handleArchiveBadge = async (badgeId: string) => {
    if (!currentUser) return

    try {
      // Optimistic update
      const now = new Date().toISOString()
      setBadges(prevBadges => prevBadges.map(badge =>
        badge.id === badgeId
          ? { ...badge, archived: true, archivedAt: now, archivedBy: currentUser.email }
          : badge
      ))

      const response = await fetch(`/api/badges/${badgeId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
        triggerContentRefresh()
        throw new Error('Failed to archive badge')
      }

      alert('Badge archived successfully!')
    } catch (err) {
      console.error('Archive error:', err)
      alert('Failed to archive badge')
    }
  }

  const handleUnarchiveBadge = async (badgeId: string) => {
    try {
      // Optimistic update
      setBadges(prevBadges => prevBadges.map(badge =>
        badge.id === badgeId
          ? { ...badge, archived: false, archivedAt: undefined, archivedBy: undefined }
          : badge
      ))

      const response = await fetch(`/api/badges/${badgeId}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
        triggerContentRefresh()
        throw new Error('Failed to unarchive badge')
      }

      alert('Badge unarchived successfully!')
    } catch (err) {
      console.error('Unarchive error:', err)
      alert('Failed to unarchive badge')
    }
  }

  const handleRestoreBadge = async (badgeId: string) => {
    try {
      // Optimistic update
      setBadges(prevBadges => prevBadges.map(badge =>
        badge.id === badgeId
          ? { ...badge, deleted: false, deletedAt: undefined, deletedBy: undefined }
          : badge
      ))

      const response = await fetch(`/api/badges/${badgeId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
        triggerContentRefresh()
        throw new Error('Failed to restore badge')
      }

      alert('Badge restored successfully!')
    } catch (err) {
      console.error('Restore error:', err)
      alert('Failed to restore badge')
    }
  }

  const handlePermanentDeleteBadge = async (badgeId: string) => {
    if (!confirm('⚠️ PERMANENT DELETE\n\nThis will permanently delete the badge and cannot be undone.\n\nAre you absolutely sure?')) return

    try {
      const response = await fetch(`/api/badges/${badgeId}/permanent`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to permanently delete badge')
      }

      await fetchAllData()
      triggerContentRefresh()
      alert('Badge permanently deleted!')
    } catch (err) {
      console.error('Permanent delete error:', err)
      alert('Failed to permanently delete badge')
    }
  }

  const handleEmptyBadgeRecycleBin = async () => {
    const deletedBadges = badges.filter((b: any) => b.deleted)

    if (deletedBadges.length === 0) {
      alert('Recycle bin is empty')
      return
    }

    if (!confirm(`⚠️ EMPTY RECYCLE BIN\n\nThis will permanently delete ${deletedBadges.length} badge(s) and cannot be undone.\n\nAre you absolutely sure?`)) return

    try {
      const deletePromises = deletedBadges.map((badge: any) =>
        fetch(`/api/badges/${badge.id}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      )

      await Promise.all(deletePromises)
      await fetchAllData()
      triggerContentRefresh()
      alert('Badge recycle bin emptied successfully!')
    } catch (err) {
      console.error('Empty recycle bin error:', err)
      alert('Failed to empty recycle bin')
    }
  }

  const handleCancelBadgeEdit = () => {
    setEditingBadge(null)
    setBadgeForm({
      name: '',
      icon: 'Shield',
      status: '',
      year: new Date().getFullYear().toString(),
      description: '',
      reportAvailable: false,
      reportUrl: '',
      isActive: true,
      productIds: []
    })
  }

  // Content Management Functions
  const handleSaveContent = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      let response

      // Route to appropriate REST endpoint based on content type
      const isUpdate = !!editingContent?.id
      const apiContentType = contentFormType || trustCenterSubTab

      if (apiContentType === 'badges') {
        const url = isUpdate ? `/api/badges/${editingContent.id}` : '/api/badges'
        const method = isUpdate ? 'PUT' : 'POST'
        response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else if (apiContentType === 'subprocessors') {
        const url = isUpdate ? `/api/subprocessors/${editingContent.id}` : '/api/subprocessors'
        const method = isUpdate ? 'PUT' : 'POST'
        response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else if (apiContentType === 'features') {
        const url = isUpdate ? `/api/security-features/${editingContent.id}` : '/api/security-features'
        const method = isUpdate ? 'PUT' : 'POST'
        response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else if (apiContentType === 'hero') {
        const url = isUpdate ? `/api/hero/${editingContent.id}` : '/api/hero'
        const method = isUpdate ? 'PUT' : 'POST'
        response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else if (apiContentType === 'settings') {
        response = await fetch('/api/settings-db', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else if (apiContentType === 'products') {
        const url = isUpdate ? `/api/products/${editingContent.id}` : '/api/products'
        const method = isUpdate ? 'PUT' : 'POST'
        response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contentForm)
        })
      } else {
        throw new Error(`Unknown content type: ${apiContentType}`)
      }

      if (!response.ok) throw new Error('Failed to save content')

      await fetchAllData()
      triggerContentRefresh()
      setShowContentForm(false)
      setEditingContent(null)
      setContentForm({})
      setContentFormType(null)
      setTimeout(() => alert('Content saved successfully!'), 100)
    } catch (err) {
      console.error('Content save error:', err)
      alert('Failed to save content')
    }
  }

  const handleEditContent = (item: any) => {
    setEditingContent(item)
    setContentForm(item)
    // Detect if this is settings based on the item having an id of 'settings-main'
    if (item && item.id === 'settings-main') {
      setContentFormType('settings')
    }
    setShowContentForm(true)
  }

  const handleDeleteContent = async (itemId: string) => {
    console.log('🗑️ handleDeleteContent called with:', itemId, 'currentUser:', currentUser?.email, 'subTab:', trustCenterSubTab)
    if (!currentUser) {
      console.error('🗑️ No currentUser - cannot delete')
      return
    }

    // For subprocessors (nested inside 'features' tab), do soft delete (move to recycle bin)
    if (trustCenterSubTab === 'features' && (contentData.subprocessors || []).some((s: any) => s.id === itemId)) {
      console.log('🗑️ Delete subprocessor clicked:', itemId)
      if (!confirm('Are you sure you want to move this subprocessor to the recycle bin?')) {
        console.log('🗑️ Delete cancelled by user')
        return
      }

      try {
        console.log('🗑️ Sending delete request for subprocessor:', itemId)
        const now = new Date().toISOString()
        const response = await fetch(`/api/subprocessors/${itemId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })

        if (!response.ok) {
          console.error('🗑️ Delete API failed:', response.status, response.statusText)
          throw new Error('Failed to delete subprocessor')
        }

        console.log('🗑️ Delete successful, updating local state to mark as deleted')
        // Mark subprocessor as deleted in local state (don't remove it - it needs to appear in Recycle Bin)
        setContentData((prev: any) => ({
          ...prev,
          subprocessors: prev.subprocessors.map((s: any) =>
            s.id === itemId
              ? { ...s, deleted: true, deletedAt: now, deletedBy: currentUser.email }
              : s
          )
        }))

        console.log('🗑️ Subprocessor marked as deleted, UI should update immediately')
        alert('Subprocessor moved to recycle bin!')
      } catch (err) {
        console.error('🗑️ Delete error:', err)
        alert('Failed to delete subprocessor')
      }
      return
    }

    // For other content types, do permanent delete
    if (!confirm('Are you sure you want to delete this item?')) return
    try {
      let response

      // Route to appropriate REST endpoint based on content type
      if (trustCenterSubTab === 'features' || contentType === 'features') {
        response = await fetch(`/api/security-features/${itemId}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      } else if (trustCenterSubTab === 'hero' || contentType === 'hero') {
        response = await fetch(`/api/hero/${itemId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      } else if (trustCenterSubTab === 'products' || contentType === 'products') {
        response = await fetch(`/api/products/${itemId}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      } else {
        throw new Error(`Unknown content type: ${contentType || trustCenterSubTab}`)
      }

      if (!response.ok) throw new Error('Failed to delete content')

      await fetchAllData()
      triggerContentRefresh()
      setTimeout(() => alert('Content deleted successfully!'), 100)
    } catch (err) {
      console.error('Content delete error:', err)
      alert('Failed to delete content')
    }
  }

  const handleArchiveSubprocessor = async (itemId: string) => {
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      const response = await fetch(`/api/subprocessors/${itemId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to archive subprocessor')
      }

      // Mark as archived in local state (don't remove - needs to appear in Archived tab)
      setContentData((prev: any) => ({
        ...prev,
        subprocessors: prev.subprocessors.map((s: any) =>
          s.id === itemId
            ? { ...s, archived: true, archivedAt: now, archivedBy: currentUser.email }
            : s
        )
      }))

      alert('Subprocessor archived successfully!')
    } catch (err) {
      console.error('Archive error:', err)
      alert('Failed to archive subprocessor')
    }
  }

  const handleUnarchiveSubprocessor = async (itemId: string) => {
    try {
      const response = await fetch(`/api/subprocessors/${itemId}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to unarchive subprocessor')
      }

      // Unmark as archived in local state
      setContentData((prev: any) => ({
        ...prev,
        subprocessors: prev.subprocessors.map((s: any) =>
          s.id === itemId
            ? { ...s, archived: false, archivedAt: undefined, archivedBy: undefined }
            : s
        )
      }))

      alert('Subprocessor unarchived successfully!')
    } catch (err) {
      console.error('Unarchive error:', err)
      alert('Failed to unarchive subprocessor')
    }
  }

  const handleRestoreSubprocessor = async (itemId: string) => {
    try {
      const response = await fetch(`/api/subprocessors/${itemId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to restore subprocessor')
      }

      // Unmark as deleted in local state
      setContentData((prev: any) => ({
        ...prev,
        subprocessors: prev.subprocessors.map((s: any) =>
          s.id === itemId
            ? { ...s, deleted: false, deletedAt: undefined, deletedBy: undefined }
            : s
        )
      }))

      alert('Subprocessor restored successfully!')
    } catch (err) {
      console.error('Restore error:', err)
      alert('Failed to restore subprocessor')
    }
  }

  const handlePermanentDeleteSubprocessor = async (itemId: string) => {
    if (!confirm('⚠️ PERMANENT DELETE\n\nThis will permanently delete the subprocessor and cannot be undone.\n\nAre you absolutely sure?')) return

    try {
      const response = await fetch(`/api/subprocessors/${itemId}/permanent`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to permanently delete subprocessor')

      await fetchAllData()
      triggerContentRefresh()
      setTimeout(() => alert('Subprocessor permanently deleted!'), 100)
    } catch (err) {
      console.error('Permanent delete error:', err)
      alert('Failed to permanently delete subprocessor')
    }
  }

  const handleEmptySubprocessorRecycleBin = async () => {
    const deletedSubprocessors = (contentData.subprocessors || []).filter((s: any) => s.deleted)

    if (deletedSubprocessors.length === 0) {
      alert('Recycle bin is already empty')
      return
    }

    if (!confirm(`⚠️ EMPTY RECYCLE BIN\n\nThis will permanently delete ${deletedSubprocessors.length} subprocessor(s) and cannot be undone.\n\nAre you absolutely sure?`)) return

    try {
      const deletePromises = deletedSubprocessors.map((sub: any) =>
        fetch(`/api/subprocessors/${sub.id}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      )

      await Promise.all(deletePromises)

      await fetchAllData()
      triggerContentRefresh()
      setTimeout(() => alert(`Recycle bin emptied! ${deletedSubprocessors.length} subprocessor(s) permanently deleted.`), 100)
    } catch (err) {
      console.error('Empty recycle bin error:', err)
      alert('Failed to empty recycle bin')
    }
  }

  // Site scraping handlers
  const normalizeUrl = (url: string): string => {
    let normalized = url.trim()
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = `https://${normalized}`
    }
    return normalized
  }

  const handleScrapePreview = async () => {
    if (!scrapeUrl) {
      setScrapeError('Please enter a URL to scrape')
      return
    }

    setScraping(true)
    setScrapeError(null)
    setScrapeResult(null)

    try {
      const normalizedUrl = normalizeUrl(scrapeUrl)
      const response = await fetch('/api/scrape-site-config', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ url: normalizedUrl, applySettings: false })
      })

      if (!response.ok) {
        throw new Error('Failed to scrape site')
      }

      const data = await response.json()
      setScrapeResult(data.result)
    } catch (error) {
      console.error('Scrape error:', error)
      setScrapeError(error instanceof Error ? error.message : 'Failed to scrape site')
    } finally {
      setScraping(false)
    }
  }

  const handleScrapeApply = async () => {
    if (!scrapeUrl) {
      setScrapeError('Please enter a URL to scrape')
      return
    }

    if (!confirm('This will apply the scraped settings to your trust center configuration. Continue?')) {
      return
    }

    setScraping(true)
    setScrapeError(null)

    try {
      const normalizedUrl = normalizeUrl(scrapeUrl)
      const response = await fetch('/api/scrape-site-config', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ url: normalizedUrl, applySettings: true })
      })

      if (!response.ok) {
        throw new Error('Failed to apply settings')
      }

      const data = await response.json()
      setScrapeResult(data.result)
      alert('Settings applied successfully! Refreshing...')
      await fetchAllData()
      triggerContentRefresh()
    } catch (error) {
      console.error('Apply settings error:', error)
      setScrapeError(error instanceof Error ? error.message : 'Failed to apply settings')
    } finally {
      setScraping(false)
    }
  }

  // CSV Upload Handler
  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setCsvFile(file)

    // Parse CSV
    const text = await file.text()
    const lines = text.split('\n').filter(line => line.trim())

    if (lines.length < 2) {
      alert('CSV file must have at least a header row and one data row')
      return
    }

    // Parse header
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

    // Expected headers: name, type, purpose, location, product (optional)
    const requiredHeaders = ['name', 'type', 'purpose', 'location']
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))

    if (missingHeaders.length > 0) {
      alert(`CSV missing required columns: ${missingHeaders.join(', ')}\n\nRequired: name, type, purpose, location\nOptional: product, dataprocessed, privacyurl, securityurl, certifications`)
      return
    }

    // Parse rows
    const preview = lines.slice(1).map((line, idx) => {
      const values = line.split(',').map(v => v.trim())
      const row: any = {}

      headers.forEach((header, i) => {
        row[header] = values[i] || ''
      })

      return {
        rowNumber: idx + 2,
        name: row.name || 'Unnamed',
        type: row.type || 'Data Processor',
        purpose: row.purpose || '',
        location: row.location || 'Unknown',
        dataProcessed: row.dataprocessed || '',
        privacyUrl: row.privacyurl || '',
        securityUrl: row.securityurl || '',
        certifications: row.certifications || '',
        product: row.product || ''
      }
    })

    setCsvPreview(preview)
  }

  const handleCsvImport = async () => {
    if (csvPreview.length === 0) {
      alert('No data to import')
      return
    }

    try {
      // Get products for mapping
      const products = contentData.products || []

      // Import each subprocessor
      for (const row of csvPreview) {
        // Find product ID if specified
        let productIds: string[] = []
        if (row.product) {
          const product = products.find((p: any) =>
            p.name.toLowerCase() === row.product.toLowerCase()
          )
          if (product) {
            productIds = [product.id]
          }
        }

        const subprocessorData = {
          name: row.name,
          type: row.type,
          purpose: row.purpose,
          dataProcessed: row.dataProcessed,
          location: row.location,
          privacyUrl: row.privacyUrl,
          securityUrl: row.securityUrl,
          certifications: row.certifications,
          productIds
        }

        await fetch('/api/subprocessors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(subprocessorData)
        })
      }

      await fetchAllData()
      triggerContentRefresh()
      setShowCsvUploadModal(false)
      setCsvFile(null)
      setCsvPreview([])
      alert(`Successfully imported ${csvPreview.length} subprocessor(s)!`)
    } catch (err) {
      console.error('CSV import error:', err)
      alert('Failed to import CSV. Please check the format and try again.')
    }
  }

  // URL Fetch Handler
  const handleUrlFetch = async () => {
    if (!urlFetchInput.trim()) {
      alert('Please enter a URL')
      return
    }

    setIsFetchingUrl(true)

    try {
      // Fetch the URL
      const response = await fetch(urlFetchInput)
      if (!response.ok) {
        throw new Error(`Failed to fetch URL: ${response.statusText}`)
      }

      const text = await response.text()

      // Try to parse as JSON first
      let subprocessors: any[] = []

      try {
        const json = JSON.parse(text)

        // Handle different JSON structures
        if (Array.isArray(json)) {
          subprocessors = json
        } else if (json.subprocessors && Array.isArray(json.subprocessors)) {
          subprocessors = json.subprocessors
        } else if (json.vendors && Array.isArray(json.vendors)) {
          subprocessors = json.vendors
        } else {
          throw new Error('Unrecognized JSON structure')
        }
      } catch {
        // If not JSON, try CSV
        const lines = text.split('\n').filter(line => line.trim())
        if (lines.length < 2) {
          throw new Error('Invalid format: not JSON or CSV')
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
        subprocessors = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim())
          const row: any = {}
          headers.forEach((header, i) => {
            row[header] = values[i] || ''
          })
          return row
        })
      }

      // Get products for mapping
      const products = contentData.products || []
      let productIds: string[] = []
      if (urlFetchProduct) {
        productIds = [urlFetchProduct]
      }

      // Import each subprocessor
      let imported = 0
      for (const item of subprocessors) {
        const subprocessorData = {
          name: item.name || item.vendor || item.company || 'Unnamed',
          type: item.type || item.category || 'Data Processor',
          purpose: item.purpose || item.description || item.services || '',
          dataProcessed: item.dataProcessed || item.data || '',
          location: item.location || item.region || item.country || 'Unknown',
          privacyUrl: item.privacyUrl || item.privacy || item.privacyPolicy || '',
          securityUrl: item.securityUrl || item.security || '',
          certifications: item.certifications || item.compliance || '',
          productIds
        }

        const response = await fetch('/api/subprocessors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(subprocessorData)
        })

        if (response.ok) imported++
      }

      await fetchAllData()
      triggerContentRefresh()
      setShowUrlFetchModal(false)
      setUrlFetchInput('')
      setUrlFetchProduct('')
      alert(`Successfully imported ${imported} subprocessor(s) from URL!`)
    } catch (err) {
      console.error('URL fetch error:', err)
      alert(`Failed to fetch from URL: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsFetchingUrl(false)
    }
  }

  // Security Features handlers
  const handleArchiveFeature = async (itemId: string) => {
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      setContentData((prev: any) => ({
        ...prev,
        features: prev.features.map((f: any) =>
          f.id === itemId
            ? { ...f, archived: true, archivedAt: now, archivedBy: currentUser.email }
            : f
        )
      }))

      const response = await fetch(`/api/security-features/${itemId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
      triggerContentRefresh()
        throw new Error('Failed to archive feature')
      }

      alert('Feature archived successfully!')
    } catch (err) {
      console.error('Archive error:', err)
      alert('Failed to archive feature')
    }
  }

  const handleUnarchiveFeature = async (itemId: string) => {
    try {
      setContentData((prev: any) => ({
        ...prev,
        features: prev.features.map((f: any) =>
          f.id === itemId
            ? { ...f, archived: false, archivedAt: undefined, archivedBy: undefined }
            : f
        )
      }))

      const response = await fetch(`/api/security-features/${itemId}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
      triggerContentRefresh()
        throw new Error('Failed to unarchive feature')
      }

      alert('Feature unarchived successfully!')
    } catch (err) {
      console.error('Unarchive error:', err)
      alert('Failed to unarchive feature')
    }
  }

  const handleDeleteFeature = async (itemId: string) => {
    if (!confirm('Are you sure you want to move this feature to the recycle bin?')) return
    if (!currentUser) return

    try {
      const now = new Date().toISOString()
      setContentData((prev: any) => ({
        ...prev,
        features: prev.features.map((f: any) =>
          f.id === itemId
            ? { ...f, deleted: true, deletedAt: now, deletedBy: currentUser.email }
            : f
        )
      }))

      const response = await fetch(`/api/security-features/${itemId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
      triggerContentRefresh()
        throw new Error('Failed to delete feature')
      }

      alert('Feature moved to recycle bin!')
    } catch (err) {
      console.error('Delete error:', err)
      alert('Failed to delete feature')
    }
  }

  const handleRestoreFeature = async (itemId: string) => {
    try {
      setContentData((prev: any) => ({
        ...prev,
        features: prev.features.map((f: any) =>
          f.id === itemId
            ? { ...f, deleted: false, deletedAt: undefined, deletedBy: undefined }
            : f
        )
      }))

      const response = await fetch(`/api/security-features/${itemId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        await fetchAllData()
      triggerContentRefresh()
        throw new Error('Failed to restore feature')
      }

      alert('Feature restored successfully!')
    } catch (err) {
      console.error('Restore error:', err)
      alert('Failed to restore feature')
    }
  }

  const handlePermanentDeleteFeature = async (itemId: string) => {
    if (!confirm('⚠️ PERMANENT DELETE\n\nThis will permanently delete the feature and cannot be undone.\n\nAre you absolutely sure?')) return

    try {
      const response = await fetch(`/api/security-features/${itemId}/permanent`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        throw new Error('Failed to permanently delete feature')
      }

      await fetchAllData()
      triggerContentRefresh()
      alert('Feature permanently deleted!')
    } catch (err) {
      console.error('Permanent delete error:', err)
      alert('Failed to permanently delete feature')
    }
  }

  const handleEmptyFeatureRecycleBin = async () => {
    const deletedFeatures = (contentData.features || []).filter((f: any) => f.deleted)

    if (deletedFeatures.length === 0) {
      alert('Recycle bin is empty')
      return
    }

    if (!confirm(`⚠️ EMPTY RECYCLE BIN\n\nThis will permanently delete ${deletedFeatures.length} feature(s) and cannot be undone.\n\nAre you absolutely sure?`)) return

    try {
      const deletePromises = deletedFeatures.map((feature: any) =>
        fetch(`/api/security-features/${feature.id}/permanent`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        })
      )

      await Promise.all(deletePromises)
      await fetchAllData()
      triggerContentRefresh()
      alert('Feature recycle bin emptied successfully!')
    } catch (err) {
      console.error('Empty recycle bin error:', err)
      alert('Failed to empty recycle bin')
    }
  }

  const handleNewContent = () => {
    if (trustCenterSubTab === 'settings') {
      // For settings, we always edit the existing settings or create new ones
      const existingSettings = contentData.settings
      setEditingContent(existingSettings)
      setContentForm(existingSettings || getDefaultContentForm())
    } else {
      setEditingContent(null)
      setContentForm(getDefaultContentForm())
    }
    setShowContentForm(true)
  }

  const getDefaultContentForm = () => {
    switch (contentType) {
      case 'subprocessors':
        return {
          name: '',
          purpose: '',
          dataTypes: [],
          region: '',
          type: '',
          dpaUrl: '',
          privacyUrl: '',
          lastReviewed: '',
          nextReview: '',
          status: 'active',
          isActive: true
        }
      case 'features':
        return {
          title: '',
          description: '',
          icon: 'Shield',
          details: [],
          category: 'security',
          displayOrder: 0,
          isActive: true
        }
      case 'hero':
        return {
          title: '',
          subtitle: '',
          description: '',
          stats: [],
          isActive: true
        }
      case 'badges':
        return {
          name: '',
          icon: 'Shield',
          status: '',
          year: new Date().getFullYear().toString(),
          description: '',
          reportAvailable: false,
          reportUrl: '',
          isActive: true
        }
      case 'settings':
        return {
          statusPageUrl: '/status',
          companyName: 'Netlify',
          supportEmail: 'support@netlify.com',
          securityEmail: 'security@netlify.com',
          privacyPolicyUrl: '',
          termsOfServiceUrl: '',
          cookiePolicyUrl: '',
          soc2ReportUrl: '',
          iso27001CertificateUrl: '',
          subprocessorsPageUrl: '',
          subprocessorsHeroTitle: '',
          subprocessorsHeroSubtitle: '',
          subprocessorsHeroDescription: '',
          companyLocation: '',
          corporateAddress: '',
          lastUpdatedText: '',
          viewFullListText: 'View Full List',
          primaryColor: '#2E51ED',
          secondaryColor: '#05BDBA',
          logoUrl: '',
          faviconUrl: '',
          brandName: '',
          isActive: true
        }
      default:
        return {}
    }
  }

  // Computed values
  const stats = {
    pendingRequests: requests.filter(r => r.status === 'pending').length,
    activeSessions: requests.filter(r => r.status === 'approved').length,
    totalRequests: requests.length,
    totalDocuments: documents.filter(d => d && !d.archived && !d.deleted).length,
    approvalRate: requests.length > 0 ? Math.round((requests.filter(r => r.status === 'approved').length / requests.length) * 100) : 0
  }

  const filteredRequests = requests.filter(request => {
    const matchesSearch = searchTerm === '' ||
      request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.name.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || request.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4 text-yellow-600" />
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'rejected': return <XCircle className="h-4 w-4 text-red-600" />
      default: return <AlertTriangle className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatFileSize = (bytes: number): string => {
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  // Loading state
  if (loginLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  // Handler for approving AI-generated content
  const handleAIContentApprove = async (content: any) => {
    try {
      if (aiGeneratorType === 'subprocessors') {
        if (Array.isArray(content)) {
          console.log(`💾 Batch saving ${content.length} AI-generated subprocessors...`)

          try {
            // Save each subprocessor individually
            const savePromises = content.map(subprocessor =>
              fetch('/api/subprocessors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: subprocessor.name,
                  type: subprocessor.service || 'Service Provider',
                  purpose: subprocessor.purpose || subprocessor.service,
                  dataTypes: subprocessor.dataTypes || (subprocessor.service ? [subprocessor.service] : ['General Services']),
                  region: subprocessor.location || '',
                  status: 'active',
                  productIds: []
                })
              })
            )

            const results = await Promise.allSettled(savePromises)
            const failures = results.filter(r => r.status === 'rejected')

            if (failures.length > 0) {
              console.error(`❌ Failed to save ${failures.length} subprocessor(s)`)
              alert(`Saved ${results.length - failures.length}/${results.length} subprocessors. ${failures.length} failed.`)
            } else {
              const savedCount = results.length
              console.log(`✅ Successfully saved ${savedCount} subprocessor(s)`)

              // Wait for blob store consistency before refreshing (Netlify Blobs eventual consistency)
              console.log('⏱️ Waiting for blob store consistency...')
              await new Promise(resolve => setTimeout(resolve, 2000))

              // Refresh data
              console.log('🔄 Refreshing data after AI content save...')
              await fetchAllData()
              triggerContentRefresh()

              alert(`Successfully added ${savedCount} subprocessor(s)!`)
            }
          } catch (error) {
            console.error(`❌ Error saving subprocessors:`, error)
            alert('Failed to save subprocessors. Please try again.')
          }
        }
      } else if (aiGeneratorType === 'features') {
        if (Array.isArray(content)) {
          console.log(`💾 Batch saving ${content.length} AI-generated features...`)

          try {
            // Save each feature individually
            const savePromises = content.map(feature =>
              fetch('/api/security-features', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: feature.title,
                  description: feature.description,
                  icon: feature.icon || 'Shield',
                  category: feature.category || 'security',
                  displayOrder: 0,
                  productIds: []
                })
              })
            )

            const results = await Promise.allSettled(savePromises)
            const successCount = results.filter(r => r.status === 'fulfilled').length
            const failCount = results.filter(r => r.status === 'rejected').length

            if (failCount > 0) {
              console.error(`❌ Failed to save ${failCount} features`)
              alert(`Saved ${successCount} features, but ${failCount} failed. Please try again.`)
            } else {
              console.log(`✅ Successfully saved ${successCount} feature(s)`)
              alert(`Successfully added ${successCount} feature(s)!`)
            }

            // Wait for blob store consistency before refreshing
            console.log('⏱️ Waiting for blob store consistency...')
            await new Promise(resolve => setTimeout(resolve, 500))

            // Refresh data
            console.log('🔄 Refreshing data after AI content save...')
            await fetchAllData()
            triggerContentRefresh()
          } catch (error) {
            console.error(`❌ Error saving features:`, error)
            alert('Failed to save features. Please try again.')
          }
        }
      } else if (aiGeneratorType === 'badges') {
        if (Array.isArray(content)) {
          console.log(`💾 Batch saving ${content.length} AI-generated compliance badges...`)

          try {
            // Save each badge individually
            const savePromises = content.map(badge =>
              fetch('/api/badges', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: badge.name || badge.title,
                  icon: badge.icon || 'Shield',
                  description: badge.description,
                  status: badge.status || 'Certified',
                  year: badge.year || new Date().getFullYear().toString(),
                  reportAvailable: badge.reportAvailable || false,
                  reportUrl: badge.reportUrl || ''
                })
              })
            )

            const results = await Promise.allSettled(savePromises)
            const successCount = results.filter(r => r.status === 'fulfilled').length
            const failCount = results.filter(r => r.status === 'rejected').length

            if (failCount > 0) {
              console.error(`❌ Failed to save ${failCount} badges`)
              alert(`Saved ${successCount} badges, but ${failCount} failed. Please try again.`)
            } else {
              console.log(`✅ Successfully saved ${successCount} compliance badge(s)`)
              alert(`Successfully added ${successCount} compliance badge(s)!`)
            }

            // Wait for blob store consistency before refreshing
            console.log('⏱️ Waiting for blob store consistency...')
            await new Promise(resolve => setTimeout(resolve, 500))

            // Refresh data
            console.log('🔄 Refreshing data after AI content save...')
            await fetchAllData()
            triggerContentRefresh()
          } catch (error) {
            console.error(`❌ Error saving compliance badges:`, error)
            alert('Failed to save compliance badges. Please try again.')
          }
        }
      } else if (aiGeneratorType === 'legal') {
        if (Array.isArray(content)) {
          console.log(`💾 Saving ${content.length} AI-generated legal content item(s)...`)

          try {
            let savedCount = 0
            for (const legalItem of content) {
              try {
                const response = await fetch('/api/admin/page-content', {
                  method: 'POST',
                  headers: getHeaders(),
                  body: JSON.stringify({
                    pageId: legalItem.pageId || 'terms',
                    content: legalItem,
                    adminEmail: currentUser?.email || 'admin'
                  })
                })
                if (response.ok) savedCount++
              } catch (err) {
                console.error('Error saving legal content:', err)
              }
            }

            // Wait for blob store consistency before refreshing
            console.log('⏱️ Waiting for blob store consistency...')
            await new Promise(resolve => setTimeout(resolve, 500))

            // Refresh data
            console.log('🔄 Refreshing data after AI content save...')
            await fetchAllData()
            triggerContentRefresh()

            alert(`Successfully added ${savedCount} legal content item(s)!`)
          } catch (error) {
            console.error(`❌ Error saving legal content:`, error)
            alert('Failed to save legal content. Please try again.')
          }
        }
      }
    } catch (err) {
      console.error('Error saving AI-generated content:', err)
      alert('Failed to save generated content')
    }
  }

  // User management functions
  const loadUserRoles = async () => {
    if (!currentTenant) return

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch(`/api/kb-user-roles?tenantId=${currentTenant.id}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${authToken || ''}` }
      })

      if (response.ok) {
        const data = await response.json()
        setUserRoles(data.roles || [])
      }
    } catch (error) {
      console.error('Error loading user roles:', error)
    }
  }

  const grantUserRole = async () => {
    if (!currentTenant || !newUserEmail) return

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/kb-user-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          userEmail: newUserEmail,
          role: newUserRole
        })
      })

      if (response.ok) {
        setNewUserEmail('')
        setNewUserRole('editor')
        loadUserRoles()
        alert('User role granted successfully')
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to grant role')
      }
    } catch (error) {
      console.error('Error granting role:', error)
      alert('Failed to grant role')
    }
  }

  const revokeUserRole = async (userEmail: string) => {
    if (!currentTenant) return

    if (!confirm(`Are you sure you want to revoke access for ${userEmail}?`)) {
      return
    }

    try {
      const authToken = localStorage.getItem('auth-token')
      const response = await fetch('/api/kb-user-roles', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || ''}`
        },
        credentials: 'include',
        body: JSON.stringify({
          tenantId: currentTenant.id,
          userEmail
        })
      })

      if (response.ok) {
        loadUserRoles()
        alert('User role revoked successfully')
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to revoke role')
      }
    } catch (error) {
      console.error('Error revoking role:', error)
      alert('Failed to revoke role')
    }
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Product Tour */}
      {isAuthenticated && activeTourType && (
        <ProductTour
          tourType={activeTourType}
          onComplete={activeTourType === 'admin' ? handleAdminTourComplete : handlePublishTourComplete}
          onSkip={handleTourSkip}
          onStepChange={handleTourStepChange}
          autoStart={true}
          initialStep={tourInitialStep}
        />
      )}

      {/* Contextual Tour Starter - only show when no tour is active and user is authenticated */}
      {isAuthenticated && !activeTourType && (
        <TourStarter
          context={{
            activeTab,
            trustCenterSubTab,
            knowledgeBaseTab,
            caiqSubTab,
            analyticsSubTab,
            settingsSubTab,
            tprmHash: window.location.hash
          }}
          onStartTour={handleStartContextualTour}
        />
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex items-center gap-2 sm:gap-4">
              {isPlatformAdminMode && urlTenantId && (
                <button
                  onClick={() => navigate('/platformadmin')}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                  title="Back to Platform Admin"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                    {displayTenant?.name || 'Trust Center Admin'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {currentUser?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-sm">
                  <div className="font-medium text-gray-900">{currentUser?.name}</div>
                  <div className="text-gray-500">{currentUser?.role}</div>
                </div>
              </div>

              {!urlTenantId && displayTenant && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-gray-100 rounded-lg">
                  <Building2 className="h-4 w-4 text-gray-600" />
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{displayTenant.name}</div>
                  </div>
                </div>
              )}

              <button
                onClick={fetchAllData}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Refresh data"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setShowAIAgentModal(true)}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="AI Assistant"
              >
                <Brain className="h-5 w-5" />
              </button>

              <button
                onClick={() => setShowNotificationsModal(true)}
                className="p-2 text-gray-400 hover:text-gray-600 relative"
                title="Notification settings"
              >
                <Bell className="h-5 w-5" />
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <LogOut className="h-5 w-5 mr-1" />
                <span className="text-sm">Logout</span>
              </button>

              <a href="/" className="text-netlify-600 hover:text-netlify-700">
                ← Back to Trust Center
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200" data-tour="navigation-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {[
              { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { key: 'trust-center', label: 'Trust Center', icon: Database },
              { key: 'knowledge-base', label: 'Knowledge Base', icon: Brain },
              { key: 'agents', label: 'Agents', icon: Bot },
              { key: 'skills', label: 'Skills & Rank', icon: TrendingUp },
              { key: 'associates', label: 'Associates', icon: Users },
              { key: 'analytics', label: 'Analytics', icon: Eye },
              { key: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => {
              const IconComponent = tab.icon
              return (
                <button
                  key={tab.key}
                  data-tour-tab={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as any)
                    window.location.hash = tab.key
                  }}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="ml-2 bg-red-100 text-red-600 text-xs font-medium px-2 py-1 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              )
            })}
          </nav>

          {/* Mobile Navigation - Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 pt-2 border-t border-gray-200">
              <nav className="flex flex-col space-y-1">
                {[
                  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                  { key: 'trust-center', label: 'Trust Center', icon: Database },
                  { key: 'knowledge-base', label: 'Knowledge Base', icon: Brain },
                  { key: 'agents', label: 'Agents', icon: Bot },
                  { key: 'skills', label: 'Skills & Rank', icon: TrendingUp },
                  { key: 'associates', label: 'Associates', icon: Users },
                  { key: 'analytics', label: 'Analytics', icon: Eye },
                  { key: 'settings', label: 'Settings', icon: Settings }
                ].map((tab) => {
                  const IconComponent = tab.icon
                  return (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key as any)
                        window.location.hash = tab.key
                        setMobileMenuOpen(false)
                      }}
                      className={`flex items-center justify-between px-4 py-3 rounded-md text-base font-medium ${
                        activeTab === tab.key
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center">
                        <IconComponent className="h-5 w-5 mr-3" />
                        {tab.label}
                      </div>
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className="bg-red-100 text-red-600 text-xs font-medium px-2 py-1 rounded-full">
                          {tab.count}
                        </span>
                      )}
                    </button>
                  )
                })}

                {/* Mobile Actions */}
                <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => {
                      fetchAllData()
                      setMobileMenuOpen(false)
                    }}
                    className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <RefreshCw className="h-5 w-5 mr-3" />
                    Refresh Data
                  </button>
                  <button
                    onClick={() => {
                      setShowNotificationsModal(true)
                      setMobileMenuOpen(false)
                    }}
                    className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <Bell className="h-5 w-5 mr-3" />
                    Notifications
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    Logout
                  </button>
                  <a
                    href="/"
                    className="w-full flex items-center px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-md"
                  >
                    ← Back to Trust Center
                  </a>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8" data-tour="dashboard-content">
            {/* AI Agent Assistant - Prominent at top */}
            {featureFlags.aiAgent && (
            <div className="bg-gradient-to-br from-primary-50 via-primary-100 to-white rounded-xl shadow-lg border-2 border-primary-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                    <Brain className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">AI Assistant</h2>
                    <p className="text-sm text-gray-600">Ask me anything about your trust center</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAIAgentModal(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Open Full Chat
                </button>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col space-y-3">
                  <p className="text-sm text-gray-700 font-medium">Quick Actions:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <button
                      onClick={() => {
                        setShowAIAgentModal(true)
                        // The modal will handle the prefilled message via initialContext
                      }}
                      className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-lg border border-blue-200 transition-all group"
                    >
                      <div className="text-2xl">📊</div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">KB Status</div>
                        <div className="text-xs text-gray-600">Check completion</div>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowAIAgentModal(true)
                      }}
                      className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-lg border border-green-200 transition-all group"
                    >
                      <div className="text-2xl">🏅</div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">Badges</div>
                        <div className="text-xs text-gray-600">View compliance</div>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowAIAgentModal(true)
                      }}
                      className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-lg border border-purple-200 transition-all group"
                    >
                      <div className="text-2xl">🔒</div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">Security</div>
                        <div className="text-xs text-gray-600">List features</div>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setShowAIAgentModal(true)
                      }}
                      className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 rounded-lg border border-orange-200 transition-all group"
                    >
                      <div className="text-2xl">🏢</div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">Vendors</div>
                        <div className="text-xs text-gray-600">View subprocessors</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                    Powered by Claude
                  </span>
                  <span className="flex items-center">
                    <Shield className="h-3 w-3 mr-1 text-primary-600" />
                    Secure & Private
                  </span>
                </div>
                <span className="text-gray-500">Click any action to start chatting</span>
              </div>
            </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button
                onClick={() => {
                  setActiveTab('trust-center')
                  setTrustCenterSubTab('visitors')
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer text-left"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-yellow-100 text-yellow-800">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingRequests}</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setActiveTab('analytics')
                  setAnalyticsSubTab('audit')
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer text-left"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-green-100 text-green-800">
                    <Users className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setActiveTab('trust-center')
                  setTrustCenterSubTab('documents')
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer text-left"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-blue-100 text-blue-800">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Documents</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalDocuments}</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setActiveTab('trust-center')
                  setTrustCenterSubTab('visitors')
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer text-left"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-purple-100 text-purple-800">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Approval Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.approvalRate}%</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <SectionHeading id="recent-requests" className="text-lg font-semibold text-gray-900">
                    Recent Requests
                  </SectionHeading>
                </div>
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="p-6 text-center">
                      <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Loading requests...</p>
                    </div>
                  ) : requests.length === 0 ? (
                    <div className="p-6 text-center">
                      <p className="text-sm text-gray-500">No requests found</p>
                    </div>
                  ) : (
                    requests.slice(0, 5).map((request) => (
                      <div key={request.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              {getStatusIcon(request.status)}
                              <div className="ml-2">
                                <p className="text-sm font-medium text-gray-900">{request.email}</p>
                                <p className="text-sm text-gray-600">{request.company}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                            <div className="flex items-center space-x-1 ml-2">
                              <button
                                onClick={() => {
                                  setHighlightedRequestId(request.id)
                                  setActiveTab('requests')
                                  setTimeout(() => {
                                    const element = document.getElementById(`request-${request.id}`)
                                    if (element) {
                                      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                    }
                                  }, 100)
                                }}
                                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleApproveRequest(request.id)}
                                className={`p-1.5 rounded transition-colors ${
                                  request.status === 'approved'
                                    ? 'text-green-600 bg-green-50'
                                    : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                                }`}
                                title="Approve request"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleRejectRequest(request.id)}
                                className={`p-1.5 rounded transition-colors ${
                                  request.status === 'rejected'
                                    ? 'text-red-600 bg-red-50'
                                    : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                                }`}
                                title="Deny request"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <SectionHeading id="quick-actions" className="text-lg font-semibold text-gray-900">
                    Quick Actions
                  </SectionHeading>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-4">
                    <button
                      onClick={() => {
                        setActiveTab('trust-center')
                        setTrustCenterSubTab('visitors')
                        setTimeout(() => {
                          window.location.hash = 'access-requests'
                        }, 100)
                      }}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                    >
                      <Clock className="h-8 w-8 text-primary-600" />
                      <div className="ml-4">
                        <h3 className="font-medium text-gray-900">Review Pending Requests</h3>
                        <p className="text-sm text-gray-500">{stats.pendingRequests} requests awaiting approval</p>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        setActiveTab('trust-center')
                        setTrustCenterSubTab('documents')
                        setTimeout(() => {
                          window.location.hash = 'active-documents'
                        }, 100)
                      }}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                    >
                      <Upload className="h-8 w-8 text-primary-600" />
                      <div className="ml-4">
                        <h3 className="font-medium text-gray-900">Manage Documents</h3>
                        <p className="text-sm text-gray-500">{stats.totalDocuments} documents in library</p>
                      </div>
                    </button>

                    {featureFlags.policyGenerator && (
                    <button
                      onClick={() => navigate('/admin/policy-generator')}
                      className="flex items-center p-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 hover:border-blue-400 hover:shadow-md transition-all text-left"
                    >
                      <Sparkles className="h-8 w-8 text-blue-600" />
                      <div className="ml-4">
                        <h3 className="font-medium text-gray-900">AI Policy Generator</h3>
                        <p className="text-sm text-gray-600">Generate compliance policies with AI</p>
                      </div>
                    </button>
                    )}

                    <button
                      onClick={() => setActiveTab('analytics')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                    >
                      <BarChart3 className="h-8 w-8 text-primary-600" />
                      <div className="ml-4">
                        <h3 className="font-medium text-gray-900">View Analytics</h3>
                        <p className="text-sm text-gray-500">Access usage metrics and reports</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Agents Tab */}
        {activeTab === 'agents' && (
          <div className="space-y-6">
            <AgentRegistrationForm
              tenantId={effectiveTenantId}
              getHeaders={getHeaders}
            />
          </div>
        )}

      {/* Skills & Rank Tab */}
        {activeTab === 'skills' && (
          <div className="space-y-6">
            <SkillsRankTab
              tenantId={effectiveTenantId}
              getHeaders={getHeaders}
            />
          </div>
        )}

      {/* Associates Tab */}
        {activeTab === 'associates' && (
          <div className="space-y-6">
            <AssociatesTab />
          </div>
        )}

      {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Sub-tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200 px-6">
                <nav className="flex space-x-8">
                  {[
                    { key: 'overview' as const, label: 'Overview', icon: BarChart3 },
                    // Feature Usage is only available in platform admin mode
                    ...(isPlatformAdminMode ? [{ key: 'feature-usage' as const, label: 'Feature Usage', icon: Activity }] : []),
                    { key: 'engagement' as const, label: 'Engagement Scoring', icon: Users },
                    { key: 'audit' as const, label: 'Audit Trail', icon: FileText },
                    { key: 'revenue' as const, label: 'Revenue Attribution', icon: DollarSign }
                  ].map((tab) => {
                    const IconComponent = tab.icon
                    return (
                      <button
                        key={tab.key}
                        data-tour-subtab={`analytics-${tab.key}`}
                        onClick={() => setAnalyticsSubTab(tab.key)}
                        className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                          analyticsSubTab === tab.key
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className="h-4 w-4 mr-2" />
                        {tab.label}
                      </button>
                    )
                  })}
                </nav>
              </div>

              <div className="p-6">
                {analyticsSubTab === 'overview' && (
                  <div>
                    {loading ? (
                      <div className="text-center py-8">
                        <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Loading analytics...</p>
                      </div>
                    ) : analytics ? (
                      <div className="space-y-6">
                        {/* Overview Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="analytics-overview">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-blue-900">Total Requests</h3>
                            <p className="text-2xl font-bold text-blue-600">{analytics.overview.totalRequests}</p>
                          </div>
                          <div className="bg-yellow-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-yellow-900">Pending</h3>
                            <p className="text-2xl font-bold text-yellow-600">{analytics.overview.pendingRequests}</p>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-green-900">Approved</h3>
                            <p className="text-2xl font-bold text-green-600">{analytics.overview.approvedRequests}</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-purple-900">Approval Rate</h3>
                            <p className="text-2xl font-bold text-purple-600">{analytics.overview.approvalRate}%</p>
                          </div>
                        </div>

                        {/* Top Requested Documents */}
                        <div className="bg-gray-50 rounded-lg p-6">
                          <SectionHeading id="top-documents" className="text-lg font-medium text-gray-900 mb-4">
                            Top Requested Documents
                          </SectionHeading>
                          <div className="space-y-2">
                            {Object.entries(analytics.topRequestedDocuments).map(([doc, count]) => (
                              <div key={doc} className="flex justify-between items-center">
                                <span className="text-sm text-gray-700">{doc}</span>
                                <span className="text-sm font-medium text-gray-900">{count} requests</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Requests by Role */}
                        <div className="bg-gray-50 rounded-lg p-6">
                          <SectionHeading id="requests-by-role" className="text-lg font-medium text-gray-900 mb-4">
                            Requests by Role
                          </SectionHeading>
                          <div className="space-y-2">
                            {Object.entries(analytics.requestsByRole).map(([role, count]) => (
                              <div key={role} className="flex justify-between items-center">
                                <span className="text-sm text-gray-700 capitalize">{role.replace('_', ' ')}</span>
                                <span className="text-sm font-medium text-gray-900">{count} requests</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600">Analytics data not available</p>
                    )}
                  </div>
                )}

                {analyticsSubTab === 'engagement' && (
                  <div data-tour="analytics-engagement">
                    <EngagementDashboard embedded />
                  </div>
                )}

                {analyticsSubTab === 'audit' && (
                  <div data-tour="analytics-audit">
                    <AuditTrailPage embedded />
                  </div>
                )}

                {analyticsSubTab === 'revenue' && (
                  <div data-tour="analytics-revenue">
                    <RevenueAttributionPage embedded />
                  </div>
                )}

                {analyticsSubTab === 'feature-usage' && (
                  <div data-tour="analytics-feature-usage">
                    <PlatformAnalytics />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && (
          <div className="space-y-6">
            {/* Badge Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingBadge ? 'Edit Badge' : 'Add New Badge'}
              </h2>

              <form onSubmit={handleBadgeSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge Name</label>
                    <input
                      type="text"
                      value={badgeForm.name}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                      placeholder="e.g., SOC 2 Type II"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                    <select
                      value={badgeForm.icon}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                    >
                      <option value="Shield">Shield</option>
                      <option value="Lock">Lock</option>
                      <option value="Eye">Eye</option>
                      <option value="Award">Award</option>
                      <option value="CheckCircle">CheckCircle</option>
                      <option value="FileCheck">FileCheck</option>
                      <option value="Globe">Globe</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <input
                      type="text"
                      value={badgeForm.status}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                      placeholder="e.g., Certified, Verified, Compliant"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <input
                      type="text"
                      value={badgeForm.year}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, year: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                      placeholder="e.g., 2024, Ongoing"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={badgeForm.description}
                    onChange={(e) => setBadgeForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                    placeholder="Brief description of this certification or compliance"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Report URL (Optional)</label>
                    <input
                      type="url"
                      value={badgeForm.reportUrl}
                      onChange={(e) => setBadgeForm(prev => ({ ...prev, reportUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 focus:border-netlify-500"
                      placeholder="/documents/soc2-report.pdf"
                    />
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={badgeForm.reportAvailable}
                        onChange={(e) => setBadgeForm(prev => ({ ...prev, reportAvailable: e.target.checked }))}
                        className="h-4 w-4 text-netlify-600 focus:ring-netlify-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Report Available</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={badgeForm.isActive}
                        onChange={(e) => setBadgeForm(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="h-4 w-4 text-netlify-600 focus:ring-netlify-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                  <p className="text-xs text-gray-500 mb-2">Select which products this badge applies to (leave empty for all products)</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {contentData.products && contentData.products.length > 0 ? (
                      contentData.products.map((product: any) => (
                        <label key={product.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(badgeForm.productIds || []).includes(product.id)}
                            onChange={(e) => {
                              const productIds = badgeForm.productIds || []
                              if (e.target.checked) {
                                setBadgeForm(prev => ({ ...prev, productIds: [...productIds, product.id] }))
                              } else {
                                setBadgeForm(prev => ({ ...prev, productIds: productIds.filter((id: string) => id !== product.id) }))
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {product.name}
                            {product.isDefault && <span className="ml-1 text-xs text-blue-600">(Default)</span>}
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No products available. Create products in Settings first.</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  {editingBadge && (
                    <button
                      type="button"
                      onClick={handleCancelBadgeEdit}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2 bg-netlify-600 text-white rounded-lg hover:bg-netlify-700 transition-colors inline-flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingBadge ? 'Update Badge' : 'Create Badge'}
                  </button>
                </div>
              </form>
            </div>

            {/* Badges List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <SectionHeading id="compliance-badges" className="text-lg font-semibold text-gray-900">
                    Compliance Badges
                  </SectionHeading>
                  {badgeTab === 'recyclebin' && badges.filter((b: any) => b.deleted).length > 0 && (
                    <button
                      onClick={handleEmptyBadgeRecycleBin}
                      className="px-3 py-1 text-sm border border-red-300 rounded-lg text-red-700 hover:bg-red-50"
                    >
                      Empty Recycle Bin
                    </button>
                  )}
                </div>

                {/* Product Filter */}
                <div className="mb-4 flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                  <select
                    value={badgeProductFilter}
                    onChange={(e) => setBadgeProductFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Products</option>
                    <option value="unassigned">Unassigned (No Products)</option>
                    {(contentData.products || []).map((product: any) => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                  {badgeProductFilter !== 'all' && (
                    <button
                      onClick={() => setBadgeProductFilter('all')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear filter
                    </button>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex space-x-1 border-b border-gray-200">
                  <button
                    onClick={() => setBadgeTab('active')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      badgeTab === 'active'
                        ? 'border-netlify-600 text-netlify-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Active ({badges.filter((b: any) => !b.archived && !b.deleted).length})
                  </button>
                  <button
                    onClick={() => setBadgeTab('archived')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      badgeTab === 'archived'
                        ? 'border-netlify-600 text-netlify-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Archived ({badges.filter((b: any) => b.archived && !b.deleted).length})
                  </button>
                  <button
                    onClick={() => setBadgeTab('recyclebin')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      badgeTab === 'recyclebin'
                        ? 'border-netlify-600 text-netlify-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Recycle Bin ({badges.filter((b: any) => b.deleted).length})
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {loading ? (
                  <div className="p-6 text-center">
                    <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Loading badges...</p>
                  </div>
                ) : (() => {
                  let filteredBadges = badges
                  if (badgeTab === 'active') {
                    filteredBadges = badges.filter((b: any) => !b.archived && !b.deleted)
                  } else if (badgeTab === 'archived') {
                    filteredBadges = badges.filter((b: any) => b.archived && !b.deleted)
                  } else if (badgeTab === 'recyclebin') {
                    filteredBadges = badges.filter((b: any) => b.deleted)
                  }

                  // Apply product filter
                  if (badgeProductFilter !== 'all') {
                    if (badgeProductFilter === 'unassigned') {
                      filteredBadges = filteredBadges.filter((b: any) => !b.productIds || b.productIds.length === 0)
                    } else {
                      filteredBadges = filteredBadges.filter((b: any) =>
                        b.productIds && b.productIds.includes(badgeProductFilter)
                      )
                    }
                  }

                  return filteredBadges.length === 0 ? (
                    <div className="p-6 text-center">
                      <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-sm text-gray-500">
                        {badgeTab === 'active' ? 'No active badges found.' :
                         badgeTab === 'archived' ? 'No archived badges.' :
                         'Recycle bin is empty.'}
                      </p>
                    </div>
                  ) : (
                    filteredBadges.map((badge) => (
                    <div key={badge.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className={`p-2 rounded-lg ${badge.isActive ? 'bg-netlify-100' : 'bg-gray-100'}`}>
                            <Award className={`h-6 w-6 ${badge.isActive ? 'text-netlify-600' : 'text-gray-400'}`} />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className={`text-lg font-medium ${badge.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                                {badge.name}
                              </h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                badge.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {badge.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>

                            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                              <span><strong>Status:</strong> {badge.status}</span>
                              <span><strong>Year:</strong> {badge.year}</span>
                              {badge.reportAvailable && (
                                <span className="inline-flex items-center">
                                  <FileCheck className="h-4 w-4 mr-1 text-green-600" />
                                  Report Available
                                </span>
                              )}
                            </div>

                            <p className="mt-2 text-sm text-gray-600">{badge.description}</p>

                            {badge.reportUrl && (
                              <p className="mt-1 text-xs text-netlify-600">
                                <strong>Report URL:</strong> {badge.reportUrl}
                              </p>
                            )}

                            {badge.productIds && badge.productIds.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                <span className="text-xs text-gray-500">Products:</span>
                                {badge.productIds.map((productId: string) => {
                                  const product = contentData.products?.find((p: any) => p.id === productId)
                                  return product ? (
                                    <span key={productId} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                                      <Package className="h-3 w-3" />
                                      {product.name}
                                    </span>
                                  ) : null
                                })}
                              </div>
                            )}

                            <p className="mt-2 text-xs text-gray-500">
                              Updated {new Date(badge.updatedAt).toLocaleDateString()} by {badge.updatedBy}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {badgeTab === 'active' && (
                            <>
                              <button
                                onClick={() => handleEditBadge(badge)}
                                className="text-netlify-600 hover:text-netlify-700"
                                title="Edit badge"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleArchiveBadge(badge.id)}
                                className="text-gray-600 hover:text-gray-700"
                                title="Archive badge"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBadge(badge.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {badgeTab === 'archived' && (
                            <>
                              <button
                                onClick={() => handleUnarchiveBadge(badge.id)}
                                className="text-netlify-600 hover:text-netlify-700"
                                title="Unarchive badge"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBadge(badge.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {badgeTab === 'recyclebin' && (
                            <>
                              <button
                                onClick={() => handleRestoreBadge(badge.id)}
                                className="text-green-600 hover:text-green-700"
                                title="Restore badge"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handlePermanentDeleteBadge(badge.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Permanently delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                  )
                })()}
              </div>
            </div>

            {/* Questionnaire Library */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Questionnaire Library</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Pre-answered security questionnaires to accelerate vendor reviews
                  </p>
                </div>
                <a
                  href="/admin/questionnaires"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Library
                </a>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-1 rounded">SOC2</span>
                    <span className="text-xs text-gray-500">125 questions</span>
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm">SOC 2 Type II</h3>
                  <div className="mt-2 flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{width: '100%'}}></div>
                    </div>
                    <span className="text-xs text-gray-600 ml-2">100%</span>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-1 rounded">ISO27001</span>
                    <span className="text-xs text-gray-500">98 questions</span>
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm">ISO 27001</h3>
                  <div className="mt-2 flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{width: '95%'}}></div>
                    </div>
                    <span className="text-xs text-gray-600 ml-2">95%</span>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-primary-600 bg-primary-100 px-2 py-1 rounded">CAIQ</span>
                    <span className="text-xs text-gray-500">287 questions</span>
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm">CSA CAIQ v4</h3>
                  <div className="mt-2 flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div className="bg-yellow-500 h-1.5 rounded-full" style={{width: '88%'}}></div>
                    </div>
                    <span className="text-xs text-gray-600 ml-2">88%</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                💡 Save 80%+ time on security questionnaires with pre-filled answers based on your trust center documentation
              </p>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'trust-center' && (
          <div className="space-y-6">
            {/* Trust Center Sub-Tabs */}
            <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
              <div className="flex space-x-8 px-6">
                {[
                  { key: 'visitors', label: 'Visitors', icon: Users, count: stats.pendingRequests },
                  { key: 'deployments', label: 'Deploy', icon: GitBranch },
                  { key: 'documents', label: 'Documents', icon: FileText },
                  { key: 'features', label: 'Features', icon: Shield },
                  { key: 'settings', label: 'Config', icon: Settings }
                ].map((subTab) => {
                  const IconComponent = subTab.icon
                  return (
                    <button
                      key={subTab.key}
                      data-tour-subtab={subTab.key}
                      onClick={() => setTrustCenterSubTab(subTab.key as any)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                        trustCenterSubTab === subTab.key
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-2" />
                      {subTab.label}
                      {subTab.count !== undefined && subTab.count > 0 && (
                        <span className="ml-2 bg-red-100 text-red-600 text-xs font-medium px-2 py-1 rounded-full">
                          {subTab.count}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

        {/* Documents section moved to Content tab */}
        {false && (<>
          <div className="space-y-6">
            {/* Document Upload */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Upload Documents</h2>
              <p className="text-sm text-gray-600 mb-4">
                Upload security and compliance documents such as policies, procedures, certifications, attestations, reports, and agreements.
                Supported formats: <span className="font-medium">PDF, DOC, DOCX, JPG, PNG</span>.
                Document titles, categories, and types will be automatically detected from filenames.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Files (one or more)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || [])
                      setBulkFiles(files)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Titles, categories, and document types will be auto-detected from filenames
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                  <select
                    value={bulkOptions.visibility}
                    onChange={(e) => setBulkOptions(prev => ({ ...prev, visibility: e.target.value as 'public' | 'gated' | 'admin' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="public">Public - Listed in trust center</option>
                    <option value="gated">Gated - Requires login to view</option>
                    <option value="admin">Admin Only - Not visible in trust center</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkOptions.ndaRequired}
                      onChange={(e) => setBulkOptions(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">NDA Required</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkOptions.watermarked}
                      onChange={(e) => setBulkOptions(prev => ({ ...prev, watermarked: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Watermarked</span>
                  </label>
                </div>

                {bulkFiles.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      Selected Files ({bulkFiles.length})
                    </h3>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {bulkFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate flex-1">{file.name}</span>
                          <span className="text-xs text-gray-500 ml-2 bg-gray-200 px-2 py-0.5 rounded">
                            {matchCategory(file.name)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleBulkUpload}
                    disabled={bulkUploading || bulkFiles.length === 0}
                    className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {bulkUploading ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin mr-2" />
                        Uploading {bulkFiles.length} files...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload {bulkFiles.length} {bulkFiles.length === 1 ? 'File' : 'Files'}
                      </>
                    )}
                  </button>
                  {bulkFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setBulkFiles([])}
                      disabled={bulkUploading}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Documents List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Document Library ({documents.length})</h2>

                  {/* Bulk actions */}
                  {documents.length > 0 && (
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          handleRescanAll()
                        }}
                        disabled={rescanningAll}
                        className="btn-secondary text-sm flex items-center"
                        title="Re-scan compliance for all documents using AI"
                      >
                        {rescanningAll ? (
                          <>
                            <Loader className="h-4 w-4 mr-1 animate-spin" />
                            Re-scanning...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-1" />
                            Re-Scan Compliance (All)
                          </>
                        )}
                      </button>
                      {selectedDocuments.size > 0 && (
                        <>
                          <span className="text-sm text-gray-600">
                            {selectedDocuments.size} selected
                          </span>
                          <button
                            onClick={openBulkEditModal}
                            className="btn-primary text-sm flex items-center"
                            title="Edit properties of selected documents"
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Bulk Edit
                          </button>
                          <button
                            onClick={handleBulkArchive}
                            className="btn-secondary text-sm flex items-center"
                            title="Archive selected documents"
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Archive
                          </button>
                          <button
                            onClick={handleBulkDelete}
                            className="btn-danger text-sm flex items-center"
                            title="Move selected documents to recycle bin"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        </>
                      )}
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.size === documents.length && documents.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Select All</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {loading ? (
                  <div className="p-6 text-center">
                    <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Loading documents...</p>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-sm text-gray-500">No documents found</p>
                  </div>
                ) : (
                  documents.map((doc) => (
                    <div key={doc.id} className="p-6">
                      <div className="flex items-start justify-between">
                        {/* Checkbox */}
                        <div className="flex items-start space-x-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedDocuments.has(doc.id)}
                            onChange={() => handleToggleDocument(doc.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                          />
                          <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900">{doc.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">Category: {doc.category}</p>
                          <p className="text-sm text-gray-600">Size: {formatFileSize(doc.size)}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()} by {doc.uploadedBy}
                          </p>

                          <div className="flex space-x-2 mt-2">
                            {/* File Format Badge - Show file size to determine type */}
                            {doc.size === 0 || doc.size < 1000 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                📝 Markdown
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                ✓ PDF
                              </span>
                            )}

                            {/* Production Ready Warning - based on file size */}
                            {(doc.size === 0 || doc.size < 1000) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                ⚠️ Not Production Ready
                              </span>
                            )}

                            {doc.gated && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Gated
                              </span>
                            )}
                            {doc.ndaRequired && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                NDA Required
                              </span>
                            )}
                            {doc.watermarked && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Watermarked
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              doc.visibility === 'public' ? 'bg-green-100 text-green-800' :
                              doc.visibility === 'gated' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {doc.visibility}
                            </span>
                          </div>

                          {/* Compliance Framework Badges */}
                          {Array.isArray(doc.complianceMapping) && doc.complianceMapping.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {doc.complianceMapping.map((fw: any, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                                  title={`${fw.framework} - ${fw.controls.length} controls detected`}
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  {fw.framework}
                                  <span className="ml-1 px-1.5 py-0.5 bg-blue-200 rounded-full">
                                    {fw.controls.length}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Fallback to old attestationTags if no complianceMapping */}
                          {(!doc.complianceMapping || doc.complianceMapping.length === 0) && doc.attestationTags && doc.attestationTags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {doc.attestationTags.map((tag: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                                  title={`${tag} (legacy detection)`}
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDocument(doc.id)}
                            className="text-blue-600 hover:text-blue-700"
                            title="View document"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(doc.id, doc.title)}
                            className="text-green-600 hover:text-green-700"
                            title="Download document"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          {doc.isGeneratedPolicy && doc.hasMarkdownSource ? (
                            <button
                              onClick={() => handleEditMarkdown(doc)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Edit markdown source"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleEditDocument(doc)}
                              className="text-yellow-600 hover:text-yellow-700"
                              title="Edit document"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              handleRescanDocument(doc.id, doc.title)
                            }}
                            disabled={rescanningDocId === doc.id}
                            className="text-purple-600 hover:text-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Re-scan compliance frameworks using AI"
                          >
                            {rescanningDocId === doc.id ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Shield className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Delete document"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Document Modal - outside tabs but inside component */}
      {trustCenterSubTab === 'documents' && editingDoc && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Document</h3>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="certifications">Certifications</option>
                      <option value="policies">Policies</option>
                      <option value="reports">Reports</option>
                      <option value="audits">Audits</option>
                      <option value="security">Security</option>
                      <option value="compliance">Compliance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                    <select
                      value={editForm.visibility}
                      onChange={(e) => setEditForm(prev => ({ ...prev, visibility: e.target.value as 'public' | 'gated' | 'admin' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="public">Public - Listed in trust center</option>
                      <option value="gated">Gated - Requires login to view</option>
                      <option value="admin">Admin Only - Not visible in trust center</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Controls who can see this document listed. NDA/Watermark settings control download access separately.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                    <select
                      value={editForm.documentType || 'other'}
                      onChange={(e) => setEditForm(prev => ({ ...prev, documentType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="policy">Policy</option>
                      <option value="procedure">Procedure</option>
                      <option value="attestation">Attestation</option>
                      <option value="report">Report</option>
                      <option value="certificate">Certificate</option>
                      <option value="agreement">Agreement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.ndaRequired}
                        onChange={(e) => setEditForm(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">NDA Required</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.watermarked}
                        onChange={(e) => setEditForm(prev => ({ ...prev, watermarked: e.target.checked }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Watermarked</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.hidden}
                        onChange={(e) => setEditForm(prev => ({ ...prev, hidden: e.target.checked }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide from Public</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.redacted}
                        onChange={(e) => setEditForm(prev => ({ ...prev, redacted: e.target.checked }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Redacted (Limited Pages)</span>
                    </label>
                  </div>

                  {editForm.redacted && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allowed Pages
                      </label>
                      <input
                        type="text"
                        value={editForm.allowedPages}
                        onChange={(e) => setEditForm(prev => ({ ...prev, allowedPages: e.target.value }))}
                        placeholder="1,3,5 or 1-5,10,15-20"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                      />
                      <p className="mt-2 text-xs text-amber-800">
                        <strong>Examples:</strong> "1,3,5" (specific pages), "1-5,10,15-20" (ranges)
                        <br/><strong>Note:</strong> Only specified pages will be available for download. Viewing will be protected against screenshots.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Products</label>
                    <p className="text-xs text-gray-500 mb-2">Select which products this document applies to (leave empty for all products)</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {contentData.products && contentData.products.length > 0 ? (
                        contentData.products.map((product: any) => (
                          <label key={product.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(editForm.productIds || []).includes(product.id)}
                              onChange={(e) => {
                                const productIds = editForm.productIds || []
                                if (e.target.checked) {
                                  setEditForm(prev => ({ ...prev, productIds: [...productIds, product.id] }))
                                } else {
                                  setEditForm(prev => ({ ...prev, productIds: productIds.filter((id: string) => id !== product.id) }))
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {product.name}
                              {product.isDefault && <span className="ml-1 text-xs text-blue-600">(Default)</span>}
                            </span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No products available.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
      )}

      {/* Edit Markdown Modal */}
      {trustCenterSubTab === 'documents' && editingMarkdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Policy Markdown</h3>
                <p className="text-sm text-gray-600 mt-1">{editingMarkdown.doc.title}</p>
              </div>
              <button
                onClick={handleCancelMarkdownEdit}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Markdown Content
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Edit the policy content below. PDF will be automatically regenerated with a new version number.
                </p>
              </div>
              <textarea
                value={markdownContent}
                onChange={(e) => setMarkdownContent(e.target.value)}
                className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter markdown content..."
              />
            </div>

            <div className="flex justify-end space-x-3 mt-4 pt-4 border-t">
              <button
                onClick={handleCancelMarkdownEdit}
                disabled={savingMarkdown}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMarkdown}
                disabled={savingMarkdown}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
              >
                {savingMarkdown ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving & Regenerating PDF...
                  </>
                ) : (
                  'Save & Regenerate PDF'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Edit Documents ({selectedDocuments.size} selected)
              </h3>
              <button
                onClick={() => setShowBulkEditModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={bulkEditForm.category}
                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Keep current values</option>
                    {Array.from(new Set(documents.map(d => d.category).filter(Boolean))).sort().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={bulkEditForm.type}
                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Keep current values</option>
                    {Array.from(new Set(documents.map(d => d.type).filter(Boolean))).sort().map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                <select
                  value={bulkEditForm.product}
                  onChange={(e) => setBulkEditForm(prev => ({ ...prev, product: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Keep current values</option>
                  {Array.from(new Set(documents.map(d => d.product).filter(Boolean))).sort().map(prod => (
                    <option key={prod} value={prod}>{prod}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                <select
                  value={bulkEditForm.visibility}
                  onChange={(e) => setBulkEditForm(prev => ({ ...prev, visibility: e.target.value as 'public' | 'gated' | 'admin' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="public">Public</option>
                  <option value="gated">Gated</option>
                  <option value="admin">Admin Only</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.ndaRequired}
                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">NDA Required</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.watermarked}
                    onChange={(e) => setBulkEditForm(prev => ({ ...prev, watermarked: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Watermarked</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Category, Type, and Product dropdowns show existing values from your documents. Select "Keep current values" to leave them unchanged. Visibility, NDA Required, and Watermarked will be applied to all selected documents.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowBulkEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkEdit}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Re-Scan Results Modal */}
      {showRescanResults && rescanResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-purple-600" />
                Compliance Re-Scan Results
              </h3>
              <button
                onClick={() => setShowRescanResults(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Batch Progress Warning */}
              {rescanBatchInfo?.hasMore && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-orange-900 mb-1">Partial Scan Complete</h4>
                      <p className="text-sm text-orange-800 mb-2">
                        Processed {rescanBatchInfo.processed} of {rescanBatchInfo.total} documents ({rescanBatchInfo.remaining} remaining).
                        To avoid timeouts, documents are scanned in batches of {rescanBatchInfo.batchSize}.
                      </p>
                      <p className="text-sm font-medium text-orange-900">
                        Click "Re-Scan Compliance (All)" again to continue scanning the remaining documents.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-900">{rescanResults.total}</div>
                  <div className="text-sm text-blue-700">Total Documents</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-900">{rescanResults.scanned}</div>
                  <div className="text-sm text-green-700">Scanned</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-900">{rescanResults.skipped}</div>
                  <div className="text-sm text-yellow-700">Skipped</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-900">{rescanResults.failed}</div>
                  <div className="text-sm text-red-700">Failed</div>
                </div>
              </div>

              {/* Cost Summary */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-purple-900">Total AI Analysis Cost:</span>
                  <span className="text-lg font-bold text-purple-900">${rescanResults.totalCost.toFixed(4)}</span>
                </div>
              </div>

              {/* Detailed Results */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Document Details</h4>
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Frameworks</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rescanResults.details.map((detail: any, idx: number) => (
                        <tr key={idx} className={detail.status === 'failed' ? 'bg-red-50' : detail.status === 'skipped' ? 'bg-gray-50' : ''}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {detail.title || `Document ${detail.documentId}`}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {detail.status === 'success' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Success
                              </span>
                            )}
                            {detail.status === 'skipped' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Skipped
                              </span>
                            )}
                            {detail.status === 'failed' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {detail.status === 'success' ? (
                              <span className="font-medium text-purple-700">{detail.frameworksFound} found</span>
                            ) : (
                              <span className="text-gray-400">{detail.reason || detail.error || '—'}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-700">
                            {detail.cost ? `$${detail.cost.toFixed(4)}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowRescanResults(false)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk CSV Upload - Subprocessors
              </h3>
              <button
                onClick={() => setShowCsvUploadModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">CSV Format Instructions</h4>
                <p className="text-sm text-blue-800 mb-2">
                  Your CSV file must include the following columns:
                </p>
                <ul className="text-sm text-blue-800 space-y-1 ml-4">
                  <li><strong>Required:</strong> name, type, purpose, location</li>
                  <li><strong>Optional:</strong> product, dataprocessed, privacyurl, securityurl, certifications</li>
                </ul>
                <p className="text-sm text-blue-800 mt-2">
                  <strong>Example:</strong> name,type,purpose,location,product,dataprocessed<br />
                  AWS,Infrastructure,Cloud Hosting,USA,Platform API,User data and files
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvUpload}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
              </div>

              {csvPreview.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Preview ({csvPreview.length} subprocessors)
                  </h4>
                  <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {csvPreview.map((row, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-sm text-gray-900">{row.name}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{row.type}</td>
                            <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate">{row.purpose}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{row.location}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{row.product || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCsvUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCsvImport}
                disabled={csvPreview.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import {csvPreview.length} Subprocessors
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Fetch Modal */}
      {showUrlFetchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Fetch Subprocessors from URL
              </h3>
              <button
                onClick={() => setShowUrlFetchModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">Supported Formats</h4>
                <p className="text-sm text-purple-800 mb-2">
                  The URL should return either JSON or CSV format:
                </p>
                <ul className="text-sm text-purple-800 space-y-1 ml-4">
                  <li><strong>JSON:</strong> Array of objects or {`{ subprocessors: [...] }`} or {`{ vendors: [...] }`}</li>
                  <li><strong>CSV:</strong> Same format as CSV upload (name, type, purpose, location, etc.)</li>
                </ul>
                <p className="text-sm text-purple-800 mt-2">
                  <strong>Example:</strong> https://example.com/subprocessors.json
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL to Fetch
                </label>
                <input
                  type="url"
                  value={urlFetchInput}
                  onChange={(e) => setUrlFetchInput(e.target.value)}
                  placeholder="https://example.com/subprocessors.json"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Associate with Product (Optional)
                </label>
                <select
                  value={urlFetchProduct}
                  onChange={(e) => setUrlFetchProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">No product (or use product from data)</option>
                  {(contentData.products || []).map((product: any) => (
                    <option key={product.id} value={product.id}>
                      {product.name} {product.isDefault ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  If specified, all imported subprocessors will be associated with this product
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowUrlFetchModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                disabled={isFetchingUrl}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUrlFetch}
                disabled={!urlFetchInput.trim() || isFetchingUrl}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFetchingUrl ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Fetch & Import
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

            </div>

                {/* Content Form Modal */}
              {activeTab === 'trust-center' && (trustCenterSubTab === 'features' || trustCenterSubTab === 'settings') && showContentForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {editingContent ? 'Edit' : 'Add New'} {contentFormType === 'subprocessors' ? 'Subprocessor' :
                                                             contentFormType === 'features' ? 'Security Feature' :
                                                             contentFormType === 'badges' ? 'Compliance Badge' :
                                                             contentFormType === 'pages' ? 'Page Content' : 'Content'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowContentForm(false)
                          setEditingContent(null)
                          setContentForm({})
                          setContentFormType(null)
                        }}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                <form onSubmit={handleSaveContent} className="space-y-4">
                  {contentFormType === 'subprocessors' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={contentForm.name || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                          <input
                            type="text"
                            value={contentForm.type || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, type: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., Infrastructure, Security"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                        <textarea
                          value={contentForm.purpose || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, purpose: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          rows={3}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                          <input
                            type="text"
                            value={contentForm.region || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, region: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., US, EU, Global"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                            value={contentForm.status || 'active'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="pending">Pending</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                        <p className="text-xs text-gray-500 mb-2">Select which products/services this subprocessor is used for (leave empty to use default)</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                          {contentData.products?.map((product: any) => (
                            <label key={product.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(contentForm.productIds || []).includes(product.id)}
                                onChange={(e) => {
                                  const productIds = contentForm.productIds || []
                                  if (e.target.checked) {
                                    setContentForm(prev => ({ ...prev, productIds: [...productIds, product.id] }))
                                  } else {
                                    setContentForm(prev => ({ ...prev, productIds: productIds.filter((id: string) => id !== product.id) }))
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">
                                {product.name}
                                {product.isDefault && <span className="ml-1 text-xs text-blue-600">(Default)</span>}
                              </span>
                            </label>
                          )) || <p className="text-sm text-gray-500">No products available. Create products first.</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website / Privacy Policy URL</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={contentForm.privacyUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, privacyUrl: e.target.value }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="https://example.com/security"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              if (!contentForm.name) {
                                alert('Please enter vendor name first')
                                return
                              }
                              setLoading(true)
                              try {
                                const response = await fetch('/api/scan-attestations', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    url: contentForm.privacyUrl || `https://${contentForm.name.toLowerCase().replace(/\s+/g, '')}.com`,
                                    vendorName: contentForm.name,
                                    discoverUrls: true
                                  })
                                })
                                const data = await response.json()
                                console.log('Scan response:', data)
                                if (data.attestations && data.attestations.length > 0) {
                                  const attestations = data.attestations.map((a: any) => ({
                                    ...a,
                                    scannedAt: data.scannedAt
                                  }))
                                  console.log('Setting attestations:', attestations)
                                  setContentForm(prev => ({
                                    ...prev,
                                    attestations: attestations,
                                    complianceUrls: data.discoveredUrls || data.scannedUrls
                                  }))
                                  alert(`Found ${data.attestations.length} attestations from ${data.scannedUrls?.length || 1} page(s)!\n${data.discoveredUrls?.length || 0} compliance URLs discovered.`)
                                } else {
                                  alert('No attestations found')
                                }
                              } catch (err) {
                                console.error('Scan error:', err)
                                alert('Failed to scan for attestations')
                              } finally {
                                setLoading(false)
                              }
                            }}
                            disabled={loading || !contentForm.name}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
                          >
                            <Search className="h-4 w-4 mr-1" />
                            Discover & Scan
                          </button>
                        </div>
                        {contentForm.complianceUrls && contentForm.complianceUrls.length > 0 && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                            <p className="font-medium text-blue-900 mb-1">Discovered URLs ({contentForm.complianceUrls.length}):</p>
                            <ul className="list-disc list-inside text-blue-700 space-y-1">
                              {contentForm.complianceUrls.slice(0, 5).map((url: string, idx: number) => (
                                <li key={idx} className="truncate">{url}</li>
                              ))}
                              {contentForm.complianceUrls.length > 5 && (
                                <li>... and {contentForm.complianceUrls.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        )}
                        {contentForm.attestations && contentForm.attestations.length > 0 && (
                          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-xs font-medium text-green-800 mb-2">Found Attestations:</p>
                            <div className="flex flex-wrap gap-2">
                              {contentForm.attestations.map((att: any, idx: number) => (
                                <span
                                  key={idx}
                                  className={`text-xs px-2 py-1 rounded ${
                                    att.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                    att.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {att.name} ({att.confidence})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Manual Attestation Management */}
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Attestations & Certifications
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const attestations = contentForm.attestations || []
                              setContentForm(prev => ({
                                ...prev,
                                attestations: [...attestations, {
                                  name: '',
                                  confidence: 'high',
                                  scannedAt: new Date().toISOString()
                                }]
                              }))
                            }}
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Attestation
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(contentForm.attestations || []).map((att: any, idx: number) => (
                            <div key={idx} className="flex gap-2 items-start p-2 bg-gray-50 rounded-lg">
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={att.name || ''}
                                  onChange={(e) => {
                                    const updated = [...(contentForm.attestations || [])]
                                    updated[idx] = { ...updated[idx], name: e.target.value }
                                    setContentForm(prev => ({ ...prev, attestations: updated }))
                                  }}
                                  placeholder="e.g., SOC 2 Type II, ISO 27001"
                                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                                <select
                                  value={att.confidence || 'high'}
                                  onChange={(e) => {
                                    const updated = [...(contentForm.attestations || [])]
                                    updated[idx] = { ...updated[idx], confidence: e.target.value }
                                    setContentForm(prev => ({ ...prev, attestations: updated }))
                                  }}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                  <option value="high">High Confidence</option>
                                  <option value="medium">Medium Confidence</option>
                                  <option value="low">Low Confidence</option>
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (contentForm.attestations || []).filter((_: any, i: number) => i !== idx)
                                  setContentForm(prev => ({ ...prev, attestations: updated }))
                                }}
                                className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                          {(!contentForm.attestations || contentForm.attestations.length === 0) && (
                            <p className="text-sm text-gray-500 italic">No attestations added yet. Click "Add Attestation" to add one.</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {trustCenterSubTab === 'features' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <input
                            type="text"
                            value={contentForm.title || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                          <input
                            type="text"
                            value={contentForm.icon || 'Shield'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, icon: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="Lucide icon name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={contentForm.description || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          rows={3}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={contentForm.category || 'security'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          >
                            <option value="security">Security</option>
                            <option value="privacy">Privacy</option>
                            <option value="compliance">Compliance</option>
                            <option value="infrastructure">Infrastructure</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                          <input
                            type="number"
                            value={contentForm.displayOrder || 0}
                            onChange={(e) => setContentForm(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                        <p className="text-xs text-gray-500 mb-2">Select which products this feature applies to (leave empty for all products)</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                          {contentData.products && contentData.products.length > 0 ? (
                            contentData.products.map((product: any) => (
                              <label key={product.id} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(contentForm.productIds || []).includes(product.id)}
                                  onChange={(e) => {
                                    const productIds = contentForm.productIds || []
                                    if (e.target.checked) {
                                      setContentForm(prev => ({ ...prev, productIds: [...productIds, product.id] }))
                                    } else {
                                      setContentForm(prev => ({ ...prev, productIds: productIds.filter((id: string) => id !== product.id) }))
                                    }
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">
                                  {product.name}
                                  {product.isDefault && <span className="ml-1 text-xs text-blue-600">(Default)</span>}
                                </span>
                              </label>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500">No products available. Create products in Settings first.</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {trustCenterSubTab === 'hero' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hero Section</label>
                        <select
                          value={selectedHeroType}
                          onChange={(e) => {
                            setSelectedHeroType(e.target.value as 'hero' | 'hero-terms' | 'hero-privacy' | 'hero-dmca-submit' | 'hero-dmca-counter')
                            // Load the content for the selected hero type
                            const heroData = e.target.value === 'hero' ? contentData.hero :
                                           e.target.value === 'hero-terms' ? contentData.heroTerms :
                                           e.target.value === 'hero-privacy' ? contentData.heroPrivacy :
                                           e.target.value === 'hero-dmca-submit' ? contentData.heroDmcaSubmit :
                                           e.target.value === 'hero-dmca-counter' ? contentData.heroDmcaCounter : null
                            setContentForm(heroData || { title: '', subtitle: '', description: '', stats: [] })
                            setEditingContent(heroData)
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                        >
                          <option value="hero">Main Trust Center</option>
                          <option value="hero-terms">Terms of Use</option>
                          <option value="hero-privacy">Privacy Policy</option>
                          <option value="hero-dmca-submit">DMCA Submit</option>
                          <option value="hero-dmca-counter">DMCA Counter</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          type="text"
                          value={contentForm.title || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                        <input
                          type="text"
                          value={contentForm.subtitle || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, subtitle: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={contentForm.description || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          rows={4}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stats (JSON array)</label>
                        <textarea
                          value={JSON.stringify(contentForm.stats || [], null, 2)}
                          onChange={(e) => {
                            try {
                              const stats = JSON.parse(e.target.value)
                              setContentForm(prev => ({ ...prev, stats }))
                            } catch (err) {
                              // Invalid JSON, keep the text value for user to fix
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500 font-mono text-sm"
                          rows={6}
                          placeholder='[{"value": "99.9%", "label": "Uptime", "description": "Service availability"}]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter an array of stat objects with "value", "label", and optional "description" fields
                        </p>
                      </div>
                    </>
                  )}

                  {trustCenterSubTab === 'features' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Badge Name</label>
                          <input
                            type="text"
                            value={contentForm.name || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., SOC 2 Type II"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                          <input
                            type="text"
                            value={contentForm.icon || 'Shield'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, icon: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="Lucide icon name"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <input
                            type="text"
                            value={contentForm.status || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., Verified, Certified, Compliant"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                          <input
                            type="text"
                            value={contentForm.year || new Date().getFullYear().toString()}
                            onChange={(e) => setContentForm(prev => ({ ...prev, year: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., 2024, Ongoing"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={contentForm.description || ''}
                          onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          rows={3}
                          placeholder="Brief description of the certification or compliance"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={contentForm.reportAvailable || false}
                            onChange={(e) => setContentForm(prev => ({ ...prev, reportAvailable: e.target.checked }))}
                            className="mr-2"
                          />
                          <label className="text-sm text-gray-700">Report Available</label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Report URL</label>
                          <input
                            type="text"
                            value={contentForm.reportUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, reportUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="URL to compliance report"
                            disabled={!contentForm.reportAvailable}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {trustCenterSubTab === 'settings' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <input
                            type="text"
                            value={contentForm.companyName || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, companyName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., Netlify"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Website URL</label>
                          <input
                            type="url"
                            value={contentForm.companyUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, companyUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., https://netlify.com"
                          />
                          <p className="mt-1 text-xs text-gray-500">Used for scanning security features</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status Page URL</label>
                          <input
                            type="text"
                            value={contentForm.statusPageUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, statusPageUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="e.g., https://status.netlify.com or /status (leave empty to hide)"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave empty to hide System Status section from Trust Center
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Support Email
                          </label>
                          <input
                            type="text"
                            value={contentForm.supportEmail || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, supportEmail: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder={currentUser?.email ? `support (becomes support@${currentUser.email.split('@')[1]})` : 'support or support@example.com'}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Enter just username or full email. Domain {currentUser?.email ? `@${currentUser.email.split('@')[1]}` : ''} will be added if needed.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Security Email
                          </label>
                          <input
                            type="text"
                            value={contentForm.securityEmail || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, securityEmail: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder={currentUser?.email ? `security (becomes security@${currentUser.email.split('@')[1]})` : 'security or security@example.com'}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Enter just username or full email. Domain {currentUser?.email ? `@${currentUser.email.split('@')[1]}` : ''} will be added if needed.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Policy URL</label>
                          <input
                            type="text"
                            value={contentForm.privacyPolicyUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, privacyPolicyUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/privacy or https://example.com/privacy"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Terms of Service URL</label>
                          <input
                            type="text"
                            value={contentForm.termsOfServiceUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, termsOfServiceUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/terms or https://example.com/terms"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Cookie Policy URL</label>
                          <input
                            type="text"
                            value={contentForm.cookiePolicyUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, cookiePolicyUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/cookies or https://example.com/cookies"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                      </div>

                      <h4 className="text-md font-medium text-gray-900 mt-6 mb-4">Compliance Documents</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">SOC 2 Report URL</label>
                          <input
                            type="text"
                            value={contentForm.soc2ReportUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, soc2ReportUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/soc2 or https://example.com/soc2"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">ISO 27001 Certificate URL</label>
                          <input
                            type="text"
                            value={contentForm.iso27001CertificateUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, iso27001CertificateUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/iso27001 or https://example.com/iso27001"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subprocessors Page URL</label>
                          <input
                            type="text"
                            value={contentForm.subprocessorsPageUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, subprocessorsPageUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/subprocessors or https://example.com/subprocessors"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                      </div>

                      <h4 className="text-md font-medium text-gray-900 mt-6 mb-4">Subprocessors Page Configuration</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subprocessors Hero Title</label>
                          <input
                            type="text"
                            value={contentForm.subprocessorsHeroTitle || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, subprocessorsHeroTitle: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="Our Subprocessors"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subprocessors Hero Subtitle</label>
                          <input
                            type="text"
                            value={contentForm.subprocessorsHeroSubtitle || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, subprocessorsHeroSubtitle: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="Trusted Partners for Enhanced Service Delivery"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subprocessors Hero Description</label>
                          <textarea
                            value={contentForm.subprocessorsHeroDescription || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, subprocessorsHeroDescription: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            rows={3}
                            placeholder="We work with carefully selected subprocessors to provide you with the best possible service..."
                          />
                        </div>
                      </div>

                      <h4 className="text-md font-medium text-gray-900 mt-6 mb-4">Display Content</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Location</label>
                          <input
                            type="text"
                            value={contentForm.companyLocation || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, companyLocation: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="San Francisco, CA"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Corporate Address</label>
                          <textarea
                            value={contentForm.corporateAddress || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, corporateAddress: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="512 2nd Street, Floor 2&#10;San Francisco, CA 94107"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated Text</label>
                          <input
                            type="text"
                            value={contentForm.lastUpdatedText || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, lastUpdatedText: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="March 2024"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">"View Full List" Button Text</label>
                          <input
                            type="text"
                            value={contentForm.viewFullListText || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, viewFullListText: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="View Full List"
                          />
                        </div>
                      </div>

                      <h4 className="text-md font-medium text-gray-900 mt-6 mb-4">Theme & Branding</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                          <input
                            type="color"
                            value={contentForm.primaryColor || '#2E51ED'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                            className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Main brand color (default: Netlify Royal Blue)</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                          <input
                            type="color"
                            value={contentForm.secondaryColor || '#05BDBA'}
                            onChange={(e) => setContentForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                            className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Accent color (default: Netlify Robin's Egg Blue)</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                          <input
                            type="text"
                            value={contentForm.logoUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, logoUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/logo.png or https://example.com/logo.png"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Favicon URL</label>
                          <input
                            type="text"
                            value={contentForm.faviconUrl || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, faviconUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="/favicon.ico or https://example.com/favicon.ico"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Relative paths use Company URL as parent
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                          <input
                            type="text"
                            value={contentForm.brandName || ''}
                            onChange={(e) => setContentForm(prev => ({ ...prev, brandName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                            placeholder="Product/Brand Name"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={contentForm.isActive !== false}
                      onChange={(e) => setContentForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">Active</label>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowContentForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-netlify-600 text-white rounded-lg hover:bg-netlify-700 transition-colors inline-flex items-center"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {editingContent ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
                  </div>
                </div>
              )}

            {/* Content List */}
            {trustCenterSubTab !== 'visitors' && trustCenterSubTab !== 'deployments' && trustCenterSubTab !== 'documents' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                {trustCenterSubTab !== 'features' && trustCenterSubTab !== 'documents' && trustCenterSubTab !== 'settings' && (
                  <h3 className="text-lg font-semibold text-gray-900">
                    {trustCenterSubTab === 'deployments' ? 'Deployments' :
                     trustCenterSubTab === 'documents' ? 'Documents' :
                     trustCenterSubTab === 'features' ? 'Features' :
                     trustCenterSubTab === 'settings' ? 'System Settings' : 'Content'}
                  </h3>
                )}
                {trustCenterSubTab === 'subprocessors' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowReviewModal(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center text-sm"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Generate Review
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('Convert all vendor URLs to base domains? This will help discovery work better.\n\nExample: https://aws.amazon.com/privacy → https://aws.amazon.com')) return

                        setLoading(true)
                        let updatedCount = 0

                        for (const subprocessor of contentData.subprocessors) {
                          if (subprocessor.privacyUrl) {
                            try {
                              const url = new URL(subprocessor.privacyUrl)
                              const baseUrl = `${url.protocol}//${url.host}`

                              // Only update if it's different
                              if (baseUrl !== subprocessor.privacyUrl) {
                                const updatedSubprocessor = {
                                  ...subprocessor,
                                  privacyUrl: baseUrl
                                }

                                await fetch(`/api/subprocessors/${subprocessor.id}`, {
                                  method: 'PUT',
                                  credentials: 'include',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
                                  },
                                  body: JSON.stringify(updatedSubprocessor)
                                })

                                updatedCount++
                              }
                            } catch (err) {
                              console.error(`Failed to update ${subprocessor.name}:`, err)
                            }
                          }
                        }

                        setLoading(false)
                        alert(`Updated ${updatedCount} vendor URL(s) to base domains!`)
                        await fetchAllData()
      triggerContentRefresh()
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center text-sm"
                    >
                      <Globe className="h-4 w-4 mr-1" />
                      Fix All URLs
                    </button>
                    <button
                      onClick={async () => {
                        const subprocessorsWithUrls = contentData.subprocessors.filter((s: any) => s.privacyUrl)
                        if (subprocessorsWithUrls.length === 0) {
                          alert('No subprocessors have URLs to scan')
                          return
                        }

                        if (!confirm(`Scan attestations for ${subprocessorsWithUrls.length} subprocessor(s)?`)) return

                      setLoading(true)
                      let scannedCount = 0
                      let foundCount = 0

                      for (const subprocessor of subprocessorsWithUrls) {
                        try {
                          const response = await fetch('/api/scan-attestations', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              url: subprocessor.privacyUrl,
                              vendorName: subprocessor.name,
                              discoverUrls: true
                            })
                          })
                          const data = await response.json()

                          if (data.attestations && data.attestations.length > 0) {
                            // Update subprocessor with attestations and discovered URLs
                            const updatedSubprocessor = {
                              ...subprocessor,
                              attestations: data.attestations.map((a: any) => ({
                                ...a,
                                scannedAt: data.scannedAt
                              })),
                              complianceUrls: data.discoveredUrls || data.scannedUrls
                            }

                            // Save to backend
                            await fetch(`/api/subprocessors/${subprocessor.id}`, {
                              method: 'PUT',
                              credentials: 'include',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
                              },
                              body: JSON.stringify(updatedSubprocessor)
                            })

                            foundCount += data.attestations.length
                          }
                          scannedCount++
                        } catch (err) {
                          console.error(`Failed to scan ${subprocessor.name}:`, err)
                        }
                      }

                      setLoading(false)
                      alert(`Scanned ${scannedCount} subprocessors and found ${foundCount} attestations!`)
                      await fetchAllData()
      triggerContentRefresh() // Refresh data
                    }}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm"
                  >
                    <Search className="h-4 w-4 mr-1" />
                    Scan All Attestations
                  </button>
                  </div>
                )}
              </div>

              {trustCenterSubTab === 'features' && (
                <div className="space-y-6">
                  {/* Page Description */}
                  <div id="features" className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-blue-900 mb-2">Trust Center Features</h2>
                    <p className="text-sm text-blue-800">
                      Configure and manage all public-facing features of your Trust Center including subprocessors,
                      security features, compliance badges, legal pages, privacy settings, and product changelog.
                    </p>
                  </div>

                  {/* Subprocessors Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <SectionHeading id="subprocessors" className="text-2xl font-bold text-gray-900 mb-4">
                      Subprocessors
                    </SectionHeading>

                    {/* Description */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-semibold text-blue-900 mb-2">📋 Manage Your Subprocessors</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        Subprocessors are third-party vendors who help deliver your products or services. Manage them here to maintain transparency and compliance.
                      </p>
                      <div className="space-y-2 text-sm text-blue-800">
                        <p className="flex items-start gap-2">
                          <span className="font-medium">•</span>
                          <span><strong>Manual Entry:</strong> Add individual subprocessors using the form below</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-medium">•</span>
                          <span><strong>Bulk Import:</strong> Upload multiple subprocessors via CSV</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-medium">•</span>
                          <span><strong>URL Fetch:</strong> Import subprocessor lists from your existing public URLs</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-medium">•</span>
                          <span><strong>TPRM Integration:</strong> Mark vendors as subprocessors in the <a href="#tprm" onClick={(e) => { e.preventDefault(); setActiveTab('tprm') }} className="text-blue-600 hover:text-blue-700 underline font-medium">Contracts section</a> - they'll auto-sync here</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-medium">•</span>
                          <span><strong>Product Association:</strong> Assign subprocessors to specific <a href="#products" onClick={(e) => { e.preventDefault(); setTrustCenterSubTab('products') }} className="text-blue-600 hover:text-blue-700 underline font-medium">products</a> for organized tracking</span>
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      <button
                        onClick={() => {
                          setEditingContent(null)
                          setContentForm({
                            name: '',
                            type: '',
                            purpose: '',
                            dataProcessed: '',
                            location: '',
                            privacyUrl: '',
                            securityUrl: '',
                            certifications: '',
                            productIds: []
                          })
                          setContentFormType('subprocessors')
                          setShowContentForm(true)
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center text-sm font-medium"
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add New Subprocessor
                      </button>
                      <button
                        onClick={() => {
                          setCsvFile(null)
                          setCsvPreview([])
                          setShowCsvUploadModal(true)
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center text-sm font-medium"
                      >
                        <Upload className="h-4 w-4 mr-1.5" />
                        Bulk CSV Upload
                      </button>
                      <button
                        onClick={() => {
                          setUrlFetchInput('')
                          setUrlFetchProduct('')
                          setShowUrlFetchModal(true)
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center text-sm font-medium"
                      >
                        <Globe className="h-4 w-4 mr-1.5" />
                        Fetch from URL
                      </button>
                      <button
                        onClick={() => {
                          setAIGeneratorType('subprocessors')
                          setShowAIGenerator(true)
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors inline-flex items-center text-sm font-medium"
                      >
                        <Sparkles className="h-4 w-4 mr-1.5" />
                        Generate with AI
                      </button>
                    </div>

                  {/* Subprocessor Tabs */}
                  <div className="border-b border-gray-200 mb-4">
                    <div className="flex space-x-8">
                      <button
                        onClick={() => setSubprocessorTab('active')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          subprocessorTab === 'active'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Database className="h-4 w-4 inline mr-2" />
                        Active Subprocessors ({(contentData.subprocessors || []).filter((s: any) => !s.archived && !s.deleted).length})
                      </button>
                      <button
                        onClick={() => setSubprocessorTab('archived')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          subprocessorTab === 'archived'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Archive className="h-4 w-4 inline mr-2" />
                        Archived ({(contentData.subprocessors || []).filter((s: any) => s.archived && !s.deleted).length})
                      </button>
                      <button
                        onClick={() => setSubprocessorTab('recyclebin')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          subprocessorTab === 'recyclebin'
                            ? 'border-red-600 text-red-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Trash2 className="h-4 w-4 inline mr-2" />
                        Recycle Bin ({(contentData.subprocessors || []).filter((s: any) => s.deleted).length})
                      </button>
                    </div>
                  </div>

                  {/* Product Filter */}
                  <div className="mb-4 flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                    <select
                      value={subprocessorProductFilter}
                      onChange={(e) => setSubprocessorProductFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Products</option>
                      <option value="unassigned">Unassigned (No Products)</option>
                      {(contentData.products || []).map((product: any) => (
                        <option key={product.id} value={product.id}>
                          {product.name} {product.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    {subprocessorProductFilter !== 'all' && (
                      <button
                        onClick={() => setSubprocessorProductFilter('all')}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Clear filter
                      </button>
                    )}
                  </div>

                  {/* Empty Recycle Bin Button */}
                  {subprocessorTab === 'recyclebin' && (contentData.subprocessors || []).filter((s: any) => s.deleted).length > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={handleEmptySubprocessorRecycleBin}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Empty Recycle Bin
                      </button>
                    </div>
                  )}

                  {(() => {
                    // Filter subprocessors based on tab
                    let filteredSubprocessors = contentData.subprocessors || []

                    if (subprocessorTab === 'active') {
                      filteredSubprocessors = filteredSubprocessors.filter((s: any) => !s.archived && !s.deleted)
                    } else if (subprocessorTab === 'archived') {
                      filteredSubprocessors = filteredSubprocessors.filter((s: any) => s.archived && !s.deleted)
                    } else if (subprocessorTab === 'recyclebin') {
                      filteredSubprocessors = filteredSubprocessors.filter((s: any) => s.deleted)
                    }

                    // Filter by product
                    if (subprocessorProductFilter !== 'all') {
                      if (subprocessorProductFilter === 'unassigned') {
                        filteredSubprocessors = filteredSubprocessors.filter((s: any) => !s.productIds || s.productIds.length === 0)
                      } else {
                        filteredSubprocessors = filteredSubprocessors.filter((s: any) =>
                          s.productIds && s.productIds.includes(subprocessorProductFilter)
                        )
                      }
                    }

                    if (filteredSubprocessors.length === 0) {
                      return (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                          <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600">
                            {subprocessorProductFilter !== 'all'
                              ? `No subprocessors found for ${subprocessorProductFilter === 'unassigned' ? 'unassigned' : contentData.products?.find((p: any) => p.id === subprocessorProductFilter)?.name}`
                              : 'No subprocessors in this category'}
                          </p>
                          {subprocessorProductFilter !== 'all' && (
                            <button
                              onClick={() => setSubprocessorProductFilter('all')}
                              className="mt-3 text-sm text-blue-600 hover:text-blue-700"
                            >
                              Clear filter
                            </button>
                          )}
                        </div>
                      )
                    }

                    return filteredSubprocessors.map((item: any) => {
                      // Find contracts linked to this subprocessor
                      const linkedContracts = (contentData.contracts || []).filter(
                        (contract: any) => contract.subprocessorId === item.id && !contract.isArchived
                      )

                      return (
                    <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900">{item.name}</h4>
                              {linkedContracts.length > 0 && (
                                <button
                                  onClick={() => navigate('/admin/contracts')}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                                  title={`${linkedContracts.length} contract${linkedContracts.length > 1 ? 's' : ''} linked\n${linkedContracts.map((c: any) => c.fileName).join('\n')}`}
                                >
                                  <FileText className="h-3 w-3" />
                                  {linkedContracts.length}
                                </button>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{item.purpose}</p>
                            <div className="mt-2 text-xs text-gray-500">
                              <span className="mr-4">Type: {item.type}</span>
                              <span className="mr-4">Region: {item.region}</span>
                              <span className={`px-2 py-1 rounded-full ${
                                item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {item.status}
                              </span>
                            </div>
                          {item.productIds && item.productIds.length > 0 && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">Products:</span>
                              {item.productIds.map((productId: string) => {
                                const product = contentData.products?.find((p: any) => p.id === productId)
                                return product ? (
                                  <span key={productId} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                                    {product.name}
                                  </span>
                                ) : null
                              })}
                            </div>
                          )}
                          {item.attestations && item.attestations.length > 0 && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Shield className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-900">Attestations ({item.attestations.length}):</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {item.attestations.map((att: any, idx: number) => (
                                  <span
                                    key={idx}
                                    className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                      att.confidence === 'high' ? 'bg-green-100 text-green-800 border border-green-300' :
                                      att.confidence === 'medium' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                                      'bg-gray-100 text-gray-700 border border-gray-300'
                                    }`}
                                    title={`Confidence: ${att.confidence} | Scanned: ${new Date(att.scannedAt).toLocaleDateString()}`}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {att.name}
                                  </span>
                                ))}
                              </div>
                              <p className="text-xs text-green-700 mt-2">
                                Last scanned: {item.attestations[0]?.scannedAt ? new Date(item.attestations[0].scannedAt).toLocaleDateString() : 'Unknown'}
                              </p>
                            </div>
                          )}
                          {(!item.attestations || item.attestations.length === 0) && item.privacyUrl && (
                            <div className="mt-2 text-xs text-gray-500 italic">
                              No attestations found. Click "Scan Attestations" to detect certifications.
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              if (editingContent?.id === item.id) {
                                setEditingContent(null)
                                setShowContentForm(false)
                                setContentForm({})
                              } else {
                                handleEditContent(item)
                              }
                            }}
                            className={`p-2 ${editingContent?.id === item.id ? 'text-gray-600 bg-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
                            title={editingContent?.id === item.id ? 'Cancel editing' : 'Edit'}
                          >
                            {editingContent?.id === item.id ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                          </button>
                          {subprocessorTab === 'active' ? (
                            <>
                              <button
                                onClick={() => handleArchiveSubprocessor(item.id)}
                                className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                                title="Archive subprocessor"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContent(item.id)}
                                className="p-2 text-red-400 hover:text-red-600"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : subprocessorTab === 'archived' ? (
                            <>
                              <button
                                onClick={() => handleUnarchiveSubprocessor(item.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Unarchive subprocessor"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContent(item.id)}
                                className="p-2 text-red-400 hover:text-red-600"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleRestoreSubprocessor(item.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Restore subprocessor"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handlePermanentDeleteSubprocessor(item.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Permanently delete"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      </div>

                      {/* Inline Edit Form */}
                      {editingContent?.id === item.id && showContentForm && (
                        <div className="border-t border-gray-200 bg-gray-50 p-6">
                          <h4 className="text-md font-semibold text-gray-900 mb-4">Edit Subprocessor</h4>
                          <form onSubmit={handleSaveContent} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                  type="text"
                                  value={contentForm.name || ''}
                                  onChange={(e) => setContentForm(prev => ({ ...prev, name: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <input
                                  type="text"
                                  value={contentForm.type || ''}
                                  onChange={(e) => setContentForm(prev => ({ ...prev, type: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                  placeholder="e.g., Infrastructure, Security"
                                  required
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                              <textarea
                                value={contentForm.purpose || ''}
                                onChange={(e) => setContentForm(prev => ({ ...prev, purpose: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                rows={3}
                                required
                              />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                                <input
                                  type="text"
                                  value={contentForm.region || ''}
                                  onChange={(e) => setContentForm(prev => ({ ...prev, region: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                  placeholder="e.g., US, EU, Global"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                  value={contentForm.status || 'active'}
                                  onChange={(e) => setContentForm(prev => ({ ...prev, status: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                  <option value="pending">Pending</option>
                                </select>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                              <p className="text-xs text-gray-500 mb-2">Select which products/services this subprocessor is used for</p>
                              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white">
                                {contentData.products?.map((product: any) => (
                                  <label key={product.id} className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(contentForm.productIds || []).includes(product.id)}
                                      onChange={(e) => {
                                        const productIds = contentForm.productIds || []
                                        if (e.target.checked) {
                                          setContentForm(prev => ({ ...prev, productIds: [...productIds, product.id] }))
                                        } else {
                                          setContentForm(prev => ({ ...prev, productIds: productIds.filter((id: string) => id !== product.id) }))
                                        }
                                      }}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {product.name}
                                      {product.isDefault && <span className="ml-1 text-xs text-blue-600">(Default)</span>}
                                    </span>
                                  </label>
                                )) || <p className="text-sm text-gray-500">No products available.</p>}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Policy URL</label>
                              <div className="flex gap-2">
                                <input
                                  type="url"
                                  value={contentForm.privacyUrl || ''}
                                  onChange={(e) => setContentForm(prev => ({ ...prev, privacyUrl: e.target.value }))}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-500"
                                  placeholder="https://example.com/security"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!contentForm.name) {
                                      alert('Please enter vendor name first')
                                      return
                                    }
                                    setLoading(true)
                                    try {
                                      const response = await fetch('/api/scan-attestations', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          url: contentForm.privacyUrl || `https://${contentForm.name.toLowerCase().replace(/\s+/g, '')}.com`,
                                          vendorName: contentForm.name,
                                          discoverUrls: true
                                        })
                                      })
                                      const data = await response.json()
                                      if (data.attestations && data.attestations.length > 0) {
                                        const attestations = data.attestations.map((a: any) => ({
                                          ...a,
                                          scannedAt: data.scannedAt
                                        }))
                                        setContentForm(prev => ({
                                          ...prev,
                                          attestations: attestations,
                                          complianceUrls: data.discoveredUrls || data.scannedUrls
                                        }))
                                        alert(`Found ${data.attestations.length} attestations!`)
                                      } else {
                                        alert('No attestations found')
                                      }
                                    } catch (err) {
                                      alert('Failed to scan for attestations')
                                    } finally {
                                      setLoading(false)
                                    }
                                  }}
                                  disabled={loading || !contentForm.name}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center whitespace-nowrap"
                                >
                                  <Search className="h-4 w-4 mr-1" />
                                  Scan
                                </button>
                              </div>
                            </div>

                            <div className="border-t pt-4">
                              <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700">Attestations</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const attestations = contentForm.attestations || []
                                    setContentForm(prev => ({
                                      ...prev,
                                      attestations: [...attestations, {
                                        name: '',
                                        confidence: 'high',
                                        scannedAt: new Date().toISOString()
                                      }]
                                    }))
                                  }}
                                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add
                                </button>
                              </div>
                              <div className="space-y-2">
                                {(contentForm.attestations || []).map((att: any, idx: number) => (
                                  <div key={idx} className="flex gap-2 items-start p-2 bg-white rounded-lg border border-gray-200">
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      <input
                                        type="text"
                                        value={att.name || ''}
                                        onChange={(e) => {
                                          const updated = [...(contentForm.attestations || [])]
                                          updated[idx] = { ...updated[idx], name: e.target.value }
                                          setContentForm(prev => ({ ...prev, attestations: updated }))
                                        }}
                                        placeholder="e.g., SOC 2 Type II"
                                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                                      />
                                      <select
                                        value={att.confidence || 'high'}
                                        onChange={(e) => {
                                          const updated = [...(contentForm.attestations || [])]
                                          updated[idx] = { ...updated[idx], confidence: e.target.value }
                                          setContentForm(prev => ({ ...prev, attestations: updated }))
                                        }}
                                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                                      >
                                        <option value="high">High</option>
                                        <option value="medium">Medium</option>
                                        <option value="low">Low</option>
                                      </select>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = (contentForm.attestations || []).filter((_: any, i: number) => i !== idx)
                                        setContentForm(prev => ({ ...prev, attestations: updated }))
                                      }}
                                      className="p-1 text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="flex space-x-3 pt-4 border-t">
                              <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                              >
                                Save Changes
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingContent(null)
                                  setShowContentForm(false)
                                  setContentForm({})
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                      )
                    })
                  })()}
                  </div>

                  {/* Security Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <SectionHeading id="security" className="text-2xl font-bold text-gray-900">
                        Security
                      </SectionHeading>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setAIGeneratorType('features')
                            setShowAIGenerator(true)
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors inline-flex items-center text-sm font-medium"
                        >
                          <Sparkles className="h-4 w-4 mr-1.5" />
                          Generate with AI
                        </button>
                        <button
                          onClick={async () => {
                            if (!contentData.settings?.companyUrl) {
                              alert('Please set your company website URL in Settings → System Settings first.')
                              return
                            }

                            if (!confirm(`Scan ${contentData.settings.companyUrl} for security features?\n\nWe'll check your /security, /trust, and /compliance pages using AI.`)) return

                          setLoading(true)
                          try {
                            const response = await fetch('/api/generate-security-features', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                companyName: contentData.settings?.companyName || 'Your Company',
                                companyUrl: contentData.settings.companyUrl,
                                adminEmail: currentUser?.email || 'admin@system'
                              })
                            })

                            if (response.ok) {
                              const data = await response.json()
                              let message = `Successfully generated ${data.count} security features!`

                              if (data.usedAI) {
                                message += ` (AI-powered)`
                              }

                              if (data.usage) {
                                message += `\n\n📊 Usage: ${data.usage.remaining} of ${data.usage.limit} AI scans remaining today`
                                if (data.usage.remaining === 0) {
                                  message += `\n\n💡 Tip: Set your own Anthropic API key in tenant secrets to get unlimited scans.`
                                }
                              }

                              alert(message)
                              await new Promise(resolve => setTimeout(resolve, 500))
                              await fetchAllData()
                              triggerContentRefresh()
                            } else {
                              const error = await response.json()
                              if (response.status === 429) {
                                alert(
                                  `⏱️ Daily AI Scan Limit Reached\n\n` +
                                  `You've used all ${error.usage?.limit || 3} AI scans for today.\n\n` +
                                  `💡 To get unlimited scans:\n` +
                                  `Set your own Anthropic API key in Settings → Tenant Secrets\n` +
                                  `Key name: ANTHROPIC_API_KEY\n\n` +
                                  `Resets tomorrow, or you can still use keyword-based scanning.`
                                )
                              } else {
                                alert(`Failed to generate features: ${error.message}`)
                              }
                            }
                          } catch (err) {
                            console.error('Generation error:', err)
                            alert('Failed to generate security features')
                          } finally {
                            setLoading(false)
                          }
                        }}
                        disabled={loading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center text-sm"
                        title="AI-powered scan of your security pages"
                      >
                        <Award className="h-4 w-4 mr-1" />
                        Web Scan Features
                      </button>
                      </div>
                    </div>

                  {/* Product Filter */}
                  <div className="mb-4 flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
                    <select
                      value={featureProductFilter}
                      onChange={(e) => setFeatureProductFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="all">All Products</option>
                      <option value="unassigned">Unassigned (No Products)</option>
                      {(contentData.products || []).map((product: any) => (
                        <option key={product.id} value={product.id}>
                          {product.name} {product.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    {featureProductFilter !== 'all' && (
                      <button
                        onClick={() => setFeatureProductFilter('all')}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Clear filter
                      </button>
                    )}
                  </div>

                  {/* Tabs */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex space-x-1 border-b border-gray-200">
                      <button
                        onClick={() => setFeatureTab('active')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          featureTab === 'active'
                            ? 'border-netlify-600 text-netlify-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Active ({(contentData.features || []).filter((f: any) => !f.archived && !f.deleted).length})
                      </button>
                      <button
                        onClick={() => setFeatureTab('archived')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          featureTab === 'archived'
                            ? 'border-netlify-600 text-netlify-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Archived ({(contentData.features || []).filter((f: any) => f.archived && !f.deleted).length})
                      </button>
                      <button
                        onClick={() => setFeatureTab('recyclebin')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          featureTab === 'recyclebin'
                            ? 'border-netlify-600 text-netlify-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Recycle Bin ({(contentData.features || []).filter((f: any) => f.deleted).length})
                      </button>
                    </div>
                    {featureTab === 'recyclebin' && (contentData.features || []).filter((f: any) => f.deleted).length > 0 && (
                      <button
                        onClick={handleEmptyFeatureRecycleBin}
                        className="px-3 py-1 text-sm border border-red-300 rounded-lg text-red-700 hover:bg-red-50"
                      >
                        Empty Recycle Bin
                      </button>
                    )}
                  </div>

                  {(() => {
                    let filteredFeatures = contentData.features || []
                    if (featureTab === 'active') {
                      filteredFeatures = filteredFeatures.filter((f: any) => !f.archived && !f.deleted)
                    } else if (featureTab === 'archived') {
                      filteredFeatures = filteredFeatures.filter((f: any) => f.archived && !f.deleted)
                    } else if (featureTab === 'recyclebin') {
                      filteredFeatures = filteredFeatures.filter((f: any) => f.deleted)
                    }

                    // Apply product filter
                    if (featureProductFilter !== 'all') {
                      if (featureProductFilter === 'unassigned') {
                        filteredFeatures = filteredFeatures.filter((f: any) => !f.productIds || f.productIds.length === 0)
                      } else {
                        filteredFeatures = filteredFeatures.filter((f: any) =>
                          f.productIds && f.productIds.includes(featureProductFilter)
                        )
                      }
                    }

                    return filteredFeatures.length === 0 ? (
                      <div className="p-6 text-center border border-gray-200 rounded-lg">
                        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm text-gray-500">
                          {featureTab === 'active' ? 'No active features found.' :
                           featureTab === 'archived' ? 'No archived features.' :
                           'Recycle bin is empty.'}
                        </p>
                      </div>
                    ) : (
                      filteredFeatures.map((item: any) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{item.title}</h4>
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                              <div className="mt-2 text-xs text-gray-500">
                                <span className="mr-4">Category: {item.category}</span>
                                <span className="mr-4">Order: {item.displayOrder}</span>
                                <span className="mr-4">Icon: {item.icon}</span>
                              </div>
                              {item.productIds && item.productIds.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  <span className="text-xs text-gray-500">Products:</span>
                                  {item.productIds.map((productId: string) => {
                                    const product = contentData.products?.find((p: any) => p.id === productId)
                                    return product ? (
                                      <span key={productId} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                                        <Package className="h-3 w-3" />
                                        {product.name}
                                      </span>
                                    ) : null
                                  })}
                                </div>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              {featureTab === 'active' && (
                                <>
                                  <button
                                    onClick={() => handleEditContent(item)}
                                    className="p-2 text-netlify-600 hover:text-netlify-700"
                                    title="Edit feature"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleArchiveFeature(item.id)}
                                    className="p-2 text-gray-600 hover:text-gray-700"
                                    title="Archive feature"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFeature(item.id)}
                                    className="p-2 text-red-600 hover:text-red-700"
                                    title="Move to recycle bin"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              {featureTab === 'archived' && (
                                <>
                                  <button
                                    onClick={() => handleUnarchiveFeature(item.id)}
                                    className="p-2 text-netlify-600 hover:text-netlify-700"
                                    title="Unarchive feature"
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFeature(item.id)}
                                    className="p-2 text-red-600 hover:text-red-700"
                                    title="Move to recycle bin"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              {featureTab === 'recyclebin' && (
                                <>
                                  <button
                                    onClick={() => handleRestoreFeature(item.id)}
                                    className="p-2 text-green-600 hover:text-green-700"
                                    title="Restore feature"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handlePermanentDeleteFeature(item.id)}
                                    className="p-2 text-red-600 hover:text-red-700"
                                    title="Permanently delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )
                  })()}
                  </div>

              {trustCenterSubTab === 'hero' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Hero Sections</h4>

                  {[
                    { key: 'hero', label: 'Main Trust Center', data: contentData.hero },
                    { key: 'hero-terms', label: 'Terms of Use', data: contentData.heroTerms },
                    { key: 'hero-privacy', label: 'Privacy Policy', data: contentData.heroPrivacy },
                    { key: 'hero-dmca-submit', label: 'DMCA Submit', data: contentData.heroDmcaSubmit },
                    { key: 'hero-dmca-counter', label: 'DMCA Counter', data: contentData.heroDmcaCounter }
                  ].map((heroSection) => (
                    <div key={heroSection.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-medium text-gray-900">{heroSection.label}</h5>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {heroSection.key}
                            </span>
                          </div>
                          {heroSection.data ? (
                            <>
                              <h6 className="font-medium text-gray-800">{heroSection.data.title} {heroSection.data.subtitle}</h6>
                              <p className="text-sm text-gray-600 mt-1">{heroSection.data.description}</p>
                              <div className="mt-2 text-xs text-gray-500">
                                Stats: {(heroSection.data.stats || []).length} items
                              </div>
                            </>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No hero content configured</p>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedHeroType(heroSection.key as 'hero' | 'hero-terms' | 'hero-privacy' | 'hero-dmca-submit' | 'hero-dmca-counter')
                              setContentForm(heroSection.data || { title: '', subtitle: '', description: '', stats: [] })
                              setEditingContent(heroSection.data)
                              setShowContentForm(true)
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600"
                            title={heroSection.data ? 'Edit hero content' : 'Create hero content'}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

                  {/* Compliance Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <SectionHeading id="compliance" className="text-2xl font-bold text-gray-900">
                        Compliance
                      </SectionHeading>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setAIGeneratorType('badges')
                            setShowAIGenerator(true)
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors inline-flex items-center text-sm font-medium"
                        >
                          <Sparkles className="h-4 w-4 mr-1.5" />
                          Generate with AI
                        </button>
                        <button
                          onClick={async () => {
                          if (!confirm('Scan documents, security features, and subprocessors for attestations?\n\nThis will analyze all your content to find compliance certifications and create compliance badges.')) return

                          setLoading(true)
                          try {
                            const attestationPatterns = [
                              { name: 'SOC 2 Type II', patterns: ['soc 2 type ii', 'soc 2 type 2', 'soc 2', 'soc2', 'soc ii'], icon: 'Shield', status: 'Certified' },
                              { name: 'SOC 2 Type I', patterns: ['soc 2 type i', 'soc 2 type 1', 'soc i'], icon: 'Shield', status: 'Certified' },
                              { name: 'SOC 1', patterns: ['soc 1', 'soc1', 'ssae 18'], icon: 'Shield', status: 'Certified' },
                              { name: 'ISO 27001', patterns: ['iso 27001', 'iso27001', 'iso/iec 27001', 'isms'], icon: 'Award', status: 'Certified' },
                              { name: 'ISO 27017', patterns: ['iso 27017', 'iso27017', 'iso/iec 27017'], icon: 'Award', status: 'Certified' },
                              { name: 'ISO 27018', patterns: ['iso 27018', 'iso27018', 'iso/iec 27018'], icon: 'Award', status: 'Certified' },
                              { name: 'ISO 9001', patterns: ['iso 9001', 'iso9001', 'quality management'], icon: 'Award', status: 'Certified' },
                              { name: 'PCI DSS', patterns: ['pci dss', 'pci-dss', 'payment card industry'], icon: 'CreditCard', status: 'Compliant' },
                              { name: 'HIPAA', patterns: ['hipaa compliant', 'hipaa', 'health insurance portability'], icon: 'Heart', status: 'Compliant' },
                              { name: 'GDPR', patterns: ['gdpr compliant', 'gdpr', 'general data protection'], icon: 'Lock', status: 'Compliant' },
                              { name: 'CCPA', patterns: ['ccpa compliant', 'ccpa', 'california consumer privacy'], icon: 'Lock', status: 'Compliant' },
                              { name: 'FedRAMP', patterns: ['fedramp', 'federal risk'], icon: 'Flag', status: 'Authorized' }
                            ]

                            const foundBadges: any[] = []
                            const existingBadges = badges.map((b: any) => b.name.toLowerCase())

                            let combinedText = ''
                            documents?.forEach((doc: any) => {
                              combinedText += ` ${doc.title} ${doc.category} `
                            })
                            contentData.features?.forEach((feature: any) => {
                              combinedText += ` ${feature.title} ${feature.description} ${feature.details?.join(' ')} `
                            })
                            contentData.subprocessors?.forEach((sub: any) => {
                              sub.attestations?.forEach((att: any) => {
                                combinedText += ` ${att.name} `
                              })
                            })

                            const textLower = combinedText.toLowerCase()

                            for (const attestation of attestationPatterns) {
                              if (existingBadges.includes(attestation.name.toLowerCase())) continue

                              for (const pattern of attestation.patterns) {
                                if (textLower.includes(pattern)) {
                                  foundBadges.push({
                                    id: crypto.randomUUID(),
                                    name: attestation.name,
                                    icon: attestation.icon,
                                    description: `${attestation.status} - Auto-detected from documents and features`,
                                    reportAvailable: false
                                  })
                                  break
                                }
                              }
                            }

                            if (foundBadges.length === 0) {
                              alert('No new attestations found.\n\nTip: Upload compliance documents (like "SOC 2 Report") or add security features that mention certifications.')
                              setLoading(false)
                              return
                            }

                            const badgesList = foundBadges.map(b => b.name).join('\n  • ')
                            if (!confirm(`Found ${foundBadges.length} attestation(s):\n  • ${badgesList}\n\nAdd these as compliance badges?`)) {
                              setLoading(false)
                              return
                            }

                            let savedCount = 0
                            for (const badge of foundBadges) {
                              try {
                                const response = await fetch('/api/badges', {
                                  method: 'POST',
                                  headers: getHeaders(),
                                  body: JSON.stringify({
                                    ...badge,
                                    isActive: true
                                  })
                                })
                                if (response.ok) savedCount++
                              } catch (err) {
                                console.error('Error saving badge:', err)
                              }
                            }

                            await fetchAllData()
                            triggerContentRefresh()
                            alert(`Successfully added ${savedCount} compliance badge(s)!`)
                          } catch (err) {
                            console.error('Mining error:', err)
                            alert('Failed to mine attestations')
                          } finally {
                            setLoading(false)
                          }
                        }}
                        disabled={loading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center text-sm"
                      >
                        <Search className="h-4 w-4 mr-1" />
                        Mine Attestations
                      </button>
                      </div>
                    </div>

                  <div className="space-y-3">
                  {badges.map((item: any) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          <div className="mt-2 text-xs text-gray-500">
                            <span className="mr-4">Status: {item.status}</span>
                            <span className="mr-4">Year: {item.year}</span>
                            <span className="mr-4">Icon: {item.icon}</span>
                            {item.reportAvailable && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                Report Available
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditContent(item)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteContent(item.id)}
                            className="p-2 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                  </div>

                  {/* Legal Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <SectionHeading id="legal" className="text-2xl font-bold text-gray-900">
                        Legal Pages
                      </SectionHeading>
                      <button
                        onClick={() => {
                          setAIGeneratorType('legal')
                          setShowAIGenerator(true)
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors inline-flex items-center text-sm font-medium"
                      >
                        <Sparkles className="h-4 w-4 mr-1.5" />
                        Generate with AI
                      </button>
                    </div>

                    <p className="text-sm text-gray-600 mb-6">
                      Manage your legal pages. Changes must be explicitly published to go live on your trust center.
                    </p>

                    <div className="grid gap-4">
                      {[
                        { id: 'terms', name: 'Terms of Use', icon: FileText, path: '/terms' },
                        { id: 'privacy', name: 'Privacy Policy', icon: Lock, path: '/privacy' },
                        { id: 'dmca-submit', name: 'DMCA Takedown', icon: AlertCircle, path: '/dmca/submit' },
                        { id: 'dmca-counter', name: 'DMCA Counter Notice', icon: AlertCircle, path: '/dmca/counter' }
                      ].map((page) => {
                        const Icon = page.icon
                        const isPublished = contentData?.legalPages?.[page.id]?.published ?? false
                        return (
                          <div
                            key={page.id}
                            className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="p-2 bg-blue-50 rounded-lg">
                                  <Icon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">{page.name}</h4>
                                    {isPublished ? (
                                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                                        Published
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full font-medium">
                                        Draft
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {page.path}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  // Determine preview URL based on available configuration
                                  let previewUrl: string | null = null
                                  if (displayTenant?.settings?.customDomain) {
                                    previewUrl = `https://${displayTenant.settings.customDomain}${page.path}`
                                  } else if (displayTenant?.deploymentUrl) {
                                    previewUrl = `${displayTenant.deploymentUrl}${page.path}`
                                  }

                                  // Only disable if page is not published or no URL is available
                                  const canPreview = isPublished && previewUrl

                                  return canPreview ? (
                                    <a
                                      href={previewUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                                    >
                                      <Eye className="h-4 w-4" />
                                      Preview
                                    </a>
                                  ) : (
                                    <button
                                      disabled
                                      className="px-3 py-1.5 text-sm border border-gray-300 text-gray-400 rounded-lg bg-gray-50 cursor-not-allowed flex items-center gap-1"
                                      title={!isPublished ? "Page not published" : "No site URL configured"}
                                    >
                                      <Eye className="h-4 w-4" />
                                      Preview
                                    </button>
                                  )
                                })()}
                                <button
                                  onClick={() => {
                                    // Navigate to page content editor or open modal
                                    window.open(`/admin#legal-${page.id}`, '_blank')
                                  }}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                                >
                                  <Edit className="h-4 w-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={async () => {
                                    const newStatus = !isPublished
                                    try {
                                      await fetch('/api/legal-page-publish', {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
                                        },
                                        body: JSON.stringify({
                                          pageId: page.id,
                                          published: newStatus
                                        })
                                      })
                                      await fetchAllData()
                                    } catch (error) {
                                      console.error('Failed to toggle publish status:', error)
                                      alert('Failed to update publish status')
                                    }
                                  }}
                                  className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 font-medium ${
                                    isPublished
                                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      : 'bg-green-600 text-white hover:bg-green-700'
                                  }`}
                                >
                                  {isPublished ? 'Unpublish' : 'Publish'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-yellow-900">Publishing Required</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            Legal pages must be explicitly published to appear on your trust center. Draft pages are only visible to administrators. Click "Publish" to make a page live.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {trustCenterSubTab === 'settings' && (
                <div className="space-y-6">
                  {/* Page Description */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-blue-900 mb-2">Trust Center Configuration</h2>
                    <p className="text-sm text-blue-800">
                      Configure your Trust Center's core settings including company information, branding,
                      contact details, and system-wide preferences.
                    </p>
                  </div>

                  {/* Site Scraper Section */}
                  <div className="border border-gray-200 rounded-lg p-6 bg-gradient-to-r from-purple-50 to-blue-50">
                    <div className="flex items-center mb-4">
                      <Sparkles className="h-6 w-6 text-purple-600 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900">Auto-Configure from Website</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Automatically extract company information, branding, and settings from your website to quickly configure your trust center.
                    </p>

                    <div className="space-y-4">
                      <div className="flex space-x-2">
                        <div className="flex-1">
                          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
                            <Globe className="h-5 w-5 text-gray-400 ml-3" />
                            <input
                              type="url"
                              value={scrapeUrl}
                              onChange={(e) => setScrapeUrl(e.target.value)}
                              placeholder="https://yourcompany.com"
                              className="flex-1 px-3 py-2 outline-none rounded-lg"
                              disabled={scraping}
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleScrapePreview}
                          disabled={scraping || !scrapeUrl}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
                        >
                          {scraping ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                              Scraping...
                            </>
                          ) : (
                            'Preview'
                          )}
                        </button>
                        <button
                          onClick={handleScrapeApply}
                          disabled={scraping || !scrapeUrl}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          Apply Settings
                        </button>
                      </div>

                      {scrapeError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-800">{scrapeError}</p>
                        </div>
                      )}

                      {scrapeResult && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold text-gray-900">Extracted Configuration</h5>
                            <span className="text-xs text-gray-500">
                              Confidence: {Math.round(scrapeResult.confidence * 100)}%
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {scrapeResult.companyName && (
                              <div>
                                <label className="font-medium text-gray-700">Company Name</label>
                                <p className="text-gray-600">{scrapeResult.companyName}</p>
                              </div>
                            )}
                            {scrapeResult.logoUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Logo URL</label>
                                <p className="text-gray-600 truncate">{scrapeResult.logoUrl}</p>
                              </div>
                            )}
                            {scrapeResult.primaryColor && (
                              <div>
                                <label className="font-medium text-gray-700">Primary Color</label>
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 rounded border" style={{ backgroundColor: scrapeResult.primaryColor }}></div>
                                  <p className="text-gray-600">{scrapeResult.primaryColor}</p>
                                </div>
                              </div>
                            )}
                            {scrapeResult.secondaryColor && (
                              <div>
                                <label className="font-medium text-gray-700">Secondary Color</label>
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 rounded border" style={{ backgroundColor: scrapeResult.secondaryColor }}></div>
                                  <p className="text-gray-600">{scrapeResult.secondaryColor}</p>
                                </div>
                              </div>
                            )}
                            {scrapeResult.faviconUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Favicon URL</label>
                                <p className="text-gray-600 truncate">{scrapeResult.faviconUrl}</p>
                              </div>
                            )}
                            {scrapeResult.companyWebsite && (
                              <div>
                                <label className="font-medium text-gray-700">Company Website</label>
                                <p className="text-gray-600 truncate">{scrapeResult.companyWebsite}</p>
                              </div>
                            )}
                            {scrapeResult.description && (
                              <div className="md:col-span-2">
                                <label className="font-medium text-gray-700">Description</label>
                                <p className="text-gray-600">{scrapeResult.description}</p>
                              </div>
                            )}
                            {scrapeResult.email && (
                              <div>
                                <label className="font-medium text-gray-700">Support Email</label>
                                <p className="text-gray-600">{scrapeResult.email}</p>
                              </div>
                            )}
                            {scrapeResult.securityEmail && (
                              <div>
                                <label className="font-medium text-gray-700">Security Email</label>
                                <p className="text-gray-600">{scrapeResult.securityEmail}</p>
                              </div>
                            )}
                            {scrapeResult.privacyPolicyUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Privacy Policy</label>
                                <p className="text-gray-600 truncate">{scrapeResult.privacyPolicyUrl}</p>
                              </div>
                            )}
                            {scrapeResult.termsOfServiceUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Terms of Service</label>
                                <p className="text-gray-600 truncate">{scrapeResult.termsOfServiceUrl}</p>
                              </div>
                            )}
                            {scrapeResult.cookiePolicyUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Cookie Policy</label>
                                <p className="text-gray-600 truncate">{scrapeResult.cookiePolicyUrl}</p>
                              </div>
                            )}
                            {scrapeResult.soc2ReportUrl && (
                              <div>
                                <label className="font-medium text-gray-700">SOC 2 Report</label>
                                <p className="text-gray-600 truncate">{scrapeResult.soc2ReportUrl}</p>
                              </div>
                            )}
                            {scrapeResult.iso27001CertUrl && (
                              <div>
                                <label className="font-medium text-gray-700">ISO 27001 Certificate</label>
                                <p className="text-gray-600 truncate">{scrapeResult.iso27001CertUrl}</p>
                              </div>
                            )}
                            {scrapeResult.subprocessorsPageUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Subprocessors Page</label>
                                <p className="text-gray-600 truncate">{scrapeResult.subprocessorsPageUrl}</p>
                              </div>
                            )}
                            {scrapeResult.statusPageUrl && (
                              <div>
                                <label className="font-medium text-gray-700">Status Page URL</label>
                                <p className="text-gray-600 truncate">{scrapeResult.statusPageUrl}</p>
                              </div>
                            )}
                            {scrapeResult.phone && (
                              <div>
                                <label className="font-medium text-gray-700">Phone</label>
                                <p className="text-gray-600">{scrapeResult.phone}</p>
                              </div>
                            )}
                            {scrapeResult.address && (
                              <div>
                                <label className="font-medium text-gray-700">Corporate Address</label>
                                <p className="text-gray-600">{scrapeResult.address}</p>
                              </div>
                            )}
                            {scrapeResult.location && (
                              <div>
                                <label className="font-medium text-gray-700">Company Location</label>
                                <p className="text-gray-600">{scrapeResult.location}</p>
                              </div>
                            )}
                          </div>
                          {scrapeResult.errors && scrapeResult.errors.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <label className="font-medium text-gray-700 text-xs">Warnings:</label>
                              <ul className="text-xs text-orange-600 list-disc list-inside">
                                {scrapeResult.errors.map((err: string, i: number) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Configuration Section */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-lg font-semibold text-gray-900">Company Configuration</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditContent(contentData.settings)}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center text-sm"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Settings
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Company Name</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.companyName || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Company Website</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.companyUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Support Email</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.supportEmail || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Security Email</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.securityEmail || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Status Page URL</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.statusPageUrl || 'Not set'}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Brand Name</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.brandName || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Company Location</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.companyLocation || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Corporate Address</label>
                          <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{contentData.settings?.corporateAddress || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Document URLs Section */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Document URLs</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Privacy Policy URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.privacyPolicyUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Terms of Service URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.termsOfServiceUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Cookie Policy URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.cookiePolicyUrl || 'Not set'}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">SOC 2 Report URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.soc2ReportUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">ISO 27001 Certificate URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.iso27001CertificateUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Subprocessors Page URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.subprocessorsPageUrl || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hero Content Configuration Section */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Hero Content Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Subprocessors Hero Title</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.subprocessorsHeroTitle || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Subprocessors Hero Subtitle</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.subprocessorsHeroSubtitle || 'Not set'}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Subprocessors Hero Description</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.subprocessorsHeroDescription || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Display Content Section */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Display Content</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Last Updated Text</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.lastUpdatedText || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">View Full List Text</label>
                          <p className="mt-1 text-sm text-gray-900">{contentData.settings?.viewFullListText || 'View Full List'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Theme/Brand Configuration Section */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Theme/Brand Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Primary Color</label>
                          <div className="mt-1 flex items-center space-x-2">
                            <div className="w-6 h-6 rounded border border-gray-300" style={{backgroundColor: contentData.settings?.primaryColor || '#2E51ED'}}></div>
                            <span className="text-sm text-gray-900">{contentData.settings?.primaryColor || '#2E51ED'}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Secondary Color</label>
                          <div className="mt-1 flex items-center space-x-2">
                            <div className="w-6 h-6 rounded border border-gray-300" style={{backgroundColor: contentData.settings?.secondaryColor || '#05BDBA'}}></div>
                            <span className="text-sm text-gray-900">{contentData.settings?.secondaryColor || '#05BDBA'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Logo URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.logoUrl || 'Not set'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500 uppercase tracking-wider">Favicon URL</label>
                          <p className="mt-1 text-sm text-gray-900 break-all">{contentData.settings?.faviconUrl || 'Not set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {trustCenterSubTab === 'features' && (
                <div className="space-y-6">
                  {/* Changelog Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <SectionHeading id="changelog" className="text-2xl font-bold text-gray-900">
                          Changelog
                        </SectionHeading>
                        <p className="text-sm text-gray-600 mt-1">
                          Manage your product changelog entries and settings
                        </p>
                      </div>
                      <a
                        href={
                          displayTenant?.settings?.customDomain
                            ? `https://${displayTenant.settings.customDomain}/changelog`
                            : displayTenant?.deploymentUrl
                            ? `${displayTenant.deploymentUrl}/changelog`
                            : '#'
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        View Public Changelog
                      </a>
                    </div>

                    {/* Sub-tabs */}
                    <div className="border-b border-gray-200">
                      <nav className="flex space-x-8">
                        {[
                          { key: 'entries', label: 'Entries', icon: FileText },
                          { key: 'settings', label: 'Settings', icon: Settings }
                        ].map((subTab) => {
                          const IconComponent = subTab.icon
                          return (
                            <button
                              key={subTab.key}
                              onClick={() => setChangelogSubTab(subTab.key as any)}
                              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                                changelogSubTab === subTab.key
                                  ? 'border-primary-500 text-primary-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              <IconComponent className="h-4 w-4 mr-2" />
                              {subTab.label}
                            </button>
                          )
                        })}
                      </nav>
                    </div>
                  </div>

                  {/* Entries Sub-tab */}
                  {changelogSubTab === 'entries' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Changelog Entries</h3>
                        <button
                          onClick={() => {
                            // TODO: Open create entry modal
                            alert('Create entry functionality coming soon! Use the API at /api/admin/changelog/entries')
                          }}
                          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          New Entry
                        </button>
                      </div>

                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">
                          Changelog management UI is being built
                        </p>
                        <div className="max-w-2xl mx-auto text-left bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-700 mb-2 font-medium">For now, you can:</p>
                          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                            <li>View entries at <code className="bg-white px-2 py-0.5 rounded">/changelog</code> on your trust center</li>
                            <li>Create entries via API: <code className="bg-white px-2 py-0.5 rounded">POST /api/admin/changelog/entries</code></li>
                            <li>List entries: <code className="bg-white px-2 py-0.5 rounded">GET /api/admin/changelog/entries</code></li>
                            <li>Update entries: <code className="bg-white px-2 py-0.5 rounded">PUT /api/admin/changelog/entries/:id</code></li>
                            <li>Delete entries: <code className="bg-white px-2 py-0.5 rounded">DELETE /api/admin/changelog/entries/:id</code></li>
                          </ul>
                          <p className="text-xs text-gray-500 mt-3">
                            Full admin UI with entry editor, GitHub integration, and AI generation coming in Phase 2-5
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Settings Sub-tab */}
                  {changelogSubTab === 'settings' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-6">Changelog Settings</h3>

                      <div className="space-y-6">
                        {/* Basic Settings */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-4">Display Settings</h4>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Show Dates</label>
                                <p className="text-xs text-gray-500">Display entry dates on public changelog</p>
                              </div>
                              <input
                                type="checkbox"
                                defaultChecked
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Category Labels</label>
                                <p className="text-xs text-gray-500">Show category labels on entries</p>
                              </div>
                              <input
                                type="checkbox"
                                defaultChecked
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Advanced Features */}
                        <div className="border-t border-gray-200 pt-6">
                          <h4 className="text-sm font-medium text-gray-900 mb-4">Advanced Features</h4>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">GitHub Integration</label>
                                <p className="text-xs text-gray-500">Auto-generate from GitHub releases</p>
                              </div>
                              <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                                Configure
                              </button>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">AI Content Generation</label>
                                <p className="text-xs text-gray-500">Generate audience-specific variations with AI</p>
                              </div>
                              <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                                Configure
                              </button>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">RSS Feed</label>
                                <p className="text-xs text-gray-500">Enable RSS feed for changelog updates</p>
                              </div>
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                                <p className="text-xs text-gray-500">Notify subscribers of new entries</p>
                              </div>
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-gray-200 pt-6">
                          <button
                            className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                            onClick={() => alert('Settings save functionality coming soon!')}
                          >
                            Save Settings
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Privacy Section */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <SectionHeading id="privacy" className="text-2xl font-bold text-gray-900 mb-6">
                      Privacy
                    </SectionHeading>

                  {/* Privacy Sub-Tabs */}
                  <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
                    <div className="flex space-x-8 px-6">
                      {[
                        { key: 'requests', label: 'Requests', icon: FileText },
                        { key: 'portal', label: 'Portal', icon: Shield },
                        { key: 'cookies', label: 'Cookie Settings', icon: Cookie },
                        { key: 'settings', label: 'Privacy Settings', icon: Settings },
                        { key: 'retention', label: 'Data Retention', icon: Database }
                      ].map((subTab) => {
                        const IconComponent = subTab.icon
                        return (
                          <button
                            key={subTab.key}
                            data-tour-subtab={`privacy-${subTab.key}`}
                            onClick={() => setPrivacySubTab(subTab.key as any)}
                            className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                              privacySubTab === subTab.key
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <IconComponent className="h-4 w-4 mr-2" />
                            {subTab.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Privacy Requests Sub-Tab */}
                  {privacySubTab === 'requests' && (
                    <div data-tour="privacy-requests">
                      <PrivacyRequestsManager />
                    </div>
                  )}

                  {/* Privacy Portal Sub-Tab */}
                  {privacySubTab === 'portal' && (
                    <div data-tour="privacy-portal">
                      <AdminPrivacyPortal />
                    </div>
                  )}

                  {/* Cookie Settings Sub-Tab */}
                  {privacySubTab === 'cookies' && (
                    <div data-tour="privacy-cookies">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="mb-6">
                          <h2 className="text-lg font-semibold text-gray-900">Cookie Settings</h2>
                          <p className="text-sm text-gray-600 mt-1">Configure cookie categories and consent preferences</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                          <div className="flex items-start">
                            <Cookie className="h-6 w-6 text-blue-600 mt-1 mr-4" />
                            <div>
                              <h3 className="text-sm font-semibold text-blue-900 mb-2">Cookie Management Coming Soon</h3>
                              <p className="text-sm text-blue-800">
                                Configure cookie categories (Necessary, Analytics, Marketing, Preferences),
                                customize consent banner appearance, and view consent analytics.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Privacy Settings Sub-Tab */}
                  {privacySubTab === 'settings' && (
                    <div data-tour="privacy-settings">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="mb-6">
                          <h2 className="text-lg font-semibold text-gray-900">Privacy Settings</h2>
                          <p className="text-sm text-gray-600 mt-1">Configure privacy-related URLs and contact information</p>
                        </div>

                        <div className="space-y-6">
                          {/* Privacy URLs Section */}
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Privacy & Legal URLs</h3>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Privacy Policy URL
                                </label>
                                <input
                                  type="text"
                                  placeholder="/privacy"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                  disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">Managed in System Settings</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Cookie Policy URL
                                </label>
                                <input
                                  type="text"
                                  placeholder="/cookies"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                  disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">Managed in System Settings</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Data Processing Agreement (DPA) URL
                                </label>
                                <input
                                  type="text"
                                  placeholder="/dpa"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                  disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">Managed in System Settings</p>
                              </div>
                            </div>
                          </div>

                          {/* Privacy Contact Information */}
                          <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Privacy Contact Information</h3>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Data Protection Officer (DPO) Email
                                </label>
                                <input
                                  type="email"
                                  placeholder="dpo@company.com"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                  disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">Managed in System Settings</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Privacy Inquiries Email
                                </label>
                                <input
                                  type="email"
                                  placeholder="privacy@company.com"
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                  disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">Managed in System Settings</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                              💡 <strong>Tip:</strong> Privacy-related URLs and email addresses are managed in the Settings tab under System Settings.
                              Navigate to Settings → System Configuration to update these values.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Data Retention Sub-Tab */}
                  {privacySubTab === 'retention' && (
                    <div data-tour="privacy-retention">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="mb-6">
                          <h2 className="text-lg font-semibold text-gray-900">Data Retention Policies</h2>
                          <p className="text-sm text-gray-600 mt-1">Configure data retention periods and auto-deletion schedules</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                          <div className="flex items-start">
                            <Database className="h-6 w-6 text-blue-600 mt-1 mr-4" />
                            <div>
                              <h3 className="text-sm font-semibold text-blue-900 mb-2">Data Retention Configuration Coming Soon</h3>
                              <p className="text-sm text-blue-800 mb-3">
                                Configure automated data retention and deletion policies to ensure compliance with GDPR, CCPA, and other privacy regulations.
                              </p>
                              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                                <li>Set retention periods for different data types</li>
                                <li>Configure auto-deletion schedules</li>
                                <li>View retention compliance reports</li>
                                <li>Audit data lifecycle events</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Documents Sub-Tab */}
            {trustCenterSubTab === 'documents' && (
              <div className="space-y-6">
                {/* Page Description */}
                <div id="documents" className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">Document Management</h2>
                  <p className="text-sm text-blue-800">
                    Upload and manage security and compliance documents for your Trust Center.
                    Documents can be public or restricted with NDA requirements and watermarking.
                  </p>
                </div>

                {/* AI Policy Generator Button */}
                {featureFlags.policyGenerator && (
                <button
                  onClick={() => navigate('/admin/policy-generator')}
                  className="w-full flex items-center p-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 hover:border-blue-400 hover:shadow-md transition-all text-left"
                >
                  <Sparkles className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <h3 className="font-medium text-gray-900">AI Policy Generator</h3>
                    <p className="text-sm text-gray-600">Generate compliance policies and documents using AI</p>
                  </div>
                </button>
                )}

                {/* Document Upload */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Documents</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload security and compliance documents such as policies, procedures, certifications, attestations, reports, and agreements.
                    Supported formats: <span className="font-medium">PDF, DOC, DOCX, JPG, PNG</span>.
                    Document titles, categories, and types will be automatically detected from filenames.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Files (one or more)
                      </label>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || [])
                          setBulkFiles(files)
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        accept=".pdf,.doc,.docx,.jpg,.png"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Titles, categories, and document types will be auto-detected from filenames
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                      <select
                        value={bulkOptions.visibility}
                        onChange={(e) => setBulkOptions(prev => ({ ...prev, visibility: e.target.value as 'public' | 'gated' | 'admin' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="public">Public - Listed in trust center</option>
                        <option value="gated">Gated - Requires login to view</option>
                        <option value="admin">Admin Only - Not visible in trust center</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bulkOptions.ndaRequired}
                          onChange={(e) => setBulkOptions(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">NDA Required</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bulkOptions.watermarked}
                          onChange={(e) => setBulkOptions(prev => ({ ...prev, watermarked: e.target.checked }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Watermarked</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bulkOptions.redacted}
                          onChange={(e) => setBulkOptions(prev => ({ ...prev, redacted: e.target.checked }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Redacted (Limited Pages)</span>
                      </label>
                    </div>

                    {bulkOptions.redacted && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Allowed Pages
                        </label>
                        <input
                          type="text"
                          value={bulkOptions.allowedPages}
                          onChange={(e) => setBulkOptions(prev => ({ ...prev, allowedPages: e.target.value }))}
                          placeholder="1,3,5 or 1-5,10,15-20"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                        />
                        <p className="mt-2 text-xs text-amber-800">
                          <strong>Examples:</strong> "1,3,5" (specific pages), "1-5,10,15-20" (ranges)
                          <br/><strong>Note:</strong> Only specified pages will be available for download. Viewing will be protected against screenshots.
                        </p>
                      </div>
                    )}

                    {bulkFiles.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">
                          Selected Files ({bulkFiles.length})
                        </h3>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {bulkFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <span className="text-gray-700 truncate flex-1">{file.name}</span>
                              <span className="text-xs text-gray-500 ml-2 bg-gray-200 px-2 py-0.5 rounded">
                                {matchCategory(file.name)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={handleBulkUpload}
                        disabled={bulkUploading || bulkFiles.length === 0}
                        className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {bulkUploading ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin mr-2" />
                            Uploading {bulkFiles.length} files...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload {bulkFiles.length} {bulkFiles.length === 1 ? 'File' : 'Files'}
                          </>
                        )}
                      </button>
                      {bulkFiles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setBulkFiles([])}
                          disabled={bulkUploading}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Documents List */}
                <div>
                  {/* Active/Archived Tabs */}
                  <div className="border-b border-gray-200 mb-4">
                    <div className="flex space-x-8">
                      <button
                        onClick={() => setDocumentTab('active')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          documentTab === 'active'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <FileText className="h-4 w-4 inline mr-2" />
                        Active Documents ({documents.filter(d => d && !d.archived && !d.deleted).length})
                      </button>
                      <button
                        onClick={() => setDocumentTab('archived')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          documentTab === 'archived'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Archive className="h-4 w-4 inline mr-2" />
                        Archived ({documents.filter(d => d && d.archived && !d.deleted).length})
                      </button>
                      <button
                        onClick={() => setDocumentTab('recyclebin')}
                        className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                          documentTab === 'recyclebin'
                            ? 'border-red-600 text-red-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Trash2 className="h-4 w-4 inline mr-2" />
                        Recycle Bin ({documents.filter(d => d && d.deleted).length})
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <SectionHeading
                      id={documentTab === 'active' ? 'active-documents' : documentTab === 'archived' ? 'archived-documents' : 'documents-recycle-bin'}
                      className="text-lg font-semibold text-gray-900"
                      as="h3"
                    >
                      {documentTab === 'active' ? 'Active Documents' : documentTab === 'archived' ? 'Archived Documents' : 'Recycle Bin'} ({filteredDocuments.length})
                    </SectionHeading>
                    <div className="flex items-center space-x-2">
                      {documentTab === 'recyclebin' && documents.filter(d => d && d.deleted).length > 0 && (
                        <button
                          onClick={handleEmptyRecycleBin}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Empty Recycle Bin
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (!window.confirm('This will migrate all documents from the old multi-version storage format to the new single-record format. This is a one-time operation. Continue?')) {
                            return
                          }

                          setLoading(true)
                          try {
                            const token = localStorage.getItem('auth-token')
                            const response = await fetch('/api/documents/migrate', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              }
                            })

                            if (!response.ok) {
                              throw new Error(`Migration failed: ${response.status} ${response.statusText}`)
                            }

                            const result = await response.json()
                            console.log('[Migration] Result:', result)

                            if (result.success) {
                              alert(`Migration complete!\n\nMigrated: ${result.result.migrated}\nSkipped (already migrated): ${result.result.skipped}\nErrors: ${result.result.errors.length}\n\n${result.message}`)
                              await fetchAllData()
                              triggerContentRefresh()
                            } else {
                              alert(`Migration failed: ${result.error}`)
                            }
                          } catch (err: any) {
                            console.error('[Migration] Error:', err)
                            alert(`Migration error: ${err.message}`)
                          } finally {
                            setLoading(false)
                          }
                        }}
                        disabled={loading}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center"
                      >
                        <Database className="h-4 w-4 mr-1" />
                        Migrate Storage
                      </button>
                      <button
                      onClick={async () => {
                        const untaggedCount = documents.filter(d => d && !d.documentType).length
                        const taggedCount = documents.filter(d => d && d.documentType).length

                        const message = `Document Status:\n• ${untaggedCount} untagged documents\n• ${taggedCount} already tagged documents\n\nChoose scan mode:\nOK = Rescan ALL documents (overwrites existing tags)\nCancel = Scan only untagged documents`

                        const rescanAll = window.confirm(message)

                        setLoading(true)
                        try {
                          let updatedCount = 0
                          const detectionResults: { title: string, detected: string, oldType?: string }[] = []

                          for (const doc of documents) {
                            // Skip if already has a type and we're not rescanning all
                            if (!rescanAll && doc.documentType) continue

                            console.log(`🔍 Scanning "${doc.title}"${doc.documentType ? ` (currently: ${doc.documentType})` : ''}...`)

                            // Call backend to scan document content
                            try {
                              const scanResponse = await fetch('/api/scan-document-content', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ documentId: doc.id })
                              })

                              if (!scanResponse.ok) {
                                console.error(`Failed to scan ${doc.title}`)
                                continue
                              }

                              const scanResult = await scanResponse.json()
                              const detectedType = scanResult.detectedType
                              const attestationTags = scanResult.attestationTags || []

                              detectionResults.push({
                                title: doc.title,
                                detected: detectedType,
                                oldType: doc.documentType
                              })
                              const tagsDisplay = attestationTags.length > 0 ? ` [${attestationTags.join(', ')}]` : ''
                              console.log(`   → ${detectedType}${tagsDisplay} (${scanResult.extractedTextLength} chars scanned)`)

                              // Update document with detected type and attestation tags
                              const updateResponse = await fetch(`/api/documents/${doc.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  documentType: detectedType,
                                  attestationTags: attestationTags
                                })
                              })

                              if (updateResponse.ok) {
                                updatedCount++
                              }
                            } catch (err) {
                              console.error(`Error scanning document ${doc.id}:`, err)
                            }
                          }

                          await fetchAllData()
    triggerContentRefresh()

                          // Show summary of what was detected
                          const typeCounts = detectionResults.reduce((acc, r) => {
                            acc[r.detected] = (acc[r.detected] || 0) + 1
                            return acc
                          }, {} as Record<string, number>)

                          const summary = Object.entries(typeCounts)
                            .map(([type, count]) => `${type}: ${count}`)
                            .join('\n')

                          // Show which ones changed
                          const changed = detectionResults.filter(r => r.oldType && r.oldType !== r.detected)
                          const changedSummary = changed.length > 0
                            ? `\n\nChanged:\n${changed.map(c => `${c.title}: ${c.oldType} → ${c.detected}`).join('\n')}`
                            : ''

                          alert(`Successfully scanned and tagged ${updatedCount} document(s)!\n\nDetected types:\n${summary}${changedSummary}`)
                        } catch (err) {
                          console.error('Scan error:', err)
                          alert('Failed to scan documents')
                        } finally {
                          setLoading(false)
                        }
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm"
                    >
                      <Search className="h-4 w-4 mr-1" />
                      Scan & Tag Types
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        handleRescanAll()
                      }}
                      disabled={rescanningAll || loading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center text-sm"
                      title="AI-powered compliance framework detection for all documents"
                    >
                      {rescanningAll ? (
                        <>
                          <Loader className="h-4 w-4 mr-1 animate-spin" />
                          Scanning... {scanProgress ? `${scanProgress.processed}/${scanProgress.total} (${scanProgress.percentage}%)` : ''}
                        </>
                      ) : (
                        <>
                          <Shield className="h-4 w-4 mr-1" />
                          Re-Scan Compliance (All)
                        </>
                      )}
                    </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {rescanningAll && scanProgress && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">
                          Scanning documents for compliance frameworks...
                        </span>
                        <span className="text-sm font-medium text-blue-700">
                          {scanProgress.processed} / {scanProgress.total} ({scanProgress.percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${scanProgress.percentage}%` }}
                        ></div>
                      </div>
                      {scanProgress.lastProcessed && (
                        <div className="mt-2 text-xs text-blue-700">
                          Last processed: {scanProgress.lastProcessed.title || scanProgress.lastProcessed.documentId}
                          {scanProgress.lastProcessed.status === 'success' && scanProgress.lastProcessed.frameworksFound && (
                            <span className="ml-2 text-green-600">
                              ✓ Found {scanProgress.lastProcessed.frameworksFound} frameworks
                            </span>
                          )}
                          {scanProgress.lastProcessed.status === 'skipped' && (
                            <span className="ml-2 text-gray-600">
                              (Skipped: {scanProgress.lastProcessed.reason})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Filter and Sort Controls */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          value={docFilterCategory}
                          onChange={(e) => setDocFilterCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="all">All Categories</option>
                          {uniqueCategories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                        <select
                          value={docFilterType}
                          onChange={(e) => setDocFilterType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="all">All Types</option>
                          <option value="policy">Policy</option>
                          <option value="procedure">Procedure</option>
                          <option value="attestation">Attestation</option>
                          <option value="report">Report</option>
                          <option value="certificate">Certificate</option>
                          <option value="agreement">Agreement</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Attestation</label>
                        <select
                          value={docFilterAttestation}
                          onChange={(e) => setDocFilterAttestation(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="all">All Attestations</option>
                          {uniqueAttestations.map(attestation => (
                            <option key={attestation} value={attestation}>{attestation}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                        <select
                          value={docFilterProduct}
                          onChange={(e) => setDocFilterProduct(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="all">All Products</option>
                          <option value="unassigned">Unassigned</option>
                          {(contentData.products || []).map((product: any) => (
                            <option key={product.id} value={product.id}>
                              {product.name} {product.isDefault ? '(Default)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                        <select
                          value={docFilterVisibility}
                          onChange={(e) => setDocFilterVisibility(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="all">All Visibility</option>
                          <option value="public">Public</option>
                          <option value="gated">Gated</option>
                          <option value="admin">Admin Only</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                        <select
                          value={docSortBy}
                          onChange={(e) => setDocSortBy(e.target.value as 'name' | 'date' | 'size' | 'category' | 'type')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="date">Upload Date</option>
                          <option value="name">Name</option>
                          <option value="size">File Size</option>
                          <option value="category">Category</option>
                          <option value="type">Document Type</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                        <select
                          value={docSortOrder}
                          onChange={(e) => setDocSortOrder(e.target.value as 'asc' | 'desc')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        >
                          <option value="desc">Descending</option>
                          <option value="asc">Ascending</option>
                        </select>
                      </div>
                    </div>

                    {/* Reset Filters Button */}
                    {(docFilterCategory !== 'all' || docFilterVisibility !== 'all' || docFilterType !== 'all' || docFilterAttestation !== 'all' || docFilterProduct !== 'all' || docSortBy !== 'date' || docSortOrder !== 'desc') && (
                      <div className="mt-3">
                        <button
                          onClick={() => {
                            setDocFilterCategory('all')
                            setDocFilterType('all')
                            setDocFilterAttestation('all')
                            setDocFilterProduct('all')
                            setDocFilterVisibility('all')
                            setDocSortBy('date')
                            setDocSortOrder('desc')
                          }}
                          className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reset Filters
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Bulk Actions Bar */}
                  {filteredDocuments.length > 0 && (
                    <div className="mb-4 flex items-center justify-between bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={selectAllAdminDocs}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Select All ({filteredDocuments.length})
                        </button>
                        {selectedAdminDocs.size > 0 && (
                          <>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={deselectAllAdminDocs}
                              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                            >
                              Deselect All
                            </button>
                            <span className="text-gray-300">|</span>
                            <span className="text-sm text-gray-600">
                              {selectedAdminDocs.size} document{selectedAdminDocs.size !== 1 ? 's' : ''} selected
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {selectedAdminDocs.size > 0 && documentTab === 'active' && (
                          <button
                            onClick={() => {
                              // Convert selectedAdminDocs to selectedDocuments for the modal
                              setSelectedDocuments(new Set(selectedAdminDocs))
                              setShowBulkEditModal(true)
                            }}
                            className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Bulk Edit
                          </button>
                        )}
                        {selectedAdminDocs.size > 0 && documentTab === 'active' && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Are you sure you want to archive ${selectedAdminDocs.size} document(s)?`)) return
                              if (!currentUser) return

                              try {
                                const now = new Date().toISOString()
                                const selectedIds = Array.from(selectedAdminDocs)

                                for (const docId of selectedIds) {
                                  await fetch(`/api/documents/${docId}/archive`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' }
                                  })
                                }

                                setSelectedAdminDocs(new Set())
                                await fetchAllData()
                                triggerContentRefresh()
                                alert(`Successfully archived ${selectedIds.length} document(s)!`)
                              } catch (err) {
                                console.error('Bulk archive error:', err)
                                alert('Failed to archive documents')
                                await fetchAllData()
                              }
                            }}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </button>
                        )}
                        {selectedAdminDocs.size > 0 && documentTab === 'active' && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Are you sure you want to move ${selectedAdminDocs.size} document(s) to the recycle bin?`)) return
                              if (!currentUser) return

                              try {
                                const now = new Date().toISOString()
                                const selectedIds = Array.from(selectedAdminDocs)

                                for (const docId of selectedIds) {
                                  await fetch(`/api/documents/${docId}`, {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' }
                                  })
                                }

                                setSelectedAdminDocs(new Set())
                                await fetchAllData()
                                triggerContentRefresh()
                                alert(`Successfully moved ${selectedIds.length} document(s) to recycle bin!`)
                              } catch (err) {
                                console.error('Bulk delete error:', err)
                                alert('Failed to delete documents')
                                await fetchAllData()
                              }
                            }}
                            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </button>
                        )}
                        <button
                          onClick={handleAdminBulkDownload}
                          disabled={selectedAdminDocs.size === 0 || adminBulkDownloading}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {adminBulkDownloading ? (
                            <>
                              <Loader className="h-4 w-4 mr-2 animate-spin" />
                              Downloading ZIP...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download Selected {selectedAdminDocs.size > 0 ? `(${selectedAdminDocs.size})` : ''}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {filteredDocuments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {documents.length === 0 ? 'No documents uploaded yet.' : 'No documents match the current filters.'}
                      </div>
                    ) : (
                      filteredDocuments.map((doc) => (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start">
                        <div className="flex items-start space-x-4 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedAdminDocs.has(doc.id)}
                            onChange={() => toggleAdminDocSelection(doc.id)}
                            className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                          />
                          <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-medium text-gray-900">{doc.title}</h4>
                            {/* Public Visibility Status Badge */}
                            {doc.visibility === 'admin' ? (
                              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                🚫 Admin Only
                              </span>
                            ) : doc.hidden ? (
                              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                🔒 Link Only
                              </span>
                            ) : (doc.gated || doc.visibility === 'gated') ? (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                🔐 Requires Login
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                🌐 Public
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span className="capitalize">{doc.category}</span>
                            {doc.documentType && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium capitalize">{doc.documentType}</span>}
                            <span>{(doc.size / 1024 / 1024).toFixed(2)} MB</span>
                            {doc.version && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">v{doc.version}</span>}
                            {/* File Format Badge - Show PDF generation status */}
                            {doc.pdfStatus === 'pending' || doc.pdfStatus === 'generating' ? (
                              // PDF is currently being generated
                              <>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium animate-pulse">🔄 PDF Processing</span>
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">⏳ Generating PDF</span>
                              </>
                            ) : doc.pdfStatus === 'failed' ? (
                              // PDF generation failed
                              <>
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">❌ PDF Failed</span>
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">⚠️ Not Production Ready</span>
                              </>
                            ) : doc.pdfStatus === 'ready' || (doc.size > 1000 && !doc.pdfStatus) ? (
                              // PDF is ready (either explicitly marked or has substantial file size)
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">✓ PDF Ready</span>
                            ) : (
                              // Small file size or no clear status - likely markdown
                              <>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">📝 Markdown</span>
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">⚠️ Not Production Ready</span>
                              </>
                            )}
                            {doc.ndaRequired && <span className="text-purple-600">NDA</span>}
                            {doc.watermarked && <span className="text-green-600">Watermarked</span>}
                            {doc.redacted && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Redacted - {doc.allowedPages || doc.maxPages?.toString() || '5'}</span>}
                          </div>
                          {/* Compliance Framework Badges */}
                          {Array.isArray(doc.complianceMapping) && doc.complianceMapping.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {doc.complianceMapping.map((fw: any, idx: number) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                                  title={`${fw.framework} - ${fw.controls.length} controls detected`}
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  {fw.framework}
                                  <span className="ml-1 px-1.5 py-0.5 bg-blue-200 rounded-full">
                                    {fw.controls.length}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Fallback to old attestationTags if no complianceMapping */}
                          {(!doc.complianceMapping || doc.complianceMapping.length === 0) && doc.attestationTags && doc.attestationTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {doc.attestationTags.map((tag, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                  <Shield className="h-3 w-3 mr-1" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {doc.productIds && doc.productIds.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs text-gray-500">Products:</span>
                              {doc.productIds.map((productId: string) => {
                                const product = contentData.products?.find((p: any) => p.id === productId)
                                return product ? (
                                  <span key={productId} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                                    <Package className="h-3 w-3" />
                                    {product.name}
                                  </span>
                                ) : null
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(`/api/admin/download-document?id=${doc.id}`)
                                if (!response.ok) throw new Error('Download failed')

                                const blob = await response.blob()
                                const url = window.URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = doc.title
                                document.body.appendChild(a)
                                a.click()
                                window.URL.revokeObjectURL(url)
                                document.body.removeChild(a)
                              } catch (err) {
                                console.error('Download failed:', err)
                                alert('Failed to download document')
                              }
                            }}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                            title="Download original (no watermark)"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={async () => {
                              const publicUrl = `${window.location.origin}/documents/${doc.id}`
                              try {
                                await navigator.clipboard.writeText(publicUrl)
                                alert(`Link copied to clipboard!\n\n${publicUrl}\n\n${doc.gated ? 'Note: This document is gated - visitors will need to request access.' : doc.visibility === 'admin' ? 'Note: This document is admin-only and not publicly accessible.' : 'This document is publicly accessible.'}`)
                              } catch (err) {
                                console.error('Failed to copy:', err)
                                alert(`Copy this link:\n\n${publicUrl}`)
                              }
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                            title="Copy public link"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditDocument(doc)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit document"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              handleRescanDocument(doc.id, doc.title)
                            }}
                            disabled={rescanningDocId === doc.id}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Re-scan compliance frameworks using AI"
                          >
                            {rescanningDocId === doc.id ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Shield className="h-4 w-4" />
                            )}
                          </button>
                          {documentTab === 'active' ? (
                            <>
                              <button
                                onClick={() => handleArchiveDocument(doc.id)}
                                className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                                title="Archive document"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : documentTab === 'archived' ? (
                            <>
                              <button
                                onClick={() => handleUnarchiveDocument(doc.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Unarchive document"
                              >
                                <ArchiveRestore className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Move to recycle bin"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleRestoreDocument(doc.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                title="Restore document"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handlePermanentDelete(doc.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                title="Permanently delete"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                        </div>
                      </div>
                    )))}
                  </div>
                </div>
              </div>
            )}

            {/* Deployments Sub-Tab */}
            {trustCenterSubTab === 'deployments' && (
              <div className="space-y-6" id="deploy">
                {/* Page Description */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">Content Deployment Management</h2>
                  <p className="text-sm text-blue-800">
                    Deploy your Trust Center content changes to production. Review pending changes,
                    manage deployment history, and roll back to previous versions if needed.
                  </p>
                </div>

                <ContentVersioning onNavigateToTab={(tab) => setTrustCenterSubTab(tab)} />
              </div>
            )}

          </div>
        )}

        {/* Visitors Sub-Tab */}
        {activeTab === 'trust-center' && trustCenterSubTab === 'visitors' && (
          <div className="space-y-6">
            {/* Page Description */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">Visitor Access Management</h2>
              <p className="text-sm text-blue-800">
                Manage visitors accounts, review access requests from customers wanting to view your Trust Center,
                and configure the allowlist to pre-approve specific email domains or addresses for automatic access.
              </p>
            </div>

            {/* Access Requests Section */}
            <div>
                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search requests..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={fetchRequests}
                        disabled={loading}
                        className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>

                {/* Requests List */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-tour="requests-list">
                  <div className="p-6 border-b border-gray-200">
                    <SectionHeading id="access-requests" className="text-2xl font-bold text-gray-900">
                      Access Requests ({filteredRequests.length})
                    </SectionHeading>
                  </div>

                  <div className="divide-y divide-gray-200">
                    {loading ? (
                      <div className="p-6 text-center">
                        <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Loading requests...</p>
                      </div>
                    ) : filteredRequests.length === 0 ? (
                      <div className="p-6 text-center">
                        <p className="text-sm text-gray-500">
                          {searchTerm || statusFilter !== 'all'
                            ? 'No requests match your filters'
                            : 'No access requests found'
                          }
                        </p>
                      </div>
                    ) : (
                      filteredRequests.map((request) => (
                        <div
                          key={request.id}
                          id={`request-${request.id}`}
                          className={`p-6 transition-colors ${
                            highlightedRequestId === request.id
                              ? 'bg-blue-50 border-l-4 border-blue-500'
                              : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                {getStatusIcon(request.status)}
                                <div className="ml-3">
                                  <h3 className="text-sm font-medium text-gray-900">{request.name}</h3>
                                  <p className="text-sm text-gray-600">{request.email}</p>
                                  <p className="text-sm text-gray-600">{request.company}</p>
                                </div>
                              </div>

                              <div className="mt-4">
                                <p className="text-sm text-gray-900">
                                  <strong>Role:</strong> {request.role}
                                </p>
                                <p className="text-sm text-gray-900 mt-1">
                                  <strong>Reason:</strong> {request.reason}
                                </p>
                                {request.targets.length > 0 && (
                                  <p className="text-sm text-gray-900 mt-1">
                                    <strong>Documents:</strong> {request.targets.join(', ')}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  Requested: {new Date(request.createdAt).toLocaleString()}
                                </p>
                                {request.approvedAt && (
                                  <p className="text-xs text-gray-500">
                                    {request.status === 'approved' ? 'Approved' : 'Rejected'}: {new Date(request.approvedAt).toLocaleString()} by {request.approvedBy}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {request.status}
                              </span>

                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => {
                                    setHighlightedRequestId(request.id)
                                    setTimeout(() => {
                                      const element = document.getElementById(`request-${request.id}`)
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                      }
                                    }, 100)
                                  }}
                                  className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Scroll to this request"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleApproveRequest(request.id)}
                                  className={`p-1.5 rounded transition-colors ${
                                    request.status === 'approved'
                                      ? 'text-green-600 bg-green-50'
                                      : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                                  }`}
                                  title="Approve request"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  className={`p-1.5 rounded transition-colors ${
                                    request.status === 'rejected'
                                      ? 'text-red-600 bg-red-50'
                                      : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
                                  }`}
                                  title="Deny request"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                                {request.status === 'approved' && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await fetch('/api/admin/generate-magic-link', {
                                          method: 'POST',
                                          headers: getHeaders(),
                                          credentials: 'include',
                                          body: JSON.stringify({
                                            requestId: request.id,
                                            email: request.email
                                          })
                                        })

                                        const data = await response.json()
                                        console.log('Magic link response:', data)

                                        if (!response.ok) {
                                          throw new Error(data.error || data.details || 'Failed to generate magic link (status: ' + response.status + ')')
                                        }

                                        if (data.error) {
                                          throw new Error(data.error + (data.details ? '\n\n' + data.details : ''))
                                        }

                                        // Copy to clipboard
                                        await navigator.clipboard.writeText(data.magicLink)

                                        // Mobile-friendly alert message
                                        alert(`✓ Magic link copied!\n\nValid for: ${data.expiresIn}\n\nSend to: ${request.email}`)
                                      } catch (error) {
                                        console.error('Error generating magic link:', error)
                                        alert('Failed to generate magic link:\n\n' + error.message)
                                      }
                                    }}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors touch-manipulation text-sm"
                                    title="Generate magic link for users who can't receive emails"
                                    aria-label="Generate magic link"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4 flex-shrink-0">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                    </svg>
                                    <span className="hidden sm:inline">Magic Link</span>
                                    <span className="sm:hidden">Link</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleArchiveRequest(request.id)}
                                  className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                  title="Archive request"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteRequest(request.id)}
                                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Move to recycle bin"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
            </div>

            {/* Visitor Management Section */}
            <AllowlistManager />

            {/* NDA Acceptances */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200 px-6 py-4">
                <SectionHeading id="nda-acceptances" className="text-lg font-semibold text-gray-900">
                  NDA Acceptances
                </SectionHeading>
              </div>
              <div className="p-6">
                <NdaAcceptanceManager tenantId={effectiveTenantId} />
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Settings Sub-Tabs */}
            <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
              <div className="flex space-x-8 px-6">
                {[
                  ...(isPlatformAdminMode ? [{ key: 'platform', label: 'Platform', icon: Settings }] : []),
                  { key: 'general', label: 'General', icon: Settings },
                  { key: 'billing', label: 'Billing', icon: CreditCard },
                  { key: 'team', label: 'Team', icon: Users },
                  { key: 'integrations', label: 'Integrations', icon: Plug },
                  { key: 'security', label: 'Security', icon: Shield }
                ].map((subTab) => {
                  const IconComponent = subTab.icon
                  return (
                    <button
                      key={subTab.key}
                      data-tour-subtab={`settings-${subTab.key}`}
                      onClick={() => setSettingsSubTab(subTab.key as any)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                        settingsSubTab === subTab.key
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 mr-2" />
                      {subTab.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Platform Sub-Tab (Admin Only) */}
            {isPlatformAdminMode && settingsSubTab === 'platform' && (
              <div className="space-y-6">
                <TenantManager />
              </div>
            )}

            {/* General Sub-Tab */}
            {settingsSubTab === 'general' && (
              <div className="space-y-6">
                {/* Products */}
                <div data-tour="settings-products">
                  <ProductManager currentUser={currentUser} />
                </div>

                {/* Custom Domain */}
                <div data-tour="settings-domain">
                  <CustomDomainSettings
                    tenantId={displayTenant?.id || ''}
                    userEmail={currentUser?.email}
                    onNavigateToDeployments={() => {
                      setActiveTab('trust-center')
                      setTrustCenterSubTab('deployments')
                    }}
                  />
                </div>

                {/* NDA Settings */}
                <NdaSettingsManager
                  tenantId={effectiveTenantId}
                  userEmail={currentUser?.email || ''}
                />

                {/* Product Tour */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <SectionHeading id="product-tour" className="text-lg font-semibold text-gray-900">
                      Product Tour
                    </SectionHeading>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">
                      Take a guided tour to learn about the admin features and how to manage your trust center.
                    </p>
                    <button
                      onClick={async () => {
                        // Reset and restart the admin tour
                        await resetAdminTour()
                        // Navigate to dashboard tab
                        setActiveTab('dashboard')
                        window.location.hash = 'dashboard'
                        // Start the tour
                        setTimeout(() => {
                          setActiveTourType('admin')
                          startAdminTour()
                        }, 300)
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Start Admin Tour
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Billing Sub-Tab */}
            {settingsSubTab === 'billing' && (
              <div className="space-y-6" data-tour="settings-billing">
                <BillingSettings currentUser={currentUser} />
              </div>
            )}

            {/* Team Sub-Tab */}
            {settingsSubTab === 'team' && (
              <div className="space-y-6" data-tour="settings-team">
            {/* User Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <SectionHeading id="user-management" className="text-lg font-semibold text-gray-900">
                  User Management
                </SectionHeading>
                <p className="text-gray-600 mt-1">Manage user roles and permissions for the Knowledge Base</p>
              </div>

              {/* Add New User */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">Grant Access to New User</h3>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="reviewer">Reviewer</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={grantUserRole}
                      disabled={!newUserEmail}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Grant Access
                    </button>
                  </div>
                </div>

                {/* Role Descriptions */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <p className="font-medium text-blue-900">Admin</p>
                    <p className="text-blue-700 mt-0.5">Full access: scan, edit, approve, manage users</p>
                  </div>
                  <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
                    <p className="font-medium text-green-900">Editor</p>
                    <p className="text-green-700 mt-0.5">Can initiate scans and edit answers</p>
                  </div>
                  <div className="p-2 bg-purple-50 border border-purple-200 rounded text-xs">
                    <p className="font-medium text-purple-900">Reviewer</p>
                    <p className="text-purple-700 mt-0.5">Can only approve/reject answers</p>
                  </div>
                  <div className="p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                    <p className="font-medium text-gray-900">Viewer</p>
                    <p className="text-gray-700 mt-0.5">Read-only access to browse CAIQ</p>
                  </div>
                </div>
              </div>

              {/* Current Users */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">
                  Current Users ({userRoles.length})
                </h3>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {userRoles.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No users configured yet</p>
                      <p className="text-xs mt-1">Add users above to grant them access</p>
                    </div>
                  ) : (
                    userRoles.map((user: any) => {
                      const roleColors = {
                        admin: 'bg-blue-100 text-blue-800 border-blue-200',
                        editor: 'bg-green-100 text-green-800 border-green-200',
                        reviewer: 'bg-purple-100 text-purple-800 border-purple-200',
                        viewer: 'bg-gray-100 text-gray-800 border-gray-200'
                      }

                      return (
                        <div key={user.email} className="p-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Granted by {user.grantedBy} on {new Date(user.grantedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded border ${roleColors[user.role as keyof typeof roleColors]}`}>
                              {user.role}
                            </span>
                            <button
                              onClick={() => revokeUserRole(user.email)}
                              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded"
                            >
                              Revoke
                            </button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

              </div>
            )}

            {/* Integrations Sub-Tab */}
            {settingsSubTab === 'integrations' && (
              <div className="space-y-6" data-tour="settings-integrations">
                {/* API Keys */}
                <ApiKeysSettings />

                {/* OAuth2 Clients for GPT Integration */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <SectionHeading id="oauth-clients" className="text-lg font-semibold text-gray-900">
                          OAuth2 Clients (GPT Integration)
                        </SectionHeading>
                        <p className="text-sm text-gray-600 mt-1">
                          Connect ChatGPT, Claude Projects, and other AI tools to your Trust Center admin
                        </p>
                      </div>
                      <button
                        onClick={() => setShowCreateOAuthClient(true)}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center text-sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Client
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    {oauthClients.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Shield className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                        <p className="font-medium">No OAuth2 clients configured</p>
                        <p className="text-sm mt-1">Create a client to connect external GPTs and AI assistants</p>
                        <a
                          href="/GPT_INTEGRATION_GUIDE.md"
                          target="_blank"
                          className="text-primary-600 hover:text-primary-700 text-sm mt-2 inline-block"
                        >
                          View Integration Guide →
                        </a>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {oauthClients.map((client) => (
                          <div key={client.clientId} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-gray-900">{client.name}</h4>
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                    Active
                                  </span>
                                </div>
                                <div className="mt-2 space-y-1 text-sm text-gray-600">
                                  <div>
                                    <span className="font-medium">Client ID:</span>
                                    <code className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">{client.clientId}</code>
                                  </div>
                                  <div>
                                    <span className="font-medium">Scopes:</span>
                                    <span className="ml-2">{client.scopes.join(', ')}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium">Redirect URIs:</span>
                                    <div className="ml-2 mt-1">
                                      {client.redirectUris.map((uri: string, idx: number) => (
                                        <code key={idx} className="block text-xs bg-gray-100 px-2 py-1 rounded mt-1">
                                          {uri}
                                        </code>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-2">
                                    Created {new Date(client.createdAt).toLocaleDateString()} by {client.createdBy}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => deleteOAuthClient(client.clientId)}
                                className="ml-4 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex">
                        <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900">
                          <p className="font-medium mb-1">How to connect your GPT:</p>
                          <ol className="list-decimal list-inside space-y-1 text-blue-800">
                            <li>Create an OAuth2 client above</li>
                            <li>Copy the Client ID and Client Secret (shown once)</li>
                            <li>Configure your GPT with OAuth2 authentication</li>
                            <li>Use authorization URL: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/oauth/authorize</code></li>
                            <li>Use token URL: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/oauth/token</code></li>
                          </ol>
                          <a
                            href="/GPT_INTEGRATION_GUIDE.md"
                            target="_blank"
                            className="text-blue-700 hover:text-blue-800 font-medium mt-2 inline-block"
                          >
                            View Full Integration Guide →
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Create OAuth Client Modal */}
                {showCreateOAuthClient && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="border-b border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-semibold text-gray-900">Create OAuth2 Client</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Configure a new OAuth2 client for external GPT integration
                        </p>
                      </div>

                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client Name *
                          </label>
                          <input
                            type="text"
                            value={newOAuthClient.name}
                            onChange={(e) => setNewOAuthClient({ ...newOAuthClient, name: e.target.value })}
                            placeholder="e.g., ChatGPT Integration, Claude Project"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Redirect URIs *
                          </label>
                          {newOAuthClient.redirect_uris.map((uri, index) => (
                            <div key={index} className="flex gap-2 mb-2">
                              <input
                                type="url"
                                value={uri}
                                onChange={(e) => {
                                  const newUris = [...newOAuthClient.redirect_uris]
                                  newUris[index] = e.target.value
                                  setNewOAuthClient({ ...newOAuthClient, redirect_uris: newUris })
                                }}
                                placeholder="https://chat.openai.com/aip/g-{id}/oauth/callback"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                              {newOAuthClient.redirect_uris.length > 1 && (
                                <button
                                  onClick={() => {
                                    const newUris = newOAuthClient.redirect_uris.filter((_, i) => i !== index)
                                    setNewOAuthClient({ ...newOAuthClient, redirect_uris: newUris })
                                  }}
                                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              setNewOAuthClient({
                                ...newOAuthClient,
                                redirect_uris: [...newOAuthClient.redirect_uris, '']
                              })
                            }}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            + Add another redirect URI
                          </button>
                          <p className="text-xs text-gray-500 mt-1">
                            For ChatGPT: https://chat.openai.com/aip/g-YOUR_GPT_ID/oauth/callback
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Scopes
                          </label>
                          <div className="space-y-2">
                            {['read', 'write', 'admin'].map((scope) => (
                              <label key={scope} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={newOAuthClient.scopes.includes(scope)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewOAuthClient({
                                        ...newOAuthClient,
                                        scopes: [...newOAuthClient.scopes, scope]
                                      })
                                    } else {
                                      setNewOAuthClient({
                                        ...newOAuthClient,
                                        scopes: newOAuthClient.scopes.filter(s => s !== scope)
                                      })
                                    }
                                  }}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  <span className="font-medium">{scope}</span> -
                                  {scope === 'read' && ' Read-only access'}
                                  {scope === 'write' && ' Create and update content'}
                                  {scope === 'admin' && ' Full administrative access (including deployments)'}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                        <button
                          onClick={() => {
                            setShowCreateOAuthClient(false)
                            setNewOAuthClient({ name: '', redirect_uris: [''], scopes: ['read', 'write'] })
                          }}
                          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={createOAuthClient}
                          disabled={!newOAuthClient.name || newOAuthClient.redirect_uris.some(uri => !uri)}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Create Client
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Credentials Modal */}
                {createdClientCredentials && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                      <div className="border-b border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-semibold text-gray-900">OAuth2 Client Created!</h3>
                        <p className="text-sm text-red-600 mt-1 font-medium">
                          ⚠️ Save these credentials now - they won't be shown again
                        </p>
                      </div>

                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client Name
                          </label>
                          <div className="bg-gray-50 px-3 py-2 rounded-lg text-sm">
                            {createdClientCredentials.name}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client ID
                          </label>
                          <div className="flex gap-2">
                            <code className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-sm font-mono break-all">
                              {createdClientCredentials.clientId}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(createdClientCredentials.clientId)
                                alert('Client ID copied!')
                              }}
                              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client Secret
                          </label>
                          <div className="flex gap-2">
                            <code className="flex-1 bg-red-50 px-3 py-2 rounded-lg text-sm font-mono break-all border border-red-200">
                              {createdClientCredentials.clientSecret}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(createdClientCredentials.clientSecret)
                                alert('Client Secret copied!')
                              }}
                              className="px-3 py-2 bg-red-100 hover:bg-red-200 rounded-lg"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-red-600 mt-1">
                            Store this securely - it will only be shown once!
                          </p>
                        </div>

                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-900 font-medium mb-2">Next Steps:</p>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                            <li>Save both Client ID and Client Secret</li>
                            <li>Configure your GPT/AI tool with OAuth2</li>
                            <li>Authorization URL: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/oauth/authorize</code></li>
                            <li>Token URL: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/oauth/token</code></li>
                          </ol>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                        <button
                          onClick={() => setCreatedClientCredentials(null)}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          I've Saved the Credentials
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* CRM Integration */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <SectionHeading id="crm-integration" className="text-lg font-semibold text-gray-900">
                      CRM Integration
                    </SectionHeading>
                    <p className="text-sm text-gray-600 mt-1">
                      Connect your CRM to track revenue attribution and lead conversions
                    </p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select CRM Platform
                      </label>
                      <select
                        value={selectedCrm}
                        onChange={(e) => setSelectedCrm(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Select CRM --</option>
                        <option value="salesforce">Salesforce</option>
                        <option value="hubspot">HubSpot</option>
                        <option value="pipedrive">Pipedrive</option>
                        <option value="close">Close CRM</option>
                      </select>
                    </div>

                    {selectedCrm && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            API Key / Access Token
                          </label>
                          <input
                            type="password"
                            value={crmApiKey}
                            onChange={(e) => setCrmApiKey(e.target.value)}
                            placeholder={`Enter your ${selectedCrm} API key`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Your API key is encrypted and stored securely
                          </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">
                            How to get your {selectedCrm} API key:
                          </h4>
                          {selectedCrm === 'salesforce' && (
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Log in to Salesforce</li>
                              <li>Go to Setup → Apps → App Manager</li>
                              <li>Create a new Connected App</li>
                              <li>Enable OAuth settings and generate credentials</li>
                            </ol>
                          )}
                          {selectedCrm === 'hubspot' && (
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Log in to HubSpot</li>
                              <li>Go to Settings → Integrations → Private Apps</li>
                              <li>Create a new private app</li>
                              <li>Copy the access token</li>
                            </ol>
                          )}
                          {selectedCrm === 'pipedrive' && (
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Log in to Pipedrive</li>
                              <li>Go to Settings → Personal preferences → API</li>
                              <li>Generate a new API token</li>
                            </ol>
                          )}
                          {selectedCrm === 'close' && (
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Log in to Close</li>
                              <li>Go to Settings → API Keys</li>
                              <li>Create a new API key</li>
                            </ol>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-4">
                          <button
                            onClick={async () => {
                              if (!crmApiKey) {
                                alert('Please enter an API key')
                                return
                              }
                              try {
                                // TODO: Implement actual CRM connection
                                alert(`CRM integration with ${selectedCrm} will be available soon!`)
                              } catch (error) {
                                console.error('Error connecting CRM:', error)
                                alert('Failed to connect CRM. Please try again.')
                              }
                            }}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition"
                          >
                            Connect {selectedCrm}
                          </button>

                          {selectedCrm && (
                            <button
                              onClick={() => {
                                setSelectedCrm('')
                                setCrmApiKey('')
                              }}
                              className="text-sm text-gray-600 hover:text-gray-800"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Platform Notifications - Only visible to platform admins */}
                {isPlatformAdminMode && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                    <NotificationComposer
                      tenantList={tenantList}
                      currentUserEmail={currentUser?.email || ''}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Security Sub-Tab */}
            {settingsSubTab === 'security' && (
              <div className="space-y-6" data-tour="settings-security">
                {/* Account Security / Passkeys */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <SectionHeading id="account-security" className="text-lg font-semibold text-gray-900">
                      Account Security
                    </SectionHeading>
                    <p className="text-sm text-gray-600 mt-1">Manage your passkeys and authentication methods</p>
                  </div>
                  <div className="p-6">
                    <PasskeySettings />
                  </div>
                </div>

                {/* Policy Review & Audit Compliance */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <SectionHeading id="policy-review" className="text-lg font-semibold text-gray-900">
                      Policy Review Management
                    </SectionHeading>
                    <p className="text-sm text-gray-600 mt-1">Configure automatic policy recertification for audit compliance (SOC 2, ISO 27001)</p>
                  </div>
                  <div className="p-6">
                    <PolicyReviewSettings tenantId={effectiveTenantId} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Knowledge Base Tab */}
        {activeTab === 'knowledge-base' && (
          <KnowledgeBasePage
            onNavigateToSettings={() => setActiveTab('settings')}
            initialActiveTab={knowledgeBaseTab}
            initialCaiqSubTab={caiqSubTab}
          />
        )}

        {/* Changelog Sub-Tab */}
        {activeTab === 'trust-center' && trustCenterSubTab === 'changelog' && (
          <ChangelogManager />
        )}

      </div>

      {/* Review Generation Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Generate Subprocessor Review</h3>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Review Type
                  </label>
                  <select
                    value={reviewForm.reviewType}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="quarterly">Quarterly Review</option>
                    <option value="annual">Annual Review</option>
                    <option value="audit">Audit Report</option>
                    <option value="ad-hoc">Ad-Hoc Review</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={reviewForm.notes}
                    onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add any additional notes or context for this review..."
                  />
                </div>

                {/* Integration Options */}
                {(contentData.settings?.jiraEnabled || contentData.settings?.linearEnabled) && (
                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post Review To:
                    </label>
                    <div className="space-y-2">
                      {contentData.settings?.jiraEnabled && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={reviewForm.postToJira}
                            onChange={(e) => setReviewForm({ ...reviewForm, postToJira: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Jira ({contentData.settings.jiraProjectKey})
                          </span>
                        </label>
                      )}
                      {contentData.settings?.linearEnabled && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={reviewForm.postToLinear}
                            onChange={(e) => setReviewForm({ ...reviewForm, postToLinear: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Linear (Team ID: {contentData.settings.linearTeamId})
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Review will include:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Complete snapshot of all {contentData.subprocessors?.length || 0} subprocessors</li>
                    <li>• Attestation summary and breakdown</li>
                    <li>• Regional and type distribution analysis</li>
                    <li>• Upcoming review dates (next 90 days)</li>
                    <li>• Downloadable report for audit purposes</li>
                  </ul>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setLoading(true)
                      try {
                        // Generate review
                        const response = await fetch('/api/subprocessor-reviews', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            reviewType: reviewForm.reviewType,
                            notes: reviewForm.notes,
                            reviewedBy: currentUser?.email || 'admin@system'
                          })
                        })

                        if (response.ok) {
                          const review = await response.json()
                          let successMessage = `Review generated successfully!\n\nTotal Subprocessors: ${review.totalSubprocessors}\nActive: ${review.activeSubprocessors}\nTotal Attestations: ${review.summary.totalAttestations}`

                          // Post to Jira if selected
                          if (reviewForm.postToJira) {
                            try {
                              const jiraResponse = await fetch('/api/post-review-to-tracker', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  reviewId: review.id,
                                  platform: 'jira'
                                })
                              })

                              if (jiraResponse.ok) {
                                const jiraData = await jiraResponse.json()
                                successMessage += `\n\nPosted to Jira: ${jiraData.issueUrl}`
                              } else {
                                successMessage += '\n\nFailed to post to Jira'
                              }
                            } catch (err) {
                              console.error('Jira post error:', err)
                              successMessage += '\n\nFailed to post to Jira'
                            }
                          }

                          // Post to Linear if selected
                          if (reviewForm.postToLinear) {
                            try {
                              const linearResponse = await fetch('/api/post-review-to-tracker', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  reviewId: review.id,
                                  platform: 'linear'
                                })
                              })

                              if (linearResponse.ok) {
                                const linearData = await linearResponse.json()
                                successMessage += `\n\nPosted to Linear: ${linearData.issueUrl}`
                              } else {
                                successMessage += '\n\nFailed to post to Linear'
                              }
                            } catch (err) {
                              console.error('Linear post error:', err)
                              successMessage += '\n\nFailed to post to Linear'
                            }
                          }

                          alert(successMessage)
                          setShowReviewModal(false)
                          setReviewForm({ reviewType: 'quarterly', notes: '', postToJira: false, postToLinear: false })
                          await fetchAllData()
      triggerContentRefresh()
                        } else {
                          alert('Failed to generate review')
                        }
                      } catch (err) {
                        console.error('Error generating review:', err)
                        alert('Failed to generate review')
                      } finally {
                        setLoading(false)
                      }
                    }}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate Review'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Details Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedReview.reviewType.charAt(0).toUpperCase() + selectedReview.reviewType.slice(1)} Review
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Generated on {new Date(selectedReview.reviewDate).toLocaleDateString()} by {selectedReview.reviewedBy}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900">{selectedReview.totalSubprocessors}</div>
                  <div className="text-sm text-blue-700">Total Subprocessors</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-900">{selectedReview.activeSubprocessors}</div>
                  <div className="text-sm text-green-700">Active</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-purple-900">{selectedReview.summary.totalAttestations}</div>
                  <div className="text-sm text-purple-700">Attestations</div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Attestations by Type</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(selectedReview.summary.attestationsByType).map(([name, count]) => (
                      <div key={name} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{name}</span>
                        <span className="text-sm font-medium text-gray-900">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedReview.summary.upcomingReviews.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Upcoming Reviews (Next 90 Days)</h4>
                    <div className="space-y-2">
                      {selectedReview.summary.upcomingReviews.map((review: any, idx: number) => (
                        <div key={idx} className="flex justify-between items-center py-2 px-3 bg-yellow-50 rounded">
                          <span className="text-sm text-gray-700">{review.subprocessorName}</span>
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(review.nextReview).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedReview.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedReview.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    const dataStr = JSON.stringify(selectedReview, null, 2)
                    const dataBlob = new Blob([dataStr], { type: 'application/json' })
                    const url = URL.createObjectURL(dataBlob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `subprocessor-review-${selectedReview.id}.json`
                    link.click()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download JSON
                </button>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List Section - Show under subprocessors */}
      {trustCenterSubTab === 'subprocessors' && reviews.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Previous Reviews</h3>
          <div className="space-y-3">
            {reviews.map((review) => (
              <div key={review.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      review.reviewType === 'annual' ? 'bg-purple-100 text-purple-800' :
                      review.reviewType === 'quarterly' ? 'bg-blue-100 text-blue-800' :
                      review.reviewType === 'audit' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {review.reviewType.charAt(0).toUpperCase() + review.reviewType.slice(1)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {new Date(review.reviewDate).toLocaleDateString()}
                    </span>
                    <span className="text-sm text-gray-500">by {review.reviewedBy}</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    {review.totalSubprocessors} subprocessors · {review.summary.totalAttestations} attestations
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedReview(review)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={async () => {
                      if (!confirm('Delete this review? This action cannot be undone.')) return
                      try {
                        await fetch(`/api/subprocessor-reviews?id=${review.id}`, { method: 'DELETE' })
                        await fetchAllData()
      triggerContentRefresh()
                      } catch (err) {
                        console.error('Error deleting review:', err)
                      }
                    }}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
              <button
                onClick={() => setShowNotificationsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {loadingNotifications ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No new notifications</p>
                  <p className="text-sm text-gray-500 mt-2">You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.filter(n => !n.dismissed).map(notification => {
                    const typeIcons: any = {
                      info: <Info className="h-5 w-5 text-blue-600" />,
                      warning: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
                      success: <CheckCircle2 className="h-5 w-5 text-green-600" />,
                      update: <Bell className="h-5 w-5 text-purple-600" />,
                      maintenance: <Wrench className="h-5 w-5 text-orange-600" />
                    }

                    const priorityColors: any = {
                      high: 'bg-red-50 border-red-200',
                      medium: 'bg-yellow-50 border-yellow-200',
                      low: 'bg-blue-50 border-blue-200'
                    }

                    return (
                      <div
                        key={notification.id}
                        className={`border rounded-lg p-4 ${priorityColors[notification.priority]} ${!notification.read ? 'border-2' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          {typeIcons[notification.type]}
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-semibold text-gray-900">{notification.title}</h4>
                              {!notification.read && (
                                <span className="bg-blue-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{notification.message}</p>
                            <div className="flex items-center gap-2">
                              {notification.actionLabel && notification.actionUrl && (
                                <a
                                  href={notification.actionUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={async () => {
                                    await fetch(`/api/notifications?tenantId=${effectiveTenantId}&notificationId=${notification.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ action: 'click' })
                                    })
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                                >
                                  {notification.actionLabel} →
                                </a>
                              )}
                              {!notification.read && (
                                <button
                                  onClick={async () => {
                                    await fetch(`/api/notifications?tenantId=${effectiveTenantId}&notificationId=${notification.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ action: 'read' })
                                    })
                                    setNotifications(prev => prev.map(n =>
                                      n.id === notification.id ? { ...n, read: true } : n
                                    ))
                                  }}
                                  className="text-sm text-gray-600 hover:text-gray-900"
                                >
                                  Mark as read
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  await fetch(`/api/notifications?tenantId=${effectiveTenantId}&notificationId=${notification.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ action: 'dismiss' })
                                  })
                                  setNotifications(prev => prev.map(n =>
                                    n.id === notification.id ? { ...n, dismissed: true } : n
                                  ))
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900 ml-auto"
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Content Generator Modal */}
      <AIContentGenerator
        isOpen={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        contentType={aiGeneratorType}
        onApprove={handleAIContentApprove}
      />

      {/* AI Agent Modal */}
      <AIAgentModal
        isOpen={showAIAgentModal}
        onClose={async () => {
          setShowAIAgentModal(false)
          // Wait for Netlify Blobs eventual consistency (2 seconds)
          // before refreshing data to ensure updated metadata is available
          await new Promise(resolve => setTimeout(resolve, 2000))
          await fetchAllData()
        }}
        initialContext={{
          page: activeTab
        }}
      />
    </div>
  )
}

export default AdminFullFunctional
